import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  GitMerge, Zap, Plus, Trash2, Edit3, ToggleLeft, ToggleRight,
  ChevronDown, RefreshCw, AlertCircle, CheckCircle2, Loader2, X,
  Save, Play, Settings, ArrowRight, Circle, ArrowLeft,
  GitBranch, Bell, Link2, Copy, Check, Variable,
  Database, DatabaseZap, Layers, Braces, ListTree,
  Hash, Type, List as ListIcon, FolderOpen,
  ChevronUp, Info, Eye, EyeOff, Sparkles, LayoutList,
  Mail, Phone, Tag, ToggleLeft as ToggleIcon,
  Clock, ChevronLeft,
  KeyRound, Lock, Search,
  Workflow as WorkflowIcon, FileOutput, Download, Upload, Filter,
} from 'lucide-react';
import RunPythonNodeEditor from '../components/workflow/RunPythonNodeEditor';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
  MarkerType,
  type Node,
  type Edge,
  type Connection,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import AppLayout from '../layout/AppLayout';
import {
  workflowService,
  WorkflowState,
  StateConfigResponse,
  WorkflowTransition,
  DAGDefinition,
  DAGNode,
  DAGEdge,
  DAGExecution,
  EventTypeInfo,
  EntityFieldInfo,
  SecretRef,
  FilterFieldDef,
  DAGCheckWarning,
  DAGVersion,
  DAGVersionSummary,
  DryRunOrder,
  DryRunResult,
  StepLibraryItem,
} from '../services/WorkflowService';
import { flattenSample } from '../utils/sampleFields';
import {
  TriggerFilterRow,
  FILTER_OP_LABELS,
  LIST_FILTER_OPS,
  VALUE_FREE_FILTER_OPS,
  filtersToRows,
  rowsToFilters,
  formatTriggerFilter,
} from '../utils/triggerFilters';
import {
  buildStepsExport,
  parseStepsFile,
  remapImportedSteps,
  downloadJson,
  slugify,
  StepsExport,
} from '../utils/workflowSteps';
import { integrationService } from '../services/IntegrationService';
import { adminService } from '../services/AdminService';


// In-page fallback for copy/paste of steps when the system clipboard can't be
// read (permission denied / insecure context). Shared by every canvas on the page.
let stepsClipboard: string | null = null;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ContextVar = EntityFieldInfo;

// ---------------------------------------------------------------------------
// Explicit named actions — shown to users instead of generic node types
// ---------------------------------------------------------------------------

interface ExplicitAction {
  id: string;           // unique action identifier shown in the UI
  nodeType: string;     // underlying DAG node type
  label: string;
  description: string;
  icon: React.ElementType;
  color: string;
  // default config template
  defaultConfig: (entityType: string, triggerFields: ContextVar[]) => Record<string, any>;
  // Which fields does the user need to fill in?
  configFields: ConfigFieldDef[];
}

interface ConfigFieldDef {
  key: string;
  label: string;
  type: 'text' | 'select' | 'context_var' | 'textarea' | 'status_select' | 'entity_field_select';
  description?: string;
  required?: boolean;
  options?: Array<{ value: string; label: string }>;
  // If true, show a variable picker dropdown instead of a text input
  allowContextVar?: boolean;
  placeholder?: string;
}

const EXPLICIT_ACTIONS: ExplicitAction[] = [
  // ── Order actions ──────────────────────────────────────────────────────
  {
    id: 'update_order_status',
    nodeType: 'entity_action',
    label: 'Update Order Status',
    description: 'Change the status of the order to a new value.',
    icon: Tag,
    color: 'bg-teal-50 border-teal-300 text-teal-800',
    defaultConfig: (_, triggerFields) => ({
      operation: 'update',
      entity_type: 'order',
      entity_id_source: triggerFields.find(f => f.path === 'event_payload.order_id')?.path ?? 'entity.id',
      fields: [{ key: 'status', value: '' }],
      _action_id: 'update_order_status',
    }),
    configFields: [
      { key: '_status_value', label: 'New Status', type: 'status_select', required: true,
        description: 'The status to set the order to.' },
    ],
  },
  {
    id: 'update_order_field',
    nodeType: 'entity_action',
    label: 'Update Order Field',
    description: 'Set one or more fields on the order record.',
    icon: Database,
    color: 'bg-teal-50 border-teal-300 text-teal-800',
    defaultConfig: (_, triggerFields) => ({
      operation: 'update',
      entity_type: 'order',
      entity_id_source: triggerFields.find(f => f.path === 'event_payload.order_id')?.path ?? 'entity.id',
      fields: [],
      _action_id: 'update_order_field',
    }),
    configFields: [],  // handled by field mapping UI
  },
  // ── Notifications ──────────────────────────────────────────────────────
  {
    id: 'send_email',
    nodeType: 'send_notification',
    label: 'Send Email',
    description: 'Send an email notification to the customer or any address.',
    icon: Mail,
    color: 'bg-purple-50 border-purple-300 text-purple-800',
    defaultConfig: () => ({
      channel: 'email',
      recipient: 'entity.buyer',
      message: '',
      _action_id: 'send_email',
    }),
    configFields: [
      { key: 'recipient', label: 'Recipient (email)', type: 'context_var', required: true,
        allowContextVar: true, placeholder: 'e.g. entity.buyer or hello@example.com',
        description: 'Email address or a variable that resolves to one.' },
      { key: 'message', label: 'Message body', type: 'textarea', required: true,
        placeholder: 'Your order status has been updated to…',
        description: 'You can embed variables using {variable.path} syntax.' },
    ],
  },
  {
    id: 'send_whatsapp',
    nodeType: 'send_notification',
    label: 'Send WhatsApp',
    description: 'Send a WhatsApp message to the customer.',
    icon: Phone,
    color: 'bg-purple-50 border-purple-300 text-purple-800',
    defaultConfig: () => ({
      channel: 'whatsapp',
      recipient: '',
      message: '',
      _action_id: 'send_whatsapp',
    }),
    configFields: [
      { key: 'recipient', label: 'Phone number', type: 'context_var', required: true,
        allowContextVar: true, placeholder: 'e.g. entity.phone or +12125550100',
        description: 'Phone number or a variable that resolves to one.' },
      { key: 'message', label: 'Message body', type: 'textarea', required: true,
        placeholder: 'Your order is ready…' },
    ],
  },
  // ── Integrations ───────────────────────────────────────────────────────
  {
    id: 'run_integration',
    nodeType: 'run_integration',
    label: 'Call an Integration',
    description: 'Call an external system (inventory, CRM, shipping, DMS, etc.).',
    icon: Link2,
    color: 'bg-blue-50 border-blue-300 text-blue-800',
    defaultConfig: () => ({
      integration_type: 'inventory',
      source_id: '',
      action_id: '',
      input_mapping: {},
      _action_id: 'run_integration',
    }),
    configFields: [],  // custom UI
  },
  {
    id: 'check_part_inventory',
    nodeType: 'run_integration',
    label: 'Check Part in Inventory',
    description: 'For each part in the loop, call inventory to check availability, price or stock count.',
    icon: Link2,
    color: 'bg-blue-50 border-blue-300 text-blue-800',
    defaultConfig: () => ({
      integration_type: 'inventory',
      source_id: '',
      action_id: '',
      input_mapping: { part_number: 'foreach_item.part_number', vin: 'entity.vin' },
      _action_id: 'check_part_inventory',
    }),
    configFields: [],  // custom UI — same integration picker
  },
  {
    id: 'lookup_customer_crm',
    nodeType: 'run_integration',
    label: 'Look Up Customer in CRM',
    description: 'Query the CRM for the customer linked to this order and bring back their details.',
    icon: Link2,
    color: 'bg-blue-50 border-blue-300 text-blue-800',
    defaultConfig: (_, triggerFields) => ({
      integration_type: 'crm',
      source_id: '',
      action_id: '',
      input_mapping: {
        phone: triggerFields.find(f => f.path === 'entity.buyer')?.path ?? 'entity.buyer',
        email: 'entity.buyer',
      },
      _action_id: 'lookup_customer_crm',
    }),
    configFields: [],  // custom UI
  },
  {
    id: 'lookup_part_pricing',
    nodeType: 'run_integration',
    label: 'Look Up Part Pricing',
    description: 'Query a pricing integration (CSV / API / webcrawl) to get the price of a part by OE number.',
    icon: Link2,
    color: 'bg-green-50 border-green-300 text-green-800',
    defaultConfig: (_, triggerFields) => ({
      integration_type: 'pricing',
      source_id: '',
      action_id: '',
      input_mapping: {
        oe_number: triggerFields.find(f => f.path === 'foreach_item.part_number')?.path ?? 'foreach_item.part_number',
      },
      _action_id: 'lookup_part_pricing',
    }),
    configFields: [],
  },
  // ── Data sync ──────────────────────────────────────────────────────────
  {
    id: 'refresh_integration_source',
    nodeType: 'refresh_integration_source',
    label: 'Refresh Integration Dataset',
    description: 'Re-load a CSV integration source (price / inventory list) from a fresh CSV or Excel file an upstream step wrote to GCS. Re-provisions the BigQuery table behind the source.',
    icon: DatabaseZap,
    color: 'bg-cyan-50 border-cyan-300 text-cyan-800',
    defaultConfig: () => ({
      integration_type: 'pricing',
      source_id: '',
      gcs_path: '',
      set_active: true,
      _action_id: 'refresh_integration_source',
    }),
    configFields: [],  // custom UI
  },
  {
    id: 'enrich_order',
    nodeType: 'enrich_order',
    label: 'Ground Order Enrichment',
    description: 'Match the order\'s vehicle against your enrichment sources (uploaded client documents) and merge the relevant data — alternative model names, maintenance data, etc. — into the order.',
    icon: Sparkles,
    color: 'bg-teal-50 border-teal-300 text-teal-800',
    defaultConfig: () => ({
      _action_id: 'enrich_order',
    }),
    configFields: [],  // custom UI — available-documents count rendered by the enrich_order block
  },
  // ── Foreach ────────────────────────────────────────────────────────────
  {
    id: 'for_each_part',
    nodeType: 'foreach_array',
    label: 'For Each Part in Order',
    description: 'Repeat actions for every part/line-item in the order.',
    icon: Layers,
    color: 'bg-orange-50 border-orange-300 text-orange-800',
    defaultConfig: () => ({
      array_source: 'entity.parts_in_order',
      item_var: 'part',
      item_extract_mode: 'whole',
      _action_id: 'for_each_part',
    }),
    configFields: [
      { key: 'item_var', label: 'Loop variable name', type: 'text',
        placeholder: 'part', description: 'Use this name to reference the current part inside the loop.' },
    ],
  },
  {
    id: 'for_each_array',
    nodeType: 'foreach_array',
    label: 'For Each Item in Array',
    description: 'Repeat actions for every item in any array variable.',
    icon: Layers,
    color: 'bg-orange-50 border-orange-300 text-orange-800',
    defaultConfig: () => ({
      array_source: '',
      item_var: 'item',
      item_extract_mode: 'whole',
      _action_id: 'for_each_array',
    }),
    configFields: [
      { key: 'array_source', label: 'Array to iterate', type: 'context_var', required: true,
        allowContextVar: true, placeholder: 'e.g. entity.parts_in_order',
        description: 'The array variable to loop over.' },
      { key: 'item_var', label: 'Loop variable name', type: 'text',
        placeholder: 'item', description: 'Each element will be accessible as this variable inside the loop.' },
    ],
  },
  // ── Per-part / inside-loop actions ─────────────────────────────────────
  {
    id: 'update_part_field',
    nodeType: 'entity_action',
    label: 'Update Part Field',
    description: 'Set one or more fields on the current part in the loop (e.g. availability, price).',
    icon: Database,
    color: 'bg-teal-50 border-teal-300 text-teal-800',
    defaultConfig: () => ({
      operation: 'update',
      entity_type: 'order_part',
      entity_id_source: 'foreach_item.part_id',
      fields: [],
      _action_id: 'update_part_field',
    }),
    configFields: [],  // handled by field mapping UI
  },
  {
    id: 'update_part_price_from_integration',
    nodeType: 'entity_action',
    label: 'Update Part Price from Integration',
    description: 'Map pricing integration output (price, currency, etc.) onto the current part in the loop.',
    icon: Database,
    color: 'bg-teal-50 border-teal-300 text-teal-800',
    defaultConfig: (_, triggerFields) => {
      // Try to find the nearest pricing integration node result in context
      const priceVar = triggerFields.find(f => f.path.includes('output.price'))?.path ?? '';
      const currencyVar = triggerFields.find(f => f.path.includes('output.currency'))?.path ?? '';
      const descVar = triggerFields.find(f => f.path.includes('output.description'))?.path ?? '';
      return {
        operation: 'update',
        entity_type: 'order_part',
        entity_id_source: 'foreach_item.part_id',
        fields: [
          { key: 'unit_price',  value: priceVar },
          { key: 'currency',    value: currencyVar },
          { key: 'description', value: descVar },
        ],
        _action_id: 'update_part_price_from_integration',
      };
    },
    configFields: [],  // handled by field mapping UI
  },
  // ── Supplier pricing — composable building blocks ──────────────────────
  //
  //  Intended flow (users can chain these with conditions):
  //
  //   [lookup_supplier] ──► (edge condition: output.found = true) ──► [write_result_to_part]
  //                       └─ (edge condition: output.found = false) ──► [lookup_supplier #2] ──► …
  //                                 │ Yes                   │ No
  //         ┌────────────────────────┘         ┌────────────┘
  //   [write_result_to_part]         [lookup_supplier #2] ──► …
  //
  {
    id: 'lookup_supplier',
    nodeType: 'run_integration',
    label: 'Supplier',
    description: 'Query a single supplier integration by OE number (and optionally VIN). Returns price, currency, availability and supplier name. Draw outgoing edges with edge conditions on output.found to branch YES/NO.',
    icon: Link2,
    color: 'bg-green-50 border-green-300 text-green-800',
    defaultConfig: () => ({
      integration_type: 'supplier',
      source_id: '',
      action_id: '',
      input_mapping: {
        oe_number: 'triggered_item.part_number',
        vin:       'entity.vin',
      },
      on_multiple_results: 'save_all',
      _action_id: 'lookup_supplier',
    }),
    configFields: [],  // uses the standard run_integration UI + on_multiple_results below
  },
  {
    id: 'write_result_to_part',
    nodeType: 'entity_action',
    label: 'Write Result to Part',
    description: 'Map fields from a previous lookup result onto the part that triggered the workflow. Add one row per field you want to copy (e.g. unit_price ← a result variable).',
    icon: Database,
    color: 'bg-teal-50 border-teal-300 text-teal-800',
    defaultConfig: (_entityType, _triggerFields) => ({
        operation: 'update',
        entity_type: 'order_part',
        entity_id_source: 'triggered_item_entity_id',
        fields: [],
        _action_id: 'write_result_to_part',
      }),
    configFields: [],  // handled by field mapping UI
  },
  // ── Search ─────────────────────────────────────────────────────────────
  {
    id: 'search_parts',
    nodeType: 'search_parts',
    label: 'Search Parts',
    description: 'Search the parts catalog by VIN and/or free-text query. Outputs a list of matching parts you can use in downstream nodes.',
    icon: Search,
    color: 'bg-sky-50 border-sky-300 text-sky-800',
    defaultConfig: (_, triggerFields) => ({
      vin:             triggerFields.find(f => f.path === 'entity.vin')?.path ?? 'entity.vin',
      query:           '',
      language:        'en',
      sourcer:         '',
      organization_id: '',
      create_order:    false,
      _action_id: 'search_parts',
    }),
    configFields: [],  // custom UI — rendered by step.type === 'search_parts' block
  },
  {
    id: 'find_orders_by_status',
    nodeType: 'query_orders',
    label: 'Find Orders by Status',
    description: 'Select all orders currently in a chosen status. Outputs a list you can feed into a For Each loop + Search Parts to run a search for every matching order.',
    icon: Search,
    color: 'bg-sky-50 border-sky-300 text-sky-800',
    defaultConfig: () => ({
      status: '',
      source: '',
      limit: 200,
      _action_id: 'find_orders_by_status',
    }),
    configFields: [],  // custom UI — rendered by the query_orders block
  },
  {
    id: 'check_condition',
    nodeType: 'condition',
    label: 'Check Condition',
    description: 'Evaluate a condition and branch the workflow. Connect outgoing edges with YES/NO conditions on output.result.',
    icon: GitBranch,
    color: 'bg-amber-50 border-amber-300 text-amber-800',
    defaultConfig: () => ({
      field: '',
      operator: 'eq',
      value: '',
      true_label: 'Yes',
      false_label: 'No',
      _action_id: 'check_condition',
    }),
    configFields: [],  // custom UI below
  },
  {
    id: 'switch',
    nodeType: 'switch',
    label: 'Switch',
    description: 'Pick one path by a field\'s value: one arrow per case, plus an "otherwise" arrow. Use it instead of several yes/no checks on the same field (e.g. country or brand).',
    icon: ListTree,
    color: 'bg-amber-50 border-amber-300 text-amber-800',
    defaultConfig: () => ({
      field: '',
      cases: [''],
      case_sensitive: false,
      _action_id: 'switch',
    }),
    configFields: [],  // custom UI below
  },
  // ── Subflows ───────────────────────────────────────────────────────────
  {
    id: 'call_workflow',
    nodeType: 'call_workflow',
    label: 'Call Organization Workflow',
    description: 'Run another workflow as a subflow and continue with its output. In a shared workflow, the name resolves to the triggered organization\'s own workflow — each org can implement it differently.',
    icon: WorkflowIcon,
    color: 'bg-rose-50 border-rose-300 text-rose-800',
    defaultConfig: () => ({
      workflow_name: '',
      input_fields: [],
      on_missing: 'fail',
      _action_id: 'call_workflow',
    }),
    configFields: [],  // custom UI — rendered by the call_workflow block
  },
  {
    id: 'set_output',
    nodeType: 'set_output',
    label: 'Set Workflow Output',
    description: 'Declare this workflow\'s return value when it is called as a subflow by another workflow. The caller sees these fields as output.result.<key>.',
    icon: FileOutput,
    color: 'bg-rose-50 border-rose-300 text-rose-800',
    defaultConfig: () => ({
      fields: [],
      _action_id: 'set_output',
    }),
    configFields: [],  // custom UI — rendered by the set_output block
  },
  // ── Python (lambda) ────────────────────────────────────────────────────
  {
    id: 'run_python',
    nodeType: 'run_python',
    label: 'Run Python Code',
    description: 'Execute a custom Python snippet. Read from any previous node output or entity field, and produce a JSON output for downstream nodes.',
    icon: Braces,
    color: 'bg-violet-50 border-violet-300 text-violet-800',
    defaultConfig: () => ({
      code: '# inputs  — dict of your mapped input variables\n# context — workflow context (entity, event_payload, node_results, …)\n# extras  — extras["cache"] (Redis), extras["logger"]\n\nasync def execute(inputs, context, extras):\n    cache  = extras["cache"]\n    logger = extras["logger"]\n\n    # Example — async HTTP call:\n    # async with httpx.AsyncClient() as client:\n    #     resp = await client.get("https://api.example.com/data", params=inputs)\n    #     return resp.json()\n\n    return {"result": inputs}\n',
      input_mapping: {},
      timeout_seconds: 10,
      _action_id: 'run_python',
    }),
    configFields: [],  // custom UI below
  },
];

// Group actions for the picker UI — top-level (outside any loop)
const ACTION_GROUPS = [
  { label: 'Order', ids: ['update_order_status', 'update_order_field'] },
  { label: 'Conditions', ids: ['check_condition', 'switch'] },
  { label: 'Notifications', ids: ['send_email', 'send_whatsapp'] },
  { label: 'Parts Search', ids: ['search_parts', 'find_orders_by_status'] },
  { label: 'Supplier Pricing', ids: ['lookup_supplier', 'write_result_to_part'] },
  { label: 'Integrations', ids: ['run_integration', 'check_part_inventory', 'lookup_customer_crm', 'lookup_part_pricing'] },
  { label: 'Data Sync', ids: ['refresh_integration_source', 'enrich_order'] },
  { label: 'Loops', ids: ['for_each_part', 'for_each_array'] },
  { label: 'Subflows', ids: ['call_workflow', 'set_output'] },
  { label: 'Custom Code', ids: ['run_python'] },
];

// Actions available *inside* a foreach loop body
const LOOP_ACTION_GROUPS = [
  { label: 'Integrations', ids: ['check_part_inventory', 'run_integration', 'lookup_part_pricing', 'lookup_supplier'] },
  { label: 'Conditions', ids: ['check_condition'] },
  { label: 'Update', ids: ['write_result_to_part', 'update_part_price_from_integration', 'update_part_field', 'update_order_field'] },
  { label: 'Notifications', ids: ['send_email', 'send_whatsapp'] },
  { label: 'Subflows', ids: ['call_workflow'] },
  { label: 'Custom Code', ids: ['run_python'] },
];

// Trigger-aware suggestions — shown at the top of the picker as "Suggested for this trigger"
const TRIGGER_SUGGESTIONS: Record<string, string[]> = {
  item_added_to_cart: [
    'lookup_supplier',
    'write_result_to_part',
    'send_email',
  ],
  order_status_changed: [
    'update_order_status',
    'send_email',
    'send_whatsapp',
  ],
  order_created: [
    'enrich_order',
    'send_email',
    'lookup_customer_crm',
    'update_order_field',
    'for_each_part',
  ],
  subflow: [
    'run_integration',
    'lookup_supplier',
    'run_python',
    'set_output',
  ],
};

// ---------------------------------------------------------------------------
// Constants (kept for advanced / canvas mode)
// ---------------------------------------------------------------------------

const ACTOR_OPTIONS = ['user', 'admin', 'agent', 'workflow', 'api'];

const NODE_COLORS: Record<string, string> = {
  run_integration:   'bg-blue-50 border-blue-300 text-blue-800',
  send_notification: 'bg-purple-50 border-purple-300 text-purple-800',
  condition:         'bg-amber-50 border-amber-300 text-amber-800',
  switch:            'bg-amber-50 border-amber-300 text-amber-800',
  entity_action:     'bg-teal-50 border-teal-300 text-teal-800',
  foreach_array:     'bg-orange-50 border-orange-300 text-orange-800',
  run_python:        'bg-violet-50 border-violet-300 text-violet-800',
  search_parts:      'bg-sky-50 border-sky-300 text-sky-800',
  query_orders:      'bg-sky-50 border-sky-300 text-sky-800',
  refresh_integration_source: 'bg-cyan-50 border-cyan-300 text-cyan-800',
  enrich_order:      'bg-teal-50 border-teal-300 text-teal-800',
  call_workflow:     'bg-rose-50 border-rose-300 text-rose-800',
  set_output:        'bg-rose-50 border-rose-300 text-rose-800',
};

const NODE_ICON_MAP: Record<string, React.ElementType> = {
  run_integration:   Link2,
  send_notification: Bell,
  condition:         GitBranch,
  switch:            ListTree,
  entity_action:     Database,
  foreach_array:     Layers,
  run_python:        Braces,
  search_parts:      Search,
  query_orders:      Search,
  refresh_integration_source: DatabaseZap,
  enrich_order:      Sparkles,
  call_workflow:     WorkflowIcon,
  set_output:        FileOutput,
};

const EDGE_OPERATORS = [
  { value: 'eq',           label: '= equals' },
  { value: 'neq',          label: '≠ not equals' },
  { value: 'truthy',       label: 'is truthy' },
  { value: 'falsy',        label: 'is falsy' },
  { value: 'exists',       label: 'exists' },
  { value: 'not_exists',   label: 'does not exist' },
  { value: 'contains',     label: 'contains' },
  { value: 'not_contains', label: 'does not contain' },
  { value: 'gt',  label: '>' },
  { value: 'lt',  label: '<' },
  { value: 'gte', label: '>=' },
  { value: 'lte', label: '<=' },
];

const VALUE_FREE_OPS = new Set(['truthy', 'falsy', 'exists', 'not_exists']);

const NODE_OUTPUT_SCHEMA: Record<string, Array<{ key: string; type: string; description: string }>> = {
  run_integration: [
    { key: 'success',                    type: 'boolean', description: 'Whether the integration call succeeded' },
    { key: 'output',                     type: 'object',  description: 'Full response from the integration' },
    // ── Primary output contract (always present for supplier / CSV sources) ──
    { key: 'output.found',               type: 'boolean', description: '✅ True when at least one row was returned — add a condition on an outgoing edge to branch YES/NO' },
    { key: 'output.rows_count',          type: 'integer', description: 'How many rows were returned (0 = not found)' },
    { key: 'output.rows',                type: 'array',   description: 'All returned rows — pass this to Write Result to Part or a For Each loop' },
    // ── Flat hoisted fields (from the first/selected row) ──
    { key: 'output.price',               type: 'number',  description: 'Unit price from the first/selected row' },
    { key: 'output.currency',            type: 'string',  description: 'Currency code (e.g. USD, EUR)' },
    { key: 'output.oe_number',           type: 'string',  description: 'Matched OE part number' },
    { key: 'output.description',         type: 'string',  description: 'Part description' },
    { key: 'output.supplier_name',       type: 'string',  description: 'Supplier / distributor name' },
    { key: 'output.supplier_part_number',type: 'string',  description: 'Supplier part number' },
    { key: 'output.internal_catalog_number', type: 'string', description: 'Internal catalog number' },
    { key: 'output.unit',                type: 'string',  description: 'Unit of measure' },
    { key: 'output.availability',        type: 'string',  description: 'Availability status (in_stock, on_order, discontinued…)' },
    { key: 'output.stock_qty',           type: 'number',  description: 'Quantity in stock' },
    { key: 'output.lead_time_days',      type: 'number',  description: 'Estimated lead time in days' },
    { key: 'output.min_order_qty',       type: 'number',  description: 'Minimum order quantity' },
    { key: 'output._multi_match',        type: 'boolean', description: 'True when more than one row matched' },
    { key: 'output._status',             type: 'string',  description: '"found" | "not_found" | "no_input"' },
  ],
  entity_action: [
    { key: 'success',               type: 'boolean', description: 'Whether the operation succeeded' },
    { key: 'output.operation',      type: 'string',  description: 'Operation performed' },
    { key: 'output.entity_id',      type: 'integer', description: 'ID of the affected entity' },
    { key: 'output.fields_updated', type: 'array',   description: 'List of field names that were updated' },
  ],
  send_notification: [
    { key: 'success', type: 'boolean', description: 'Whether the send succeeded' },
    { key: 'channel', type: 'string',  description: 'Channel used (email/whatsapp)' },
    { key: 'sent',    type: 'boolean', description: 'Whether the message was actually sent' },
  ],
  condition: [
    { key: 'success',       type: 'boolean', description: 'Activity success flag' },
    { key: 'output.result', type: 'boolean', description: 'Condition result (true/false)' },
  ],
  switch: [
    { key: 'success',          type: 'boolean', description: 'Activity success flag' },
    { key: 'output.case',      type: 'string',  description: 'The case that matched (as written), or null' },
    { key: 'output.otherwise', type: 'boolean', description: 'True when no case matched' },
    { key: 'output.value',     type: 'any',     description: 'The value that was read' },
  ],
  foreach_array: [
    { key: 'success',         type: 'boolean', description: 'Whether the array was resolved' },
    { key: 'output.items',    type: 'array',   description: 'Extracted items list' },
    { key: 'output.count',    type: 'integer', description: 'Number of items' },
    { key: 'output.item_var', type: 'string',  description: 'Context key for each iteration' },
  ],
  search_parts: [
    { key: 'success',                  type: 'boolean', description: 'Whether the search succeeded' },
    { key: 'output.results',           type: 'array',   description: 'List of matching parts (oeNumber, description, price, …)' },
    { key: 'output.session_id',        type: 'string',  description: 'ADK session used for this search' },
    { key: 'output.agent_message',     type: 'string',  description: "Agent's text response" },
    { key: 'output.requested_parts',   type: 'array',   description: 'Part names the agent understood from the query' },
    { key: 'output.not_found_parts',   type: 'array',   description: 'Parts the agent could not locate in the catalog' },
    { key: 'output.vin_data',          type: 'object',  description: 'Decoded VIN data returned by the agent' },
    { key: 'output.has_error',         type: 'boolean', description: 'True if the search encountered an error' },
  ],
  query_orders: [
    { key: 'success',      type: 'boolean', description: 'Whether the query ran' },
    { key: 'output.items', type: 'array',   description: 'Matching orders — pass to a For Each loop (each item has id, vin, status, source)' },
    { key: 'output.count', type: 'integer', description: 'How many orders matched' },
  ],
  refresh_integration_source: [
    { key: 'success',           type: 'boolean', description: 'Whether the refresh succeeded' },
    { key: 'output.bq_table',   type: 'string',  description: 'Serving BigQuery table ref (native copy when materialization succeeded)' },
    { key: 'output.bq_external_table', type: 'string', description: 'GCS-backed external table behind the native copy' },
    { key: 'output.gcs_path',   type: 'string',  description: 'GCS URI now backing the source' },
    { key: 'output.item_count', type: 'integer', description: 'Row count of the refreshed dataset' },
    { key: 'output.status',     type: 'string',  description: 'Resulting source status (e.g. active)' },
  ],
  call_workflow: [
    { key: 'success',                 type: 'boolean', description: 'Whether the child workflow completed successfully' },
    { key: 'output.status',           type: 'string',  description: 'Child run status: completed | failed | skipped | timeout' },
    { key: 'output.result',           type: 'object',  description: '✅ The child workflow\'s declared output (its Set Workflow Output fields) — reference as output.result.<key>' },
    { key: 'output.execution_id',     type: 'integer', description: 'Child execution ID (full node results live on that run)' },
    { key: 'output.definition_id',    type: 'integer', description: 'Child workflow definition ID' },
    { key: 'output.organization_id',  type: 'integer', description: 'Organization the child workflow ran under' },
    { key: 'output.workflow_name',    type: 'string',  description: 'Resolved child workflow name' },
    { key: 'output.defaulted',        type: 'boolean', description: 'True when the fallback (default) result was used because the subflow was missing, failed, or timed out' },
    { key: 'output.fallback_reason',  type: 'string',  description: 'Why the fallback result was applied (only present when defaulted)' },
  ],
  set_output: [
    { key: 'success', type: 'boolean', description: 'Whether the output fields resolved' },
    { key: 'output',  type: 'object',  description: 'The declared output fields (what the calling workflow receives as result)' },
  ],
};

// ---------------------------------------------------------------------------
// Context helpers
// ---------------------------------------------------------------------------

const ENTITY_FIELDS_FALLBACK: Record<string, ContextVar[]> = {
  order: [
    { path: 'entity.id',            type: 'integer', description: 'Order ID' },
    { path: 'entity.status',        type: 'string',  description: 'Current status' },
    { path: 'entity.buyer',         type: 'string',  description: 'Buyer name / identifier' },
    { path: 'entity.vin',           type: 'string',  description: 'Vehicle VIN' },
    { path: 'entity.vin_data.make', type: 'string',  description: 'Vehicle make' },
    { path: 'entity.vin_data.model',type: 'string',  description: 'Vehicle model' },
    { path: 'entity.vin_data.year', type: 'string',  description: 'Model year' },
    { path: 'entity.parts_in_order',type: 'array',   description: 'All line items in the order' },
  ],
};

// Default fields exposed on a parts_in_order loop item
const PART_ITEM_FIELDS: ContextVar[] = [
  { path: 'foreach_item.part_number',   type: 'string',  description: 'Part number / SKU' },
  { path: 'foreach_item.part_name',     type: 'string',  description: 'Part name / description' },
  { path: 'foreach_item.quantity',      type: 'integer', description: 'Quantity ordered' },
  { path: 'foreach_item.unit_price',    type: 'number',  description: 'Unit price' },
  { path: 'foreach_item.part_id',       type: 'string',  description: 'Internal part ID' },
  { path: 'foreach_item.manufacturer',  type: 'string',  description: 'Manufacturer / brand' },
  { path: 'foreach_item.status',        type: 'string',  description: 'Part line status' },
];

// Context vars injected automatically when trigger = item_added_to_cart
// The backend resolves these from event_payload.item_id against entity.parts_in_order
const TRIGGERED_ITEM_FIELDS: ContextVar[] = [
  { path: 'triggered_item.part_number',  type: 'string',  description: 'Added item — part number / SKU' },
  { path: 'triggered_item.part_name',    type: 'string',  description: 'Added item — name / description' },
  { path: 'triggered_item.quantity',     type: 'integer', description: 'Added item — quantity' },
  { path: 'triggered_item.unit_price',   type: 'number',  description: 'Added item — current unit price' },
  { path: 'triggered_item.part_id',      type: 'string',  description: 'Added item — internal part ID' },
  { path: 'triggered_item.manufacturer', type: 'string',  description: 'Added item — manufacturer' },
  { path: 'triggered_item.status',       type: 'string',  description: 'Added item — part line status' },
  { path: 'triggered_item_index',        type: 'integer', description: 'Index of the added item in parts_in_order' },
  { path: 'triggered_item_entity_id',    type: 'string',  description: 'Compound ID "<order>:<item>" — use as entity_id_source to update this item' },
];

function buildContextVars(triggerType: string, eventTypes: EventTypeInfo[]) {
  const et = eventTypes.find(e => e.type === triggerType);
  const eventVars: ContextVar[] = et
    ? Object.entries(et.payload_schema).map(([k, v]: [string, any]) => ({
        path: `event_payload.${k}`, type: v.type ?? 'any',
        description: v.description ?? k.replace(/_/g, ' '),
      }))
    : [];
  const entityType: string = et?.entity_type ?? 'order';
  const entityVars: ContextVar[] = et?.entity_fields?.length
    ? et.entity_fields
    : (ENTITY_FIELDS_FALLBACK[entityType] ?? []);
  // Inject triggered_item.* — prefer server-declared fields, fall back to constant
  const extraVars: ContextVar[] = triggerType === 'item_added_to_cart'
    ? (et?.triggered_item_fields?.length ? et.triggered_item_fields : TRIGGERED_ITEM_FIELDS)
    : [];
  return { eventVars, entityType, entityVars, extraVars };
}

/** Return the foreach_array ancestor of a node (if any) and its item_var. */
function findForeachAncestor(nodeId: string, nodes: DAGNode[], edges: DAGEdge[]): DAGNode | null {
  const visited = new Set<string>();
  const queue = [nodeId];
  while (queue.length) {
    const cur = queue.shift()!;
    const ancestors = edges.filter(e => e.target === cur).map(e => e.source);
    for (const src of ancestors) {
      if (visited.has(src)) continue;
      visited.add(src);
      const n = nodes.find(x => x.id === src);
      if (n?.type === 'foreach_array') return n;
      queue.push(src);
    }
  }
  return null;
}

/** Build foreach_item.* context vars based on the loop's array_source. */
function buildForeachItemVars(foreachNode: DAGNode): ContextVar[] {
  const itemVar: string = foreachNode.config?.item_var ?? 'foreach_item';
  const arraySource: string = foreachNode.config?.array_source ?? '';
  const isPartsLoop = arraySource.includes('parts_in_order') ||
    foreachNode.config?._action_id === 'for_each_part';

  const base: ContextVar[] = isPartsLoop
    ? PART_ITEM_FIELDS.map(v => ({ ...v, path: v.path.replace('foreach_item', itemVar) }))
    : [
        { path: `${itemVar}`, type: 'any', description: 'Current loop item' },
      ];
  // Always also expose under the canonical foreach_item key
  const canonical: ContextVar[] = base.map(v => ({
    ...v,
    path: v.path.replace(new RegExp(`^${itemVar}`), 'foreach_item'),
  }));
  // Deduplicate
  const seen = new Set<string>();
  return [...base, ...canonical].filter(v => seen.has(v.path) ? false : (seen.add(v.path), true));
}

function buildNodeResultVars(
  nodeId: string, nodes: DAGNode[], edges: DAGEdge[],
  /** Real step outputs from the last test run (node id → result.output). */
  samples?: Record<string, any>,
): ContextVar[] {
  const vars: ContextVar[] = [];

  // When nodeId is empty, include ALL nodes (used for canvas-level context where
  // we don't know which specific node is being configured, e.g. edge conditions).
  let relevantNodes: DAGNode[];
  if (!nodeId) {
    relevantNodes = nodes;
  } else {
    const visited = new Set<string>();
    const queue = [nodeId];
    while (queue.length) {
      const cur = queue.shift()!;
      edges.filter(e => e.target === cur).forEach(e => {
        if (!visited.has(e.source)) { visited.add(e.source); queue.push(e.source); }
      });
    }

    // If this node is inside a foreach loop, inject foreach_item.* vars
    const foreachAncestor = findForeachAncestor(nodeId, nodes, edges);
    if (foreachAncestor) {
      vars.push(...buildForeachItemVars(foreachAncestor));
    }

    relevantNodes = nodes.filter(n => visited.has(n.id));
  }

  for (const n of relevantNodes) {
    // For run_integration nodes that have a persisted output schema from the
    // selected action, expose those specific fields instead of the generic fallback.
    let schema: Array<{ key: string; type: string; description: string }>;
    if (n.type === 'run_integration' && n.config?._output_schema) {
      const dynSchema = n.config._output_schema as Record<string, { type?: string; description?: string }>;
      schema = [
        { key: 'success', type: 'boolean', description: 'Whether the integration call succeeded' },
        ...Object.entries(dynSchema).map(([k, v]) => ({
          key: `output.${k}`,
          type: v?.type ?? 'any',
          description: v?.description ?? k.replace(/_/g, ' '),
        })),
      ];
    } else {
      schema = NODE_OUTPUT_SCHEMA[n.type] ?? [
        { key: 'success', type: 'boolean', description: 'Activity success flag' },
        { key: 'output',  type: 'object',  description: 'Activity output object' },
      ];
    }

    const sample = samples?.[n.id];
    const seen = new Map<string, { type: string; preview: string }>();
    if (sample !== undefined) {
      for (const f of flattenSample(sample, 'output')) seen.set(f.path, f);
    }
    for (const field of schema) {
      const ex = seen.get(field.key);
      vars.push({
        path: `node_results.${n.id}.${field.key}`,
        type: field.type,
        description: `[${n.label || n.type}] ${field.description}${ex ? ` · test run: ${ex.preview}` : ''}`,
      });
      seen.delete(field.key);
    }
    // Fields that only showed up in real output (e.g. columns inside rows)
    seen.forEach((f, path) => {
      vars.push({
        path: `node_results.${n.id}.${path}`,
        type: f.type,
        description: `[${n.label || n.type}] seen in test run: ${f.preview}`,
      });
    });
  }
  return vars;
}

// ---------------------------------------------------------------------------
// JSON Path helpers (for advanced mode)
// ---------------------------------------------------------------------------

function parseJsonPath(path: string): Array<{ key: string; index?: number }> {
  const segments: Array<{ key: string; index?: number }> = [];
  for (const part of path.split('.')) {
    if (!part) continue;
    const m = part.match(/^(.+?)\[(\d+)\]$/);
    if (m) segments.push({ key: m[1], index: Number(m[2]) });
    else segments.push({ key: part });
  }
  return segments;
}

function buildJsonPath(segments: Array<{ key: string; index?: number }>): string {
  return segments.map((s, i) => {
    const base = i === 0 ? s.key : `.${s.key}`;
    return s.index !== undefined ? `${base}[${s.index}]` : base;
  }).join('').replace(/\.\./g, '.');
}

// ---------------------------------------------------------------------------
// VariablePicker — searchable combobox for picking context variables.
// Renders its option list through a portal (position: fixed) so it is not
// clipped by the overflow-hidden config modals.
// ---------------------------------------------------------------------------

// Group by namespace — a namespace missing from this list renders NOWHERE,
// so every new context namespace must be registered here (bitten by
// organization.*).
const VARIABLE_NAMESPACES = ['event_payload', 'entity', 'organization', 'triggered_item', 'node_results', 'foreach_item'];

const variableNsLabel = (ns: string) =>
  ns === 'event_payload' ? '⚡ Trigger data' :
  ns === 'entity' ? '📦 Entity fields' :
  ns === 'organization' ? '🏢 Organisation' :
  ns === 'triggered_item' ? '🎯 Added item (this event)' :
  ns === 'node_results' ? '🔗 Previous step outputs' :
  '🔁 Loop item';

type VariablePickerProps = {
  contextVars: ContextVar[];
  placeholder?: string;
  allowFreeText?: boolean;
  /** "∅ Set to null" option — on by default; value/onChange then carry real
   *  null (stored as JSON null in the config — the backend resolvers pass
   *  non-string values through untouched, so null survives to the run). */
  allowNull?: boolean;
  value: string | null;
  onChange: (v: string | null) => void;
};

const VariablePicker: React.FC<VariablePickerProps> = ({ value, contextVars, placeholder, onChange, allowFreeText = true, allowNull = true }) => {
  const emit = onChange;
  const isNull = allowNull && value === null;
  const [freeText, setFreeText] = useState(!contextVars.some(v => v.path === value) && !!value);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const isKnown = contextVars.some(v => v.path === value);

  // Every whitespace-separated token must match path or description.
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const matches = (v: ContextVar) =>
    !tokens.length || tokens.every(t => `${v.path} ${v.description ?? ''}`.toLowerCase().includes(t));

  const groups = VARIABLE_NAMESPACES.map(ns => ({
    ns,
    vars: contextVars.filter(v =>
      (v.path.startsWith(ns + '.') || v.path === ns || (ns === 'triggered_item' && v.path.startsWith('triggered_item')))
      && matches(v)),
  })).filter(g => g.vars.length);

  // Flat list of selectable rows, in render order, for keyboard navigation.
  const flat: Array<{ kind: 'var'; v: ContextVar } | { kind: 'custom' } | { kind: 'null' }> = [
    ...groups.flatMap(g => g.vars.map(v => ({ kind: 'var' as const, v }))),
    ...(allowFreeText ? [{ kind: 'custom' as const }] : []),
    ...(allowNull ? [{ kind: 'null' as const }] : []),
  ];

  const updateRect = useCallback(() => {
    if (triggerRef.current) setRect(triggerRef.current.getBoundingClientRect());
  }, []);

  const openDropdown = () => {
    updateRect();
    setQuery('');
    setHighlight(0);
    setOpen(true);
  };

  const pick = (item: { kind: 'var'; v: ContextVar } | { kind: 'custom' } | { kind: 'null' }) => {
    if (item.kind === 'custom') {
      setFreeText(true);
      if (query.trim()) emit(query.trim());
    } else if (item.kind === 'null') {
      setFreeText(false);
      emit(null);
    } else {
      setFreeText(false);
      emit(item.v.path);
    }
    setOpen(false);
  };

  useEffect(() => {
    if (!open) return;
    searchRef.current?.focus();
    const onDoc = (e: MouseEvent) => {
      if (dropdownRef.current?.contains(e.target as Element) || triggerRef.current?.contains(e.target as Element)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [open, updateRect]);

  useEffect(() => {
    if (!open) return;
    dropdownRef.current?.querySelector(`[data-idx="${highlight}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  let dropdownStyle: React.CSSProperties = {};
  if (rect) {
    const width = Math.min(Math.max(rect.width, 380), window.innerWidth - 16);
    const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < 260 && rect.top > spaceBelow;
    dropdownStyle = openUp
      ? { position: 'fixed', left, bottom: window.innerHeight - rect.top + 4, width, maxHeight: Math.min(340, rect.top - 8) }
      : { position: 'fixed', left, top: rect.bottom + 4, width, maxHeight: Math.min(340, spaceBelow - 8) };
  }

  let rowIdx = -1;

  return (
    <div className="space-y-1">
      <div className="flex gap-1.5">
        <button
          type="button"
          ref={triggerRef}
          onClick={() => (open ? setOpen(false) : openDropdown())}
          className="flex-1 min-w-0 flex items-center gap-1.5 border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white text-left focus:outline-none focus:ring-2 focus:ring-indigo-200">
          <span className={`flex-1 truncate ${isKnown || isNull ? 'font-mono text-gray-800' : (freeText || value) ? 'text-gray-700' : 'text-gray-400'}`}>
            {isNull ? '∅ null' : isKnown ? value : (freeText || value) ? '✏️ Custom value' : (placeholder ?? '— choose a value —')}
          </span>
          {(freeText || value || isNull) && (
            <span
              role="button"
              title="Clear"
              onClick={e => { e.stopPropagation(); setFreeText(false); emit(''); setOpen(false); }}
              className="text-gray-300 hover:text-gray-500 cursor-pointer shrink-0">
              <X className="w-3 h-3" />
            </span>
          )}
          <ChevronDown className="w-3 h-3 text-gray-400 shrink-0" />
        </button>
      </div>
      {open && rect && createPortal(
        <div
          ref={dropdownRef}
          style={dropdownStyle}
          className="z-[9999] bg-white border border-gray-200 rounded-xl shadow-2xl flex flex-col overflow-hidden">
          <div className="p-1.5 border-b border-gray-100">
            <div className="relative">
              <Search className="w-3 h-3 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
              <input
                ref={searchRef}
                value={query}
                placeholder="Search fields by name or description…"
                className="w-full border border-gray-200 rounded-lg pl-6 pr-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-200"
                onChange={e => { setQuery(e.target.value); setHighlight(0); }}
                onKeyDown={e => {
                  if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, flat.length - 1)); }
                  else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
                  else if (e.key === 'Enter') {
                    e.preventDefault();
                    if (flat[highlight]) pick(flat[highlight]);
                    else if (allowFreeText && query.trim()) pick({ kind: 'custom' });
                  } else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); triggerRef.current?.focus(); }
                }}
              />
            </div>
          </div>
          <div className="overflow-y-auto flex-1 py-1">
            {groups.map(g => (
              <div key={g.ns}>
                <div className="px-2.5 pt-1.5 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400 sticky top-0 bg-white">
                  {variableNsLabel(g.ns)}
                </div>
                {g.vars.map(v => {
                  const idx = ++rowIdx;
                  return (
                    <button
                      type="button"
                      key={v.path}
                      data-idx={idx}
                      title={`${v.path}${v.description ? ` — ${v.description}` : ''}`}
                      onMouseEnter={() => setHighlight(idx)}
                      onClick={() => pick({ kind: 'var', v })}
                      className={`w-full text-left px-2.5 py-1.5 text-xs truncate ${idx === highlight ? 'bg-indigo-50' : ''} ${v.path === value ? 'text-indigo-600 font-medium' : 'text-gray-700'}`}>
                      <span className="font-mono">{v.path.replace(`${g.ns}.`, '')}</span>
                      {v.description && <span className="text-gray-400"> — {v.description}</span>}
                    </button>
                  );
                })}
              </div>
            ))}
            {!groups.length && (
              <div className="px-3 py-4 text-xs text-gray-400 text-center">
                No matching fields{allowFreeText && query.trim() ? ' — press Enter to use it as a custom value' : ''}
              </div>
            )}
            {allowFreeText && (() => {
              const idx = ++rowIdx;
              return (
                <button
                  type="button"
                  data-idx={idx}
                  onMouseEnter={() => setHighlight(idx)}
                  onClick={() => pick({ kind: 'custom' })}
                  className={`w-full text-left px-2.5 py-1.5 text-xs border-t border-gray-100 ${idx === highlight ? 'bg-indigo-50' : ''} text-gray-700`}>
                  ✏️ {query.trim() ? <>Use “<span className="font-mono">{query.trim()}</span>” as a custom value</> : 'Type a custom value…'}
                </button>
              );
            })()}
            {allowNull && (() => {
              const idx = ++rowIdx;
              return (
                <button
                  type="button"
                  data-idx={idx}
                  onMouseEnter={() => setHighlight(idx)}
                  onClick={() => pick({ kind: 'null' })}
                  className={`w-full text-left px-2.5 py-1.5 text-xs border-t border-gray-100 ${idx === highlight ? 'bg-indigo-50' : ''} ${isNull ? 'text-indigo-600 font-medium' : 'text-gray-700'}`}>
                  ∅ Set to <span className="font-mono">null</span> (no value)
                </button>
              );
            })()}
          </div>
        </div>,
        document.body
      )}
      {(freeText || (!isKnown && value)) && (
        <input
          className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-gray-200"
          value={value ?? ''}
          placeholder="static value or dot-path"
          onChange={e => emit(e.target.value)}
        />
      )}
      {isKnown && (() => {
        const v = contextVars.find(cv => cv.path === value)!;
        return (
          <p className="text-[10px] text-indigo-500 flex items-center gap-1">
            <Info className="w-2.5 h-2.5" /> {v.description}
          </p>
        );
      })()}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Condition rows editor — shared by the list editor and the canvas editor.
// Multiple conditions combine with AND: the node passes only when ALL rows
// match. `conditions` is the authoritative config; row 0 is mirrored into
// the legacy single field/operator/value keys for back-compat (node labels,
// old runs).
// ---------------------------------------------------------------------------

type ConditionRow = { field: string; operator: string; value?: any };

const ConditionRowsEditor: React.FC<{
  config: Record<string, any>;
  contextVars: ContextVar[];
  cfgMany: (patch: Record<string, any>) => void;
}> = ({ config, contextVars, cfgMany }) => {
  const rows: ConditionRow[] = Array.isArray(config.conditions) && config.conditions.length
    ? config.conditions
    : [{ field: config.field ?? '', operator: config.operator ?? 'eq', value: config.value ?? '' }];

  const write = (next: ConditionRow[]) => {
    cfgMany({
      conditions: next,
      field: next[0]?.field ?? '',
      operator: next[0]?.operator ?? 'eq',
      value: next[0]?.value,
    });
  };
  const updateRow = (i: number, patch: Partial<ConditionRow>) =>
    write(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 space-y-2.5">
      <p className="text-[10px] font-semibold text-amber-700 uppercase tracking-wide flex items-center gap-1">
        <GitBranch className="w-3 h-3" />
        {rows.length > 1 ? 'Conditions — passes only when ALL match (AND)' : 'Condition to evaluate'}
      </p>
      {rows.map((row, i) => (
        <div key={i} className="space-y-2">
          {i > 0 && (
            <div className="flex items-center gap-2">
              <div className="flex-1 border-t border-amber-200" />
              <span className="text-[10px] font-bold text-amber-500">AND</span>
              <div className="flex-1 border-t border-amber-200" />
            </div>
          )}
          <div className="flex items-start gap-1.5">
            <div className="flex-1 space-y-2">
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                  Check value <span className="text-red-400">*</span>
                </label>
                <VariablePicker allowNull={false} value={row.field ?? ''} contextVars={contextVars}
                  placeholder="e.g. organization.location" onChange={v => updateRow(i, { field: v ?? '' })} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Operator</label>
                  <select className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-amber-200"
                    value={row.operator ?? 'eq'} onChange={e => updateRow(i, { operator: e.target.value })}>
                    {EDGE_OPERATORS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                {!VALUE_FREE_OPS.has(row.operator ?? 'eq') && (
                  <div>
                    <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Compare to</label>
                    <VariablePicker value={row.value === undefined ? '' : row.value} contextVars={contextVars}
                      placeholder="value" onChange={v => updateRow(i, { value: v })} />
                  </div>
                )}
              </div>
            </div>
            {rows.length > 1 && (
              <button type="button" onClick={() => write(rows.filter((_, idx) => idx !== i))}
                title="Remove this condition"
                className="mt-5 p-1 text-amber-400 hover:text-red-500 rounded transition-colors flex-shrink-0">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      ))}
      <button type="button"
        onClick={() => write([...rows, { field: '', operator: 'eq', value: '' }])}
        className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-600 hover:text-amber-800 transition-colors">
        <Plus className="w-3 h-3" /> Add AND condition
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Subflow config panels — shared by the list editor and the canvas editor
// ---------------------------------------------------------------------------

const CallWorkflowConfig: React.FC<{
  config: Record<string, any>;
  contextVars: ContextVar[];
  cfg: (key: string, value: any) => void;
}> = ({ config, contextVars, cfg }) => {
  const inputFields: Array<{ key: string; value: any }> = config.input_fields ?? [];
  const defaultFields: Array<{ key: string; value: any }> = config.default_result_fields ?? [];
  return (
    <div className="rounded-xl border border-rose-200 bg-rose-50/50 p-3 space-y-3">
      <p className="text-[10px] font-semibold text-rose-700 uppercase tracking-wide flex items-center gap-1">
        <WorkflowIcon className="w-3 h-3" /> Subflow configuration
      </p>
      <div>
        <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
          Workflow name <span className="text-red-400">*</span>
        </label>
        <input
          className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-rose-200 bg-white"
          placeholder="e.g. supplier-enrichment"
          value={config.workflow_name ?? ''}
          onChange={e => cfg('workflow_name', e.target.value)}
        />
        <p className="text-[10px] text-gray-400 mt-0.5">
          In a shared workflow this resolves to the <strong>triggered organization's</strong> active
          workflow with this exact name — each org builds its own implementation.
        </p>
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
            Inputs passed to the subflow
          </label>
          <button
            type="button"
            className="text-[10px] px-2 py-0.5 rounded-md bg-rose-100 text-rose-700 hover:bg-rose-200 transition-colors font-medium"
            onClick={() => cfg('input_fields', [...inputFields, { key: '', value: '' }])}
          >
            + Add input
          </button>
        </div>
        <p className="text-[10px] text-gray-400 mb-2">
          Available inside the subflow as <code className="font-mono bg-rose-100 px-0.5 rounded">event_payload.&lt;key&gt;</code>.
          The parent's own event payload is passed down automatically — add inputs only for
          values beyond it (e.g. from <code className="font-mono bg-rose-100 px-0.5 rounded">node_results</code> or a foreach item).
        </p>
        {inputFields.map((f, fi) => (
          <div key={fi} className="flex items-start gap-1.5 mb-1.5">
            <input
              className="w-32 border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-rose-200 bg-white"
              placeholder="key (e.g. vin)"
              value={f.key}
              onChange={e => {
                const next = inputFields.map((x, i) => (i === fi ? { ...x, key: e.target.value } : x));
                cfg('input_fields', next);
              }}
            />
            <div className="flex-1">
              <VariablePicker
                value={f.value ?? ''}
                contextVars={contextVars}
                placeholder="value — dot-path or static"
                onChange={v => {
                  const next = inputFields.map((x, i) => (i === fi ? { ...x, value: v } : x));
                  cfg('input_fields', next);
                }}
              />
            </div>
            <button
              type="button"
              className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
              onClick={() => cfg('input_fields', inputFields.filter((_, i) => i !== fi))}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
            If the org has no such workflow
          </label>
          <select
            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-rose-200"
            value={config.on_missing ?? 'fail'}
            onChange={e => cfg('on_missing', e.target.value)}
          >
            <option value="fail">Fail this step</option>
            <option value="skip">Skip and continue</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
            Timeout (seconds)
          </label>
          <input
            type="number"
            min={10}
            max={600}
            step={10}
            className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-rose-200 bg-white"
            placeholder="600"
            value={config.timeout_seconds ?? ''}
            onChange={e => cfg('timeout_seconds', e.target.value === '' ? null : Number(e.target.value))}
          />
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
            Fallback result (optional)
          </label>
          <button
            type="button"
            className="text-[10px] px-2 py-0.5 rounded-md bg-rose-100 text-rose-700 hover:bg-rose-200 transition-colors font-medium"
            onClick={() => cfg('default_result_fields', [...defaultFields, { key: '', value: '' }])}
          >
            + Add field
          </button>
        </div>
        <p className="text-[10px] text-gray-400 mb-2">
          If set, this step <strong>succeeds</strong> with these values as{' '}
          <code className="font-mono bg-rose-100 px-0.5 rounded">output.result.&lt;key&gt;</code> when the
          subflow is missing, fails, or times out (overrides the "no such workflow" choice above).
          Downstream steps can detect it via <code className="font-mono bg-rose-100 px-0.5 rounded">output.defaulted</code>.
        </p>
        {defaultFields.map((f, fi) => (
          <div key={fi} className="flex items-start gap-1.5 mb-1.5">
            <input
              className="w-32 border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-rose-200 bg-white"
              placeholder="key (e.g. price)"
              value={f.key}
              onChange={e => {
                const next = defaultFields.map((x, i) => (i === fi ? { ...x, key: e.target.value } : x));
                cfg('default_result_fields', next);
              }}
            />
            <div className="flex-1">
              <VariablePicker
                value={f.value === undefined ? '' : f.value}
                contextVars={contextVars}
                placeholder="value — dot-path or static"
                onChange={v => {
                  const next = defaultFields.map((x, i) => (i === fi ? { ...x, value: v } : x));
                  cfg('default_result_fields', next);
                }}
              />
            </div>
            <button
              type="button"
              className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
              onClick={() => cfg('default_result_fields', defaultFields.filter((_, i) => i !== fi))}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
      <details>
        <summary className="text-[10px] text-gray-500 cursor-pointer select-none">Advanced</summary>
        <div className="mt-2 space-y-2">
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
              Specific workflow ID (instead of name)
            </label>
            <input
              type="number"
              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-rose-200 bg-white"
              placeholder="definition id — same-org targets only"
              value={config.definition_id ?? ''}
              onChange={e => cfg('definition_id', e.target.value === '' ? null : Number(e.target.value))}
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
              Organization ID override
            </label>
            <VariablePicker
              value={config.organization_id ?? ''}
              contextVars={contextVars}
              placeholder="default: the org this run was triggered for"
              onChange={v => cfg('organization_id', v)}
            />
          </div>
        </div>
      </details>
      <div className="rounded-lg border border-rose-100 bg-rose-50/50 px-3 py-2">
        <p className="text-[10px] text-rose-600">
          💡 The subflow declares its return value with a <strong>Set Workflow Output</strong> step.
          Use it downstream as <code className="font-mono bg-rose-100 px-0.5 rounded">output.result.&lt;key&gt;</code>,
          and branch on <code className="font-mono bg-rose-100 px-0.5 rounded">output.status</code>.
        </p>
      </div>
    </div>
  );
};

const SetOutputConfig: React.FC<{
  config: Record<string, any>;
  contextVars: ContextVar[];
  cfg: (key: string, value: any) => void;
}> = ({ config, contextVars, cfg }) => {
  const fields: Array<{ key: string; value: any }> = config.fields ?? [];
  return (
    <div className="rounded-xl border border-rose-200 bg-rose-50/50 p-3 space-y-3">
      <p className="text-[10px] font-semibold text-rose-700 uppercase tracking-wide flex items-center gap-1">
        <FileOutput className="w-3 h-3" /> Workflow output fields
      </p>
      <p className="text-[10px] text-gray-400">
        When another workflow calls this one as a subflow, these fields are what it receives as
        <code className="font-mono bg-rose-100 px-0.5 rounded ml-1">output.result.&lt;key&gt;</code>.
      </p>
      {fields.map((f, fi) => (
        <div key={fi} className="flex items-start gap-1.5">
          <input
            className="w-32 border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-rose-200 bg-white"
            placeholder="key (e.g. price)"
            value={f.key}
            onChange={e => {
              const next = fields.map((x, i) => (i === fi ? { ...x, key: e.target.value } : x));
              cfg('fields', next);
            }}
          />
          <div className="flex-1">
            <VariablePicker
              value={f.value ?? ''}
              contextVars={contextVars}
              placeholder="value — dot-path or static"
              onChange={v => {
                const next = fields.map((x, i) => (i === fi ? { ...x, value: v } : x));
                cfg('fields', next);
              }}
            />
          </div>
          <button
            type="button"
            className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
            onClick={() => cfg('fields', fields.filter((_, i) => i !== fi))}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
      <button
        type="button"
        className="text-[10px] px-2 py-0.5 rounded-md bg-rose-100 text-rose-700 hover:bg-rose-200 transition-colors font-medium"
        onClick={() => cfg('fields', [...fields, { key: '', value: '' }])}
      >
        + Add output field
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// SubflowTriggerPanel — shown inside the trigger section when type === "subflow".
// Lets the child workflow declare the inputs it expects from the calling
// parent; the declared keys become event_payload.<key> options in every
// variable picker of this workflow.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// TriggerFiltersPanel — narrows which events start this workflow
// ---------------------------------------------------------------------------
// Fields come from the event type's filter_fields (GET event types). The
// backend checks every row against the event payload before starting a run;
// all rows must match. No rows = runs for every event of the type.

const TriggerFiltersPanel: React.FC<{
  value: TriggerFilterRow[];
  onChange: (v: TriggerFilterRow[]) => void;
  fields: FilterFieldDef[];
  states: WorkflowState[];
  // Org scope lives in organization_ids (not a payload filter: it also picks
  // the orgs a cron fans out to), but it reads as one more filter row here.
  // No row (open=false) = all organisations, narrowed by the other filters.
  orgScope?: {
    orgs: { id: number; name: string }[]; value: number[]; onChange: (ids: number[]) => void;
    open: boolean; setOpen: (open: boolean) => void;
  };
}> = ({ value, onChange, fields, states, orgScope }) => {
  const [orgQuery, setOrgQuery] = useState('');
  const showOrgRow = !!orgScope?.open;
  const update = (i: number, patch: Partial<TriggerFilterRow>) =>
    onChange(value.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const inputCls = 'border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-amber-200';

  return (
    <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50/40 p-3 space-y-2.5">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-semibold text-amber-700 uppercase tracking-wide flex items-center gap-1">
          <Filter className="w-3 h-3" /> Only run when…
        </p>
        <div className="flex items-center gap-1.5">
        {orgScope && !showOrgRow && (
          <button
            type="button"
            className="text-[10px] px-2 py-0.5 rounded-md bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors font-medium"
            onClick={() => orgScope.setOpen(true)}
          >
            + Organization
          </button>
        )}
        {fields.length > 0 && (
          <button
            type="button"
            className="text-[10px] px-2 py-0.5 rounded-md bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors font-medium"
            onClick={() => {
              const f = fields[0];
              onChange([...value, { key: f.key, op: f.ops?.[0] ?? 'eq', value: '' }]);
            }}
          >
            + Add filter
          </button>
        )}
        </div>
      </div>
      {orgScope && !showOrgRow && (
        <p className="text-[10px] text-gray-400">Runs for all organisations{fields.length > 0 ? ' that match the filters below' : ''}.</p>
      )}
      {fields.length > 0 && (
        <p className="text-[10px] text-gray-400">
          {value.length === 0
            ? 'No filters: this workflow runs for every event of this type.'
            : 'All filters must match for this workflow to run. For "is one of" / "is not one of", separate values with commas.'}
        </p>
      )}
      {orgScope && showOrgRow && (
        <div className="space-y-1">
          <div className="flex items-start gap-1.5">
            <div className={`w-44 text-gray-700 ${inputCls}`}>Organization</div>
            <div className={`w-32 text-gray-700 ${inputCls}`}>{FILTER_OP_LABELS.in ?? 'is one of'}</div>
            <div className={`flex-1 flex flex-wrap items-center gap-1 ${inputCls}`}>
              {orgScope.value.map(id => (
                <span key={id} className="inline-flex items-center gap-1 rounded-md bg-gray-900 text-white px-1.5 py-0.5 text-[11px]">
                  {orgScope.orgs.find(o => o.id === id)?.name ?? `#${id}`}
                  <button type="button" aria-label="Remove organization" onClick={() => orgScope.onChange(orgScope.value.filter(x => x !== id))}>
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              <input
                list="workflow-org-options"
                className="flex-1 min-w-[8rem] outline-none bg-transparent"
                placeholder={orgScope.value.length ? 'add another…' : 'current organisation only — type to add'}
                value={orgQuery}
                onChange={e => {
                  const hit = orgScope.orgs.find(o => o.name === e.target.value);
                  if (hit) {
                    if (!orgScope.value.includes(hit.id)) orgScope.onChange([...orgScope.value, hit.id]);
                    setOrgQuery('');
                  } else setOrgQuery(e.target.value);
                }}
              />
              <datalist id="workflow-org-options">
                {orgScope.orgs.filter(o => !orgScope.value.includes(o.id)).map(o => <option key={o.id} value={o.name} />)}
              </datalist>
            </div>
            <button
              type="button"
              className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
              onClick={() => { orgScope.onChange([]); orgScope.setOpen(false); setOrgQuery(''); }}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
          <p className="text-[10px] text-gray-400 pl-1">
            {orgScope.value.length === 0
              ? 'No organisation selected: this workflow runs for none. Remove the row to run for all organisations.'
              : 'Runs only for these organisations.'}
          </p>
        </div>
      )}
      {value.map((r, i) => {
        const def = fields.find(f => f.key === r.key);
        const ops = def?.ops?.length ? def.ops : Object.keys(FILTER_OP_LABELS);
        const opList = ops.includes(r.op) ? ops : [...ops, r.op];
        const isList = LIST_FILTER_OPS.has(r.op);
        const useStateSelect = def?.type === 'status' && !isList && states.length > 0;
        return (
          <div key={i} className="space-y-1">
            <div className="flex items-center gap-1.5">
              <select
                className={`w-44 ${inputCls}`}
                value={r.key}
                onChange={e => {
                  const next = fields.find(f => f.key === e.target.value);
                  const nextOps = next?.ops?.length ? next.ops : Object.keys(FILTER_OP_LABELS);
                  update(i, { key: e.target.value, op: nextOps.includes(r.op) ? r.op : nextOps[0], value: '' });
                }}
              >
                {/* A key saved via API/JSON that this event type doesn't list stays visible */}
                {!def && <option value={r.key}>{r.key}</option>}
                {fields.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
              </select>
              <select className={`w-32 ${inputCls}`} value={r.op} onChange={e => update(i, { op: e.target.value })}>
                {opList.map(o => <option key={o} value={o}>{FILTER_OP_LABELS[o] ?? o}</option>)}
              </select>
              {!VALUE_FREE_FILTER_OPS.has(r.op) && (useStateSelect ? (
                <select className={`flex-1 ${inputCls}`} value={r.value} onChange={e => update(i, { value: e.target.value })}>
                  <option value="">— status —</option>
                  {r.value && !states.some(st => st.key === r.value) && <option value={r.value}>{r.value}</option>}
                  {states.map(st => <option key={st.key} value={st.key}>{st.label}</option>)}
                </select>
              ) : (
                <input
                  className={`flex-1 font-mono ${inputCls}`}
                  placeholder={isList ? 'e.g. CL, MX' : def?.type === 'number' ? '0' : 'value'}
                  value={r.value}
                  onChange={e => update(i, { value: e.target.value })}
                />
              ))}
              <button
                type="button"
                className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                onClick={() => onChange(value.filter((_, j) => j !== i))}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            {def?.description && <p className="text-[10px] text-gray-400 pl-1">{def.description}</p>}
          </div>
        );
      })}
    </div>
  );
};

export interface SubflowExpectedInput { key: string; description: string }

const SubflowTriggerPanel: React.FC<{
  value: SubflowExpectedInput[];
  onChange: (v: SubflowExpectedInput[]) => void;
}> = ({ value, onChange }) => (
  <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50/50 p-3 space-y-2.5">
    <div className="flex items-center justify-between">
      <p className="text-[10px] font-semibold text-rose-700 uppercase tracking-wide flex items-center gap-1">
        <WorkflowIcon className="w-3 h-3" /> Inputs expected from the parent workflow
      </p>
      <button
        type="button"
        className="text-[10px] px-2 py-0.5 rounded-md bg-rose-100 text-rose-700 hover:bg-rose-200 transition-colors font-medium"
        onClick={() => onChange([...value, { key: '', description: '' }])}
      >
        + Add expected input
      </button>
    </div>
    <p className="text-[10px] text-gray-400">
      The parent's <strong>Call Organization Workflow</strong> step maps values into these keys.
      Each key becomes available here as
      <code className="font-mono bg-rose-100 px-0.5 rounded ml-1">event_payload.&lt;key&gt;</code> in
      every step's variable picker. Return values to the parent with a <strong>Set Workflow Output</strong> step.
    </p>
    {value.map((f, fi) => (
      <div key={fi} className="flex items-center gap-1.5">
        <input
          className="w-36 border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-rose-200 bg-white"
          placeholder="key (e.g. vin)"
          value={f.key}
          onChange={e => onChange(value.map((x, i) => (i === fi ? { ...x, key: e.target.value } : x)))}
        />
        <input
          className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-rose-200 bg-white"
          placeholder="description (optional) — e.g. Vehicle VIN from the importer"
          value={f.description}
          onChange={e => onChange(value.map((x, i) => (i === fi ? { ...x, description: e.target.value } : x)))}
        />
        <button
          type="button"
          className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
          onClick={() => onChange(value.filter((_, i) => i !== fi))}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// CronConfigPanel — shown inside the trigger section when type === "cron"
// ---------------------------------------------------------------------------

const CRON_PRESETS = [
  { label: 'Every hour',           value: '0 * * * *'     },
  { label: 'Every 15 minutes',     value: '*/15 * * * *'  },
  { label: 'Daily at midnight',    value: '0 0 * * *'     },
  { label: 'Daily at 9 AM',        value: '0 9 * * *'     },
  { label: 'Weekdays at 9 AM',     value: '0 9 * * 1-5'   },
  { label: '1st of every month',   value: '0 0 1 * *'     },
  { label: 'Every Sunday midnight',value: '0 0 * * 0'     },
];

interface CronConfig {
  schedule: string;
  timezone: string;
  entity_type: string;
  entity_id: string;
  payload_raw: string;  // JSON string
}

const CronConfigPanel: React.FC<{
  value: CronConfig;
  onChange: (v: CronConfig) => void;
}> = ({ value, onChange }) => {
  const [payloadError, setPayloadError] = useState('');

  const set = (patch: Partial<CronConfig>) => onChange({ ...value, ...patch });

  const validatePayload = (raw: string) => {
    if (!raw.trim() || raw.trim() === '{}') { setPayloadError(''); return; }
    try { JSON.parse(raw); setPayloadError(''); }
    catch { setPayloadError('Invalid JSON'); }
  };

  return (
    <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-700">
        <Clock className="w-3.5 h-3.5" /> Cron schedule configuration
      </div>

      {/* Presets */}
      <div>
        <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Quick presets</label>
        <div className="flex flex-wrap gap-1.5">
          {CRON_PRESETS.map(p => (
            <button key={p.value} type="button"
              onClick={() => set({ schedule: p.value })}
              className={`px-2.5 py-1 text-[11px] rounded-lg border transition-colors ${
                value.schedule === p.value
                  ? 'bg-amber-500 text-white border-amber-500'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300'
              }`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Schedule input */}
      <div>
        <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
          Cron expression <span className="text-red-400">*</span>
        </label>
        <input
          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-200 bg-white"
          placeholder="e.g. 0 9 * * 1-5"
          value={value.schedule}
          onChange={e => set({ schedule: e.target.value })}
        />
        <p className="text-[10px] text-gray-400 mt-0.5">Format: minute hour day month weekday (5 parts)</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {/* Timezone */}
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Timezone</label>
          <select
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-200"
            value={value.timezone}
            onChange={e => set({ timezone: e.target.value })}>
            <option value="UTC">UTC</option>
            <option value="America/New_York">America/New_York</option>
            <option value="America/Los_Angeles">America/Los_Angeles</option>
            <option value="America/Chicago">America/Chicago</option>
            <option value="Europe/London">Europe/London</option>
            <option value="Europe/Paris">Europe/Paris</option>
            <option value="Europe/Berlin">Europe/Berlin</option>
            <option value="Asia/Jerusalem">Asia/Jerusalem</option>
            <option value="Asia/Dubai">Asia/Dubai</option>
            <option value="Asia/Tokyo">Asia/Tokyo</option>
            <option value="Asia/Shanghai">Asia/Shanghai</option>
            <option value="Australia/Sydney">Australia/Sydney</option>
          </select>
        </div>

        {/* Entity type */}
        <div>
          <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Entity type (optional)</label>
          <select
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-200"
            value={value.entity_type}
            onChange={e => set({ entity_type: e.target.value })}>
            <option value="">— none —</option>
            <option value="order">order</option>
            <option value="contact">contact</option>
            <option value="vehicle">vehicle</option>
          </select>
        </div>
      </div>

      {/* Entity ID */}
      <div>
        <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Entity ID (optional)</label>
        <input
          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-200 bg-white"
          placeholder="Leave blank for system-level workflows"
          value={value.entity_id}
          onChange={e => set({ entity_id: e.target.value })}
        />
      </div>

      {/* Static payload */}
      <div>
        <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
          Static payload (JSON, optional)
        </label>
        <textarea
          rows={2}
          className={`w-full border rounded-xl px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-200 bg-white resize-none ${payloadError ? 'border-red-300' : 'border-gray-200'}`}
          placeholder='{ "key": "value" }'
          value={value.payload_raw}
          onChange={e => { set({ payload_raw: e.target.value }); validatePayload(e.target.value); }}
        />
        {payloadError && <p className="text-[10px] text-red-500 mt-0.5">{payloadError}</p>}
        <p className="text-[10px] text-gray-400 mt-0.5">Injected into event_payload on every run. Accessible as <code className="bg-gray-100 px-1 rounded">event_payload.*</code></p>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// SecretsConfigPanel — bind Google Secret Manager secrets to workflow variables
// ---------------------------------------------------------------------------

const SecretsConfigPanel: React.FC<{
  value: SecretRef[];
  onChange: (v: SecretRef[]) => void;
  orgId?: number;
}> = ({ value, onChange, orgId }) => {
  // Per-row local plaintext value + status (value never lives in SecretRef)
  const [draftValues, setDraftValues] = useState<Record<number, string>>({});
  const [savingIdx, setSavingIdx] = useState<number | null>(null);
  const [savedIdx, setSavedIdx] = useState<Record<number, boolean>>({});
  const [rowError, setRowError] = useState<Record<number, string>>({});
  const [showValue, setShowValue] = useState<Record<number, boolean>>({});

  // Reusable secrets already stored in Secret Manager
  const [available, setAvailable] = useState<SecretRef[]>([]);
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [showReuse, setShowReuse] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadingAvail(true);
    workflowService.listWorkflowSecrets(orgId)
      .then(r => { if (!cancelled) setAvailable(r.data ?? []); })
      .catch(() => { if (!cancelled) setAvailable([]); })
      .finally(() => { if (!cancelled) setLoadingAvail(false); });
    return () => { cancelled = true; };
  }, [orgId]);

  const add = () => onChange([...value, { name: '', secret: '', version: 'latest' }]);
  const update = (i: number, patch: Partial<SecretRef>) =>
    onChange(value.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const remove = (i: number) => {
    onChange(value.filter((_, idx) => idx !== i));
    setDraftValues(d => { const n = { ...d }; delete n[i]; return n; });
  };

  // Bind an existing (already-stored) secret into the workflow for reuse
  const reuse = (ref: SecretRef) => {
    // Avoid binding the same secret id twice
    if (value.some(s => s.secret === ref.secret)) { setShowReuse(false); return; }
    onChange([...value, {
      name: ref.name || ref.secret,
      secret: ref.secret,
      version: ref.version ?? 'latest',
      project: ref.project,
    }]);
    setShowReuse(false);
  };

  // Secrets not already bound into this workflow
  const reusable = available.filter(a => !value.some(s => s.secret === a.secret));

  const saveSecret = async (i: number) => {
    const ref = value[i];
    const val = draftValues[i] ?? '';
    setRowError(e => ({ ...e, [i]: '' }));
    if (!ref.name.trim()) { setRowError(e => ({ ...e, [i]: 'Variable name is required' })); return; }
    if (!val) { setRowError(e => ({ ...e, [i]: 'Enter a value to store' })); return; }
    setSavingIdx(i);
    try {
      const r = await workflowService.createWorkflowSecret(
        { name: ref.name.trim(), value: val, secret: ref.secret || undefined, project: ref.project || undefined },
        orgId,
      );
      // Persist the returned reference (secret id / project / version)
      update(i, { secret: r.data.secret, version: r.data.version ?? 'latest', project: r.data.project });
      setDraftValues(d => ({ ...d, [i]: '' }));
      setSavedIdx(s => ({ ...s, [i]: true }));
      setTimeout(() => setSavedIdx(s => ({ ...s, [i]: false })), 2500);
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setRowError(er => ({ ...er, [i]: typeof detail === 'string' ? detail : 'Failed to store secret' }));
    } finally {
      setSavingIdx(null);
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
          <KeyRound className="w-3.5 h-3.5 text-amber-500" /> Secrets (Google Secret Manager)
        </div>
        <div className="flex items-center gap-2">
          {(reusable.length > 0 || loadingAvail) && (
            <div className="relative">
              <button type="button" onClick={() => setShowReuse(v => !v)}
                className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-600 hover:text-indigo-600">
                <Link2 className="w-3 h-3" />
                {loadingAvail ? 'Loading…' : `Reuse existing${reusable.length ? ` (${reusable.length})` : ''}`}
                <ChevronDown className="w-3 h-3" />
              </button>
              {showReuse && reusable.length > 0 && (
                <div className="absolute right-0 mt-1 w-64 max-h-60 overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
                  <p className="px-3 py-1 text-[10px] text-gray-400 uppercase tracking-wide">Stored workflow secrets</p>
                  {reusable.map((a, idx) => (
                    <button key={idx} type="button" onClick={() => reuse(a)}
                      className="w-full text-left px-3 py-1.5 hover:bg-indigo-50 flex items-center gap-2">
                      <KeyRound className="w-3 h-3 text-amber-500 shrink-0" />
                      <span className="flex-1 min-w-0">
                        <span className="block text-xs font-mono text-gray-700 truncate">{a.name || a.secret}</span>
                        <span className="block text-[10px] text-gray-400 truncate">{a.secret}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <button type="button" onClick={add}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:text-indigo-700">
            <Plus className="w-3 h-3" /> Add secret
          </button>
        </div>
      </div>

      <p className="text-[10px] text-gray-500 leading-relaxed">
        Enter a value and click <strong>Save to Secret Manager</strong> to store it securely in Google
        Secret Manager. Reference it anywhere in the workflow as
        <code className="bg-white border border-gray-200 px-1 rounded ml-1">{'secrets.<name>'}</code>.
        The value is sent once and never stored in our database.
      </p>

      {value.length === 0 ? (
        <div className="flex items-center gap-2 text-[11px] text-gray-400 bg-white border border-dashed border-gray-200 rounded-lg px-3 py-2.5">
          <Lock className="w-3.5 h-3.5" /> No secrets configured.
        </div>
      ) : (
        <div className="space-y-2">
          {value.map((s, i) => {
            const stored = !!s.secret;
            return (
            <div key={i} className="rounded-lg border border-gray-200 bg-white p-2.5 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                    Variable name <span className="text-red-400">*</span>
                  </label>
                  <input
                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    placeholder="dms_password"
                    value={s.name}
                    onChange={e => update(i, { name: e.target.value.replace(/[^a-zA-Z0-9_]/g, '') })}
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                    Secret value {stored ? <span className="text-green-500 normal-case">· stored</span> : <span className="text-red-400">*</span>}
                  </label>
                  <div className="flex gap-1">
                    <input
                      type={showValue[i] ? 'text' : 'password'}
                      className="flex-1 min-w-0 border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-indigo-200"
                      placeholder={stored ? 'Enter new value to rotate…' : 'super-secret-value'}
                      value={draftValues[i] ?? ''}
                      onChange={e => setDraftValues(d => ({ ...d, [i]: e.target.value }))}
                    />
                    <button type="button" onClick={() => setShowValue(v => ({ ...v, [i]: !v[i] }))}
                      className="px-1.5 text-gray-400 hover:text-gray-600">
                      {showValue[i] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 items-end">
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Secret ID (optional)</label>
                  <input
                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    placeholder="auto-generated"
                    value={s.secret ?? ''}
                    onChange={e => update(i, { secret: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Project (optional)</label>
                  <input
                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-indigo-200"
                    placeholder="defaults to runtime project"
                    value={s.project ?? ''}
                    onChange={e => update(i, { project: e.target.value || undefined })}
                  />
                </div>
              </div>
              {rowError[i] && <p className="text-[10px] text-red-500">{rowError[i]}</p>}
              <div className="flex items-center justify-between pt-0.5">
                {s.name ? (
                  <code className="text-[10px] text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">secrets.{s.name}</code>
                ) : <span />}
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => saveSecret(i)} disabled={savingIdx === i}
                    className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
                    {savingIdx === i
                      ? <><Loader2 className="w-3 h-3 animate-spin" /> Saving…</>
                      : savedIdx[i]
                        ? <><Check className="w-3 h-3" /> Saved</>
                        : <><Save className="w-3 h-3" /> Save to Secret Manager</>}
                  </button>
                  <button type="button" onClick={() => remove(i)}
                    className="inline-flex items-center gap-1 text-[10px] text-red-500 hover:text-red-600">
                    <Trash2 className="w-3 h-3" /> Remove
                  </button>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// InlineContextPanel — always-visible strip showing available data
// ---------------------------------------------------------------------------

const InlineContextPanel: React.FC<{
  triggerType: string;
  eventTypes: EventTypeInfo[];
  nodeResultVars?: ContextVar[];
  foreachItemVars?: ContextVar[];
  onPick?: (path: string) => void;
  defaultOpen?: boolean;
}> = ({ triggerType, eventTypes, nodeResultVars = [], foreachItemVars = [], onPick, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [copied, setCopied] = useState<string | null>(null);
  if (!triggerType && nodeResultVars.length === 0 && foreachItemVars.length === 0) return null;

  const { eventVars, entityType, entityVars, extraVars } = triggerType
    ? buildContextVars(triggerType, eventTypes)
    : { eventVars: [], entityType: 'order', entityVars: [], extraVars: [] };

  const triggeredItemVars = extraVars.filter(v =>
    v.path.startsWith('triggered_item')
  );

  const copyPath = (path: string) => {
    navigator.clipboard.writeText(path).catch(() => {});
    setCopied(path);
    setTimeout(() => setCopied(null), 1200);
    onPick?.(path);
  };

  const Pill = ({ v }: { v: ContextVar }) => (
    <button
      type="button" onClick={() => copyPath(v.path)} title={`${v.description} — click to copy`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-mono transition-colors cursor-pointer
        ${copied === v.path ? 'bg-indigo-100 border-indigo-300 text-indigo-700' : 'bg-white border-gray-200 text-gray-600 hover:border-indigo-200 hover:text-indigo-600'}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
        v.type === 'integer' || v.type === 'number' ? 'bg-blue-400' :
        v.type === 'boolean' ? 'bg-yellow-400' :
        v.type === 'array' ? 'bg-rose-400' :
        v.type === 'object' ? 'bg-violet-400' : 'bg-green-400'
      }`} />
      {v.path.split('.').slice(1).join('.')}
      {copied === v.path ? <Check className="w-2.5 h-2.5 text-indigo-500" /> : null}
    </button>
  );

  const nodeGroups = nodeResultVars
    .filter(v => v.path.startsWith('node_results.'))
    .reduce<Record<string, ContextVar[]>>((acc, v) => {
      const nid = v.path.split('.')[1] ?? '';
      (acc[nid] ??= []).push(v);
      return acc;
    }, {});

  return (
    <div className="rounded-xl border border-indigo-100 bg-indigo-50/40 overflow-hidden">
      <button type="button" onClick={() => setOpen(p => !p)}
        className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-semibold text-indigo-700 hover:bg-indigo-50 transition-colors">
        <div className="flex items-center gap-1.5">
          <Variable className="w-3 h-3" />
          Available data in this step
        </div>
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2.5">
          {foreachItemVars.length > 0 && (
            <div className="rounded-lg border border-orange-200 bg-orange-50/60 px-2 py-2">
              <p className="text-[9px] font-bold uppercase tracking-widest text-orange-500 mb-1">🔁 Loop item fields</p>
              <div className="flex flex-wrap gap-1">
                {foreachItemVars.map(v => <Pill key={v.path} v={v} />)}
              </div>
            </div>
          )}
          {triggeredItemVars.length > 0 && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-2 py-2">
              <p className="text-[9px] font-bold uppercase tracking-widest text-emerald-600 mb-1 flex items-center gap-1">
                🎯 Added item — available in this trigger
              </p>
              <div className="flex flex-wrap gap-1">
                {triggeredItemVars.map(v => <Pill key={v.path} v={v} />)}
              </div>
            </div>
          )}
          {eventVars.length > 0 && (
            <div>
              <p className="text-[9px] font-bold uppercase tracking-widest text-indigo-400 mb-1">⚡ Trigger data</p>
              <div className="flex flex-wrap gap-1">
                {eventVars.map(v => <Pill key={v.path} v={v} />)}
              </div>
            </div>
          )}
          {(() => {
            // organization.* rides entity_fields from the backend but is its
            // own namespace — shown as its own group (the Pill strips the
            // first path segment, so mixing them under entity was ambiguous).
            const orgVars = entityVars.filter(v => v.path.startsWith('organization.'));
            const pureEntityVars = entityVars.filter(v => !v.path.startsWith('organization.'));
            return (
              <>
                {pureEntityVars.length > 0 && (
                  <div>
                    <p className="text-[9px] font-bold uppercase tracking-widest text-indigo-400 mb-1">📦 {entityType} fields</p>
                    <div className="flex flex-wrap gap-1">
                      {pureEntityVars.slice(0, 16).map(v => <Pill key={v.path} v={v} />)}
                      {pureEntityVars.length > 16 && (
                        <span className="text-[10px] text-gray-400 self-center">+{pureEntityVars.length - 16} more</span>
                      )}
                    </div>
                  </div>
                )}
                {orgVars.length > 0 && (
                  <div>
                    <p className="text-[9px] font-bold uppercase tracking-widest text-teal-500 mb-1">🏢 Organisation</p>
                    <div className="flex flex-wrap gap-1">
                      {orgVars.map(v => <Pill key={v.path} v={v} />)}
                    </div>
                  </div>
                )}
              </>
            );
          })()}
          {Object.keys(nodeGroups).length > 0 && (
            <div>
              <p className="text-[9px] font-bold uppercase tracking-widest text-emerald-500 mb-1">🔗 Previous step outputs</p>
              {Object.entries(nodeGroups).map(([nid, vars]) => (
                <div key={nid} className="mb-1.5">
                  <p className="text-[9px] text-emerald-600 font-semibold mb-0.5 truncate">
                    {vars[0].description.match(/^\[(.+?)\]/)?.[1] ?? nid}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {vars.map(v => <Pill key={v.path} v={v} />)}
                  </div>
                </div>
              ))}
            </div>
          )}
          <p className="text-[9px] text-indigo-300">Click any value to copy its path.</p>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Badge
// ---------------------------------------------------------------------------

const Badge: React.FC<{ label: string; color?: string }> = ({ label, color = 'gray' }) => {
  const cls: Record<string, string> = {
    gray:   'bg-gray-100 text-gray-700',
    blue:   'bg-blue-100 text-blue-700',
    green:  'bg-green-100 text-green-700',
    red:    'bg-red-100 text-red-700',
    purple: 'bg-purple-100 text-purple-700',
    amber:  'bg-amber-100 text-amber-700',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls[color] ?? cls.gray}`}>
      {label}
    </span>
  );
};

const execStatusColor = (s: string) =>
  s === 'completed' ? 'green' : s === 'failed' ? 'red' : s === 'running' ? 'blue' : 'gray';

// ---------------------------------------------------------------------------
// ActionPicker — modal/drawer to choose an action to add
// ---------------------------------------------------------------------------

// Flag lookup for actions gated behind the GLOBAL external_enrichment flag
// (no org override by product decision); cached per session.
let enrichmentFlagCached: boolean | undefined;
function useEnrichmentActionVisible(orgId?: number): boolean {
  // No orgId = admin/shared-workflow mode → always visible (the platform
  // admin may wire workflows before flipping the flag on).
  const [visible, setVisible] = useState<boolean>(
    orgId ? enrichmentFlagCached ?? false : true,
  );
  useEffect(() => {
    if (!orgId) { setVisible(true); return; }
    if (enrichmentFlagCached !== undefined) { setVisible(enrichmentFlagCached); return; }
    let cancelled = false;
    import('../services/AdminService').then(({ adminService }) =>
      adminService.getGlobalFeatureFlagPublic('external_enrichment')
        .then(res => {
          const on = !!res.data?.is_enabled;
          enrichmentFlagCached = on;
          if (!cancelled) setVisible(on);
        })
        .catch(() => { if (!cancelled) setVisible(false); }));
    return () => { cancelled = true; };
  }, [orgId]);
  return visible;
}

const ActionPicker: React.FC<{
  onPick: (action: ExplicitAction) => void;
  onClose: () => void;
  groups?: typeof ACTION_GROUPS;
  title?: string;
  subtitle?: string;
  triggerType?: string;
  orgId?: number;
}> = ({ onPick, onClose, groups = ACTION_GROUPS, title = 'Add a step', subtitle = 'What should happen next in your workflow?', triggerType, orgId }) => {
  const enrichmentVisible = useEnrichmentActionVisible(orgId);
  const hiddenIds = enrichmentVisible ? [] : ['enrich_order'];
  const visibleGroups = groups
    .map(g => ({ ...g, ids: g.ids.filter(id => !hiddenIds.includes(id)) }))
    .filter(g => g.ids.length > 0);
  const suggestedIds = (triggerType ? (TRIGGER_SUGGESTIONS[triggerType] ?? []) : [])
    .filter(id => !hiddenIds.includes(id));
  const suggestedActions = suggestedIds.map(id => EXPLICIT_ACTIONS.find(a => a.id === id)).filter(Boolean) as ExplicitAction[];

  const ActionBtn = ({ action }: { action: ExplicitAction }) => {
    const Icon = action.icon;
    return (
      <button type="button" onClick={() => { onPick(action); onClose(); }}
        className={`w-full flex items-start gap-3 px-3 py-2.5 rounded-xl border-2 text-left transition-all hover:scale-[1.01] active:scale-100 ${action.color}`}>
        <div className="mt-0.5 flex-shrink-0"><Icon className="w-4 h-4" /></div>
        <div>
          <p className="text-xs font-semibold">{action.label}</p>
          <p className="text-[11px] opacity-70 mt-0.5">{action.description}</p>
        </div>
      </button>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-gray-200 shadow-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <p className="text-sm font-semibold text-gray-900">{title}</p>
            <p className="text-xs text-gray-400">{subtitle}</p>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto">
          {/* Suggested for this trigger */}
          {suggestedActions.length > 0 && (
            <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-3 space-y-1.5">
              <p className="text-[10px] font-bold uppercase tracking-widest text-indigo-500 mb-2 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Suggested for this trigger
              </p>
              {suggestedActions.map(action => <ActionBtn key={action.id} action={action} />)}
            </div>
          )}
          {/* All groups */}
          {visibleGroups.map(group => (
            <div key={group.label}>
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">{group.label}</p>
              <div className="space-y-1.5">
                {group.ids.map(id => {
                  const action = EXPLICIT_ACTIONS.find(a => a.id === id)!;
                  if (!action) return null;
                  return <ActionBtn key={id} action={action} />;
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// StepCard — one action step in the simplified builder
// ---------------------------------------------------------------------------

interface StepNode extends DAGNode {
  _action_id?: string;
  children?: StepNode[];  // for foreach_array: nested body steps
}

interface StepCardProps {
  step: StepNode;
  index: number;
  totalSteps: number;
  states: WorkflowState[];
  contextVars: ContextVar[];
  foreachItemVars?: ContextVar[];
  sources: any[];
  isExpanded: boolean;
  onToggle: () => void;
  onUpdate: (updated: StepNode) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  // Loop-body management (only for foreach_array steps)
  counter?: React.MutableRefObject<number>;
  entityType?: string;
  allVars?: ContextVar[];
  orgId?: number;
  sharedOrgIds?: number[];
}

const StepCard: React.FC<StepCardProps> = ({
  step, index, totalSteps, states, contextVars, foreachItemVars = [], sources, isExpanded,
  onToggle, onUpdate, onDelete, onMoveUp, onMoveDown,
  counter, entityType = 'order', allVars = [], orgId, sharedOrgIds,
}) => {
  const [loadingSrc, setLoadingSrc] = useState(false);
  const [stepSources, setStepSources] = useState<any[]>(sources);
  const [sourcesSharedMode, setSourcesSharedMode] = useState(false);
  const [showLoopPicker, setShowLoopPicker] = useState(false);
  const [loopExpandedIdx, setLoopExpandedIdx] = useState<number | null>(null);

  const actionDef = EXPLICIT_ACTIONS.find(a =>
    a.id === step.config?._action_id || a.nodeType === step.type
  ) ?? EXPLICIT_ACTIONS.find(a => a.nodeType === step.type)!;

  const Icon = actionDef?.icon ?? Circle;
  const color = actionDef?.color ?? NODE_COLORS[step.type] ?? 'bg-gray-50 border-gray-200';

  const cfg = (k: string, v: any) => onUpdate({ ...step, config: { ...step.config, [k]: v } });
  const cfgMany = (patch: Record<string, any>) => onUpdate({ ...step, config: { ...step.config, ...patch } });

  // For entity_action with update_order_status: reflect _status_value into fields
  useEffect(() => {
    if (step.config._action_id === 'update_order_status' && step.config._status_value !== undefined) {
      const fields = [{ key: 'status', value: step.config._status_value }];
      if (JSON.stringify(step.config.fields) !== JSON.stringify(fields)) {
        onUpdate({ ...step, config: { ...step.config, fields } });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.config._status_value]);

  const loadIntSources = useCallback(async (intType: string) => {
    setLoadingSrc(true);
    try {
      const result = await integrationService.getSourcesForWorkflow(intType, orgId, sharedOrgIds);
      setStepSources(result.sources);
      setSourcesSharedMode(result.isSharedMode);
    }
    catch { setStepSources([]); }
    finally { setLoadingSrc(false); }
  }, [orgId, sharedOrgIds]);

  useEffect(() => {
    if ((step.type === 'run_integration' || step.type === 'refresh_integration_source') && step.config.integration_type) {
      loadIntSources(step.config.integration_type);
    } else if (step.type === 'enrich_order') {
      loadIntSources('enrichment');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedSource = stepSources.find((s: any) => String(s.id) === String(step.config.source_id));
  const actions: any[] = selectedSource?.actions || [];

  // Field mapping for entity_action (update_order_field)
  const entityFields: Array<{key: string; value: string | null}> = step.config.fields ?? [];
  const updateField = (i: number, patch: Partial<{key: string; value: string | null}>) => {
    const upd = entityFields.map((f, idx) => idx === i ? { ...f, ...patch } : f);
    cfg('fields', upd);
  };
  const addField = () => cfg('fields', [...entityFields, { key: '', value: '' }]);
  const removeField = (i: number) => cfg('fields', entityFields.filter((_, idx) => idx !== i));

  return (
    <div className="relative">
      {/* Connector line */}
      {index < totalSteps - 1 && (
        <div className="absolute left-5 bottom-0 translate-y-full w-0.5 h-4 bg-gray-200 z-0" />
      )}

      <div className={`relative rounded-xl border-2 overflow-hidden transition-shadow ${isExpanded ? 'shadow-md' : 'shadow-sm hover:shadow-md'} ${color}`}>
        {/* Header row */}
        <div className="flex items-center gap-3 px-3 py-2.5 cursor-pointer select-none" onClick={onToggle}>
          <div className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 font-bold text-[10px] bg-white/60`}>
            {index + 1}
          </div>
          <Icon className="w-4 h-4 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold truncate">{step.label || actionDef?.label || step.type}</p>
            {!isExpanded && step.type === 'entity_action' && step.config._action_id === 'update_order_status' && step.config._status_value && (
              <p className="text-[10px] opacity-60 truncate">→ {step.config._status_value}</p>
            )}
            {!isExpanded && (step.type === 'send_notification') && step.config.channel && (
              <p className="text-[10px] opacity-60 truncate">via {step.config.channel} to {step.config.recipient || '…'}</p>
            )}
            {!isExpanded && step.type === 'call_workflow' && (step.config.workflow_name || step.config.definition_id) && (
              <span className="text-[10px] font-mono bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded">
                → {step.config.workflow_name || `#${step.config.definition_id}`}
              </span>
            )}
            {!isExpanded && step.type === 'search_parts' && (step.config.vin || step.config.query) && (
              <p className="text-[10px] opacity-60 truncate">
                {[step.config.vin && `VIN: ${step.config.vin}`, step.config.query && `query: ${step.config.query}`].filter(Boolean).join(' · ')}
              </p>
            )}
          </div>
          <div className="flex items-center gap-0.5 flex-shrink-0">
            {index > 0 && (
              <button type="button" onClick={e => { e.stopPropagation(); onMoveUp(); }} title="Move up"
                className="p-1 opacity-50 hover:opacity-100 rounded"><ChevronUp className="w-3 h-3" /></button>
            )}
            {index < totalSteps - 1 && (
              <button type="button" onClick={e => { e.stopPropagation(); onMoveDown(); }} title="Move down"
                className="p-1 opacity-50 hover:opacity-100 rounded"><ChevronDown className="w-3 h-3" /></button>
            )}
            <button type="button" onClick={e => { e.stopPropagation(); onDelete(); }} title="Remove step"
              className="p-1 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 rounded"><Trash2 className="w-3 h-3" /></button>
            {isExpanded ? <ChevronUp className="w-3.5 h-3.5 opacity-50" /> : <ChevronDown className="w-3.5 h-3.5 opacity-50" />}
          </div>
        </div>

        {/* Expanded config */}
        {isExpanded && (
          <div className="border-t border-current/10 bg-white/80 px-4 py-3 space-y-3">
            {/* Label */}
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Step label</label>
              <input className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-gray-200"
                value={step.label} onChange={e => onUpdate({ ...step, label: e.target.value })} />
            </div>

            {/* ── update_order_status ── */}
            {step.config._action_id === 'update_order_status' && (
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                  New Status <span className="text-red-400">*</span>
                </label>
                <select className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-teal-200"
                  value={step.config._status_value ?? ''}
                  onChange={e => cfg('_status_value', e.target.value)}>
                  <option value="">— select a status —</option>
                  {states.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </div>
            )}

            {/* ── find_orders_by_status (query_orders) ── */}
            {step.config._action_id === 'find_orders_by_status' && (
              <>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                    Order Status <span className="text-red-400">*</span></label>
                  <select className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-sky-200"
                    value={step.config.status ?? ''} onChange={e => cfg('status', e.target.value)}>
                    <option value="">— select a status —</option>
                    {states.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Source (optional)</label>
                  <input className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-sky-200"
                    placeholder="e.g. insurance_portal" value={step.config.source ?? ''} onChange={e => cfg('source', e.target.value)} />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Max orders</label>
                  <input type="number" min={1} max={1000} className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-sky-200"
                    value={step.config.limit ?? 200} onChange={e => cfg('limit', Number(e.target.value))} />
                </div>
              </>
            )}

            {/* ── update_order_field / update_part_field / update_part_price_from_integration / write_result_to_part ── */}
            {(step.config._action_id === 'update_order_field' || step.config._action_id === 'update_part_field' || step.config._action_id === 'update_part_price_from_integration' || step.config._action_id === 'write_result_to_part') && (() => {
              // Known target fields for write_result_to_part (mapped to canonical part field names)
              const PART_FIELD_OPTIONS = [
                { value: 'unit_price',               label: 'Unit Price' },
                { value: 'currency',                 label: 'Currency' },
                { value: 'supplier_name',            label: 'Supplier Name' },
                { value: 'supplier_part_number',     label: 'Supplier Part #' },
                { value: 'availability',             label: 'Availability' },
                { value: 'description',              label: 'Description' },
                { value: 'internal_catalog_number',  label: 'Internal Catalog #' },
                { value: 'stock_qty',                label: 'Stock Qty' },
                { value: 'lead_time_days',           label: 'Lead Time (days)' },
                { value: 'unit',                     label: 'Unit' },
                { value: 'min_order_qty',            label: 'Min Order Qty' },
                { value: 'oe_number',                label: 'OE Number' },
              ];

              // For write_result_to_part: quick-add buttons from prior lookup nodes
              const lookupNodePrefixes = step.config._action_id === 'write_result_to_part'
                ? Array.from(new Set(
                    contextVars
                      .filter(v => v.path.startsWith('node_results.') && v.path.includes('.output.price'))
                      .map(v => v.path.split('.output.')[0])
                  ))
                : [];

              return (
              <div>
                {/* Quick-add from prior lookup (write_result_to_part only) */}
                {step.config._action_id === 'write_result_to_part' && lookupNodePrefixes.length > 0 && (
                  <div className="rounded-xl border border-green-200 bg-green-50/60 px-3 py-2.5 mb-3 space-y-2">
                    <p className="text-[9px] font-bold uppercase tracking-widest text-green-600 flex items-center gap-1">
                      ⚡ Quick-add from lookup result
                    </p>
                    {lookupNodePrefixes.map(prefix => (
                      <div key={prefix}>
                        <p className="text-[10px] font-semibold text-green-700 mb-1.5 flex items-center gap-1">
                          <span className="bg-green-100 border border-green-200 rounded px-1.5 py-0.5 font-mono text-[9px]">{prefix.replace('node_results.', '')}</span>
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {PART_FIELD_OPTIONS.map(f => {
                            const varPath = `${prefix}.output.${f.value}`;
                            const alreadyMapped = entityFields.some(ef => ef.key === f.value);
                            return (
                              <button key={f.value} type="button" title={`Write ${f.label} ← ${varPath}`} disabled={alreadyMapped}
                                onClick={() => cfg('fields', [...entityFields, { key: f.value, value: varPath }])}
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-mono transition-colors ${
                                  alreadyMapped
                                    ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-default'
                                    : 'border-green-300 bg-white text-green-700 hover:bg-green-100 hover:border-green-500 cursor-pointer'
                                }`}>
                                {alreadyMapped ? <span className="text-green-500">✓</span> : <Plus className="w-2 h-2" />} {f.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                    <p className="text-[9px] text-green-400 mt-0.5">Click a field to add it as a mapping row. Each field is read from the lookup result and written to the cart item.</p>
                  </div>
                )}
                {step.config._action_id === 'write_result_to_part' && lookupNodePrefixes.length === 0 && (
                  <div className="rounded-lg border border-amber-100 bg-amber-50/50 px-3 py-2 mb-2">
                    <p className="text-[10px] text-amber-600">💡 Add a <strong>Look Up Supplier Pricing</strong> step before this one to get quick-add shortcuts, or add rows manually below.</p>
                  </div>
                )}

                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                    {step.config._action_id === 'write_result_to_part' ? 'Field mappings → cart item' :
                     step.config._action_id === 'update_part_field' ? 'Part fields to update' :
                     step.config._action_id === 'update_part_price_from_integration' ? 'Price fields to map' :
                     'Fields to update'}
                  </label>
                  <button type="button" onClick={addField}
                    className="text-[10px] flex items-center gap-0.5 text-teal-700 border border-teal-200 rounded px-1.5 py-0.5 bg-white hover:bg-teal-50">
                    <Plus className="w-2.5 h-2.5" /> Add row
                  </button>
                </div>

                {entityFields.length === 0 ? (
                  <div className="border border-dashed border-gray-200 rounded-lg p-3 text-center text-[10px] text-gray-400">
                    {step.config._action_id === 'write_result_to_part'
                      ? 'Use the quick-add buttons above, or click "Add row" to manually map a field.'
                      : 'Click "Add row" to add field mappings.'}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {/* Header hint */}
                    {step.config._action_id === 'write_result_to_part' && (
                      <div className="flex items-center gap-1.5 text-[9px] text-gray-400 px-0.5">
                        <span className="w-28 flex-shrink-0">Field on cart item</span>
                        <span className="flex-1">Value (from lookup / context)</span>
                      </div>
                    )}
                    {entityFields.map((f, i) => (
                      <div key={i} className="flex items-start gap-1.5">
                        {step.config._action_id === 'write_result_to_part' ? (
                          // For write_result_to_part: show a dropdown of known fields + custom
                          <div className="w-36 flex-shrink-0">
                            {PART_FIELD_OPTIONS.some(o => o.value === f.key) ? (
                              <select
                                className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white font-mono focus:outline-none focus:ring-2 focus:ring-teal-200"
                                value={f.key}
                                onChange={e => updateField(i, { key: e.target.value === '__custom__' ? '' : e.target.value })}>
                                {PART_FIELD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                <option value="__custom__">✏️ Custom field…</option>
                              </select>
                            ) : (
                              <div className="flex gap-0.5">
                                <input
                                  className="flex-1 min-w-0 border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-teal-200"
                                  placeholder="field name" value={f.key}
                                  onChange={e => updateField(i, { key: e.target.value })} />
                                <button type="button" title="Pick a standard field"
                                  onClick={() => updateField(i, { key: PART_FIELD_OPTIONS[0].value })}
                                  className="flex-shrink-0 px-1 py-1 text-gray-400 hover:text-teal-600 border border-gray-200 rounded-lg text-[10px]">↑</button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <input className="w-28 border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-mono focus:outline-none flex-shrink-0"
                            placeholder="field name" value={f.key}
                            onChange={e => updateField(i, { key: e.target.value })} />
                        )}
                        <div className="flex-1">
                          <VariablePicker value={f.value} contextVars={contextVars}
                            placeholder="value or variable" onChange={v => updateField(i, { value: v })} />
                        </div>
                        <button type="button" onClick={() => removeField(i)}
                          className="p-1 text-gray-300 hover:text-red-400 flex-shrink-0 mt-1"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              );
            })()}

            {/* ── send_email / send_whatsapp ── */}
            {step.type === 'send_notification' && (
              <>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                    {step.config.channel === 'whatsapp' ? 'Phone number' : 'Recipient email'} <span className="text-red-400">*</span>
                  </label>
                  <VariablePicker value={step.config.recipient ?? ''} contextVars={contextVars}
                    placeholder="e.g. entity.buyer" onChange={v => cfg('recipient', v)} />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                    Message <span className="text-red-400">*</span>
                  </label>
                  <textarea rows={3}
                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs resize-none focus:outline-none focus:ring-1 focus:ring-gray-300"
                    placeholder="Your order has been updated…"
                    value={step.config.message ?? ''}
                    onChange={e => cfg('message', e.target.value)} />
                  <p className="text-[10px] text-gray-400">Tip: use variable paths like <code className="bg-gray-100 px-0.5 rounded font-mono">entity.status</code> in the message.</p>
                </div>
              </>
            )}

            {/* ── run_integration ── */}
            {step.type === 'run_integration' && (
              <>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Integration type</label>
                  <select className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white"
                    value={step.config.integration_type || 'inventory'}
                    onChange={e => {
                      const intType = e.target.value;
                      const nc = { ...step.config, integration_type: intType, source_id: '', action_id: '', input_mapping: {} };
                      onUpdate({ ...step, config: nc });
                      loadIntSources(intType);
                    }}>
                    {['inventory', 'pricing', 'supplier', 'crm', 'shipping', 'quotes', 'dms'].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Source</label>
                  <select className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white"
                    value={step.config.source_id ?? ''} disabled={loadingSrc}
                    onChange={e => onUpdate({ ...step, config: { ...step.config, source_id: e.target.value, action_id: '', input_mapping: {} } })}>
                    <option value="">{loadingSrc ? 'Loading…' : 'Select source…'}</option>
                    {stepSources.map((s: any) => {
                      const isShared = Array.isArray(s.shared_organization_ids) && s.shared_organization_ids.length > 0;
                      const prefix = sourcesSharedMode ? (isShared ? '🌐 ' : '🏢 ') : '';
                      return <option key={s.id} value={String(s.id)}>{prefix}{s.name}</option>;
                    })}
                  </select>
                  {sourcesSharedMode && stepSources.length === 0 && !loadingSrc && (sharedOrgIds?.length ?? 0) > 0 && (
                    <p className="text-[10px] text-amber-600 mt-0.5">No integrations found for the selected organisations.</p>
                  )}
                </div>
                {step.config.source_id && (
                  <div>
                    <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Action</label>
                    <select className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white"
                      value={step.config.action_id ?? ''}
                      onChange={e => {
                        const picked = actions.find((a: any) => String(a.id) === e.target.value);
                        const outputSchema = picked?.outputSchema?.properties ?? picked?.outputSchema ?? null;
                        onUpdate({ ...step, config: { ...step.config, action_id: e.target.value, input_mapping: {}, _output_schema: outputSchema } });
                      }}>
                      <option value="">Select action…</option>
                      {actions.map((a: any) => <option key={a.id} value={String(a.id)}>{a.name}</option>)}
                    </select>
                  </div>
                )}
                {step.config.action_id && (() => {
                  const selAction = actions.find((a: any) => String(a.id) === String(step.config.action_id));
                  if (!selAction) return null;
                  const paramNames: string[] = selAction.inputSchema?.properties
                    ? Object.keys(selAction.inputSchema.properties)
                    : (selAction.inputParameters ?? []).map((p: any) => p.name);
                  const mapping: Record<string, string | null> = step.config.input_mapping ?? {};

                  // Collect output fields for the preview panel
                  const outProps: Record<string, { type?: string; description?: string }> =
                    selAction.outputSchema?.properties ?? selAction.outputSchema ?? {};
                  const outEntries = Object.entries(outProps);

                  return (
                    <>
                      {paramNames.length > 0 && (
                        <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-3 space-y-2.5">
                          <p className="text-[10px] font-semibold text-indigo-700 uppercase tracking-wide">Map action inputs</p>
                          {paramNames.map((param: string) => {
                            const propDef = selAction.inputSchema?.properties?.[param];
                            const required = selAction.inputSchema?.required?.includes(param);
                            return (
                              <div key={param}>
                                <label className="block text-[10px] font-medium text-gray-600 mb-0.5">
                                  <code className="font-mono">{param}</code>
                                  {required && <span className="ml-1 text-red-400 text-[9px]">required</span>}
                                  {propDef?.description && <span className="ml-1 text-gray-400 italic font-normal">{propDef.description}</span>}
                                </label>
                                <VariablePicker value={param in mapping ? mapping[param] : ''} contextVars={contextVars}
                                  placeholder={`value for ${param}`}
                                  onChange={v => {
                                    const newMapping = { ...mapping, [param]: v };
                                    onUpdate({ ...step, config: { ...step.config, input_mapping: newMapping } });
                                  }} />
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {outEntries.length > 0 && (
                        <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 space-y-1.5">
                          <p className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wide flex items-center gap-1">
                            <ArrowRight className="w-3 h-3" /> Output fields available to next steps
                          </p>
                          <p className="text-[9px] text-emerald-600 mb-1">
                            Reference these as <code className="font-mono bg-emerald-100 px-0.5 rounded">node_results.{step.id}.output.<em>field</em></code>
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {outEntries.map(([k, v]) => (
                              <span key={k} title={(v as any)?.description ?? ''}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-emerald-200 bg-white text-[10px] font-mono text-emerald-700">
                                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                  (v as any)?.type === 'integer' || (v as any)?.type === 'number' ? 'bg-blue-400' :
                                  (v as any)?.type === 'boolean' ? 'bg-yellow-400' :
                                  (v as any)?.type === 'array'   ? 'bg-rose-400' :
                                  (v as any)?.type === 'object'  ? 'bg-violet-400' : 'bg-emerald-400'
                                }`} />
                                {k}
                                {(v as any)?.type && <span className="text-[9px] text-emerald-400 ml-0.5">{(v as any).type}</span>}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
                {/* ── on_multiple_results — only shown for supplier lookups ── */}
                {step.config._action_id === 'lookup_supplier' && (
                  <div>
                    <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                      When multiple rows are returned
                    </label>
                    <select
                      className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-green-200"
                      value={step.config.on_multiple_results ?? 'save_all'}
                      onChange={e => cfg('on_multiple_results', e.target.value)}>
                      <option value="save_all">Save all rows — agent / customer picks the best one</option>
                      <option value="first">Use first row automatically</option>
                    </select>
                  </div>
                )}
              </>
            )}

            {/* ── refresh_integration_source ── */}
            {step.type === 'refresh_integration_source' && (
              <>
                <div className="rounded-lg border border-cyan-200 bg-cyan-50/60 px-3 py-2 text-[11px] text-cyan-800">
                  Re-loads the data behind a CSV integration source from a fresh file in GCS. Point <span className="font-semibold">GCS file</span> at an upstream step's output (e.g. <code className="font-mono">node_results.&lt;node&gt;.output.gcs_path</code>).
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Integration type</label>
                  <select className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white"
                    value={step.config.integration_type || 'pricing'}
                    onChange={e => { const t = e.target.value; onUpdate({ ...step, config: { ...step.config, integration_type: t, source_id: '' } }); loadIntSources(t); }}>
                    {['pricing', 'supplier', 'inventory', 'quotes', 'crm', 'shipping', 'dms'].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Source to refresh <span className="text-red-400">*</span></label>
                  <select className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white" value={step.config.source_id ?? ''} disabled={loadingSrc}
                    onChange={e => cfg('source_id', e.target.value)}>
                    <option value="">{loadingSrc ? 'Loading…' : 'Select source…'}</option>
                    {stepSources.map((s: any) => {
                      const isShared = Array.isArray(s.shared_organization_ids) && s.shared_organization_ids.length > 0;
                      const prefix = sourcesSharedMode ? (isShared ? '🌐 ' : '🏢 ') : '';
                      return <option key={s.id} value={String(s.id)}>{prefix}{s.name}</option>;
                    })}
                  </select>
                  {sourcesSharedMode && stepSources.length === 0 && !loadingSrc && (sharedOrgIds?.length ?? 0) > 0 && (
                    <p className="text-[10px] text-amber-600 mt-0.5">No integrations found for the selected organisations.</p>
                  )}
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">GCS file <span className="text-red-400">*</span></label>
                  <VariablePicker value={step.config.gcs_path ?? ''} contextVars={contextVars}
                    placeholder="node_results.<node>.output.gcs_path or gs://bucket/file.csv"
                    onChange={v => cfg('gcs_path', v)} />
                  <p className="text-[10px] text-gray-400 mt-0.5">Must resolve to a <code className="font-mono">gs://</code> CSV URI.</p>
                </div>
                <div className="flex items-center justify-between rounded-lg border border-cyan-200 bg-cyan-50/60 px-3 py-2">
                  <div>
                    <p className="text-[10px] font-semibold text-cyan-800">Mark source active after refresh</p>
                  </div>
                  <button type="button"
                    onClick={() => cfg('set_active', !(step.config.set_active ?? true))}
                    className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 transition-colors duration-200 focus:outline-none ${(step.config.set_active ?? true) ? 'bg-cyan-500 border-cyan-500' : 'bg-gray-200 border-gray-200'}`}>
                    <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-200 ${(step.config.set_active ?? true) ? 'translate-x-4' : 'translate-x-0'}`} />
                  </button>
                </div>
              </>
            )}

            {/* ── enrich_order ── */}
            {step.type === 'enrich_order' && (() => {
              const activeCount = stepSources.filter((s: any) => s.status === 'active').length;
              return (
                <>
                  <div className="rounded-lg border border-teal-200 bg-teal-50/60 px-3 py-2 text-[11px] text-teal-800">
                    Looks up this order's vehicle in your reference documents (uploaded client documents,
                    managed in Admin → Enrichment) and writes the matching data — local model name,
                    maintenance schedule, part prices, warranty terms — into the order's extra data.
                    The chat then answers from it and the header shows the local model name.
                    Results are cached per vehicle model, so repeat models resolve instantly.
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Data searched</p>
                    {loadingSrc ? (
                      <p className="text-[11px] text-gray-400 mt-0.5">Loading available documents…</p>
                    ) : activeCount === 0 ? (
                      <p className="text-[11px] text-amber-600 mt-0.5">
                        No active documents available yet — upload a client document under Admin → Enrichment first.
                      </p>
                    ) : (
                      <p className="text-[11px] text-gray-600 mt-0.5">
                        All active documents are searched automatically —{' '}
                        <span className="font-semibold text-teal-700">{activeCount}</span> available.
                      </p>
                    )}
                  </div>
                </>
              );
            })()}

            {/* ── foreach_array / for_each_part ── */}
            {step.type === 'foreach_array' && (              <>
                {step.config._action_id === 'for_each_array' && (
                  <div>
                    <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                      Array to loop over <span className="text-red-400">*</span>
                    </label>
                    <VariablePicker value={step.config.array_source ?? ''} contextVars={contextVars}
                      placeholder="e.g. entity.parts_in_order" onChange={v => cfg('array_source', v)} />
                  </div>
                )}
                {step.config._action_id === 'for_each_part' && (
                  <div className="rounded-lg bg-orange-50/80 border border-orange-200 px-3 py-2 text-[11px] text-orange-700">
                    Iterates over <code className="font-mono bg-orange-100 px-0.5 rounded">entity.parts_in_order</code>.
                  </div>
                )}
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Loop variable name</label>
                  <input className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-orange-200"
                    placeholder="item" value={step.config.item_var ?? 'item'}
                    onChange={e => cfg('item_var', e.target.value)} />
                </div>
                <div className="flex items-center justify-between rounded-lg border border-orange-200 bg-orange-50/60 px-3 py-2">
                  <div>
                    <p className="text-[10px] font-semibold text-orange-800">Run iterations in parallel</p>
                    <p className="text-[10px] text-orange-500 mt-0.5">All items are processed concurrently. Use when order doesn't matter.</p>
                  </div>
                  <button type="button"
                    onClick={() => cfg('parallel', !(step.config.parallel ?? false))}
                    className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 transition-colors duration-200 focus:outline-none ${step.config.parallel ? 'bg-orange-500 border-orange-500' : 'bg-gray-200 border-gray-200'}`}>
                    <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-200 ${step.config.parallel ? 'translate-x-4' : 'translate-x-0'}`} />
                  </button>
                </div>
              </>
            )}

            {/* ── check_condition ── */}
            {step.type === 'condition' && (
              <>
                <ConditionRowsEditor config={step.config} contextVars={contextVars} cfgMany={cfgMany} />
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">True path label</label>
                    <input className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-amber-200"
                      value={step.config.true_label ?? 'Yes'} onChange={e => cfg('true_label', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">False path label</label>
                    <input className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-amber-200"
                      value={step.config.false_label ?? 'No'} onChange={e => cfg('false_label', e.target.value)} />
                  </div>
                </div>
                <div className="rounded-lg border border-amber-100 bg-amber-50/50 px-3 py-2">
                  <p className="text-[10px] text-amber-600">
                    💡 After adding this step, draw <strong>two outgoing edges</strong> from this node — one for the YES path
                    (add edge condition: <code className="font-mono bg-amber-100 px-0.5 rounded">node_results.{step.id}.output.result</code> = <code className="font-mono bg-amber-100 px-0.5 rounded">true</code>)
                    and one for the NO path.
                  </p>
                </div>
              </>
            )}

            {/* ── search_parts ── */}
            {step.type === 'call_workflow' && (
              <CallWorkflowConfig config={step.config} contextVars={contextVars} cfg={cfg} />
            )}

            {step.type === 'set_output' && (
              <SetOutputConfig config={step.config} contextVars={contextVars} cfg={cfg} />
            )}

            {step.type === 'search_parts' && (
              <div className="rounded-xl border border-sky-200 bg-sky-50/50 p-3 space-y-3">
                <p className="text-[10px] font-semibold text-sky-700 uppercase tracking-wide flex items-center gap-1">
                  <Search className="w-3 h-3" /> Parts search configuration
                </p>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                    VIN
                  </label>
                  <VariablePicker
                    value={step.config.vin ?? ''}
                    contextVars={contextVars}
                    placeholder="e.g. entity.vin or 1HGCM82633A004352"
                    onChange={v => cfg('vin', v)}
                  />
                  <p className="text-[10px] text-gray-400 mt-0.5">17-character VIN — dot-path or static value.</p>
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                    Search query
                  </label>
                  <VariablePicker
                    value={step.config.query ?? ''}
                    contextVars={contextVars}
                    placeholder='e.g. event_payload.query or "brake pads"'
                    onChange={v => cfg('query', v)}
                  />
                  <p className="text-[10px] text-gray-400 mt-0.5">Free-text parts query — dot-path or static text.</p>
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                    Language
                  </label>
                  <input
                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-sky-200 bg-white"
                    placeholder="en"
                    value={step.config.language ?? 'en'}
                    onChange={e => cfg('language', e.target.value)}
                  />
                  <p className="text-[10px] text-gray-400 mt-0.5">Response language code (en, es, he, …).</p>
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                    Sourcer
                  </label>
                  <VariablePicker
                    value={step.config.sourcer ?? ''}
                    contextVars={contextVars}
                    placeholder='e.g. event_payload.sourcer or "acme-supplier"'
                    onChange={v => cfg('sourcer', v)}
                  />
                  <p className="text-[10px] text-gray-400 mt-0.5">Optional. Labels the search as a guest request attributed to this sourcer name.</p>
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                    Organization ID
                  </label>
                  <VariablePicker
                    value={step.config.organization_id ?? ''}
                    contextVars={contextVars}
                    placeholder='e.g. entity.organization_id or a static UUID'
                    onChange={v => cfg('organization_id', v)}
                  />
                  <p className="text-[10px] text-gray-400 mt-0.5">Optional. Override the organization used for the parts search.</p>
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                    Notes
                  </label>
                  <VariablePicker
                    value={step.config.notes ?? ''}
                    contextVars={contextVars}
                    placeholder='e.g. "Automated search from workflow" or a dot-path'
                    onChange={v => cfg('notes', v)}
                  />
                  <p className="text-[10px] text-gray-400 mt-0.5">Optional. Free-text note attached to this search (visible in search history).</p>
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                    Timeout (seconds)
                  </label>
                  <input
                    type="number"
                    min={10}
                    max={600}
                    step={10}
                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-sky-200 bg-white"
                    placeholder="120"
                    value={step.config.timeout ?? 120}
                    onChange={e => cfg('timeout', e.target.value === '' ? null : Number(e.target.value))}
                  />
                  <p className="text-[10px] text-gray-400 mt-0.5">Max seconds to wait for the agent (10–600). Leave blank to use the default (120 s).</p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id={`create_order_${step.id}`}
                    checked={!!step.config.create_order}
                    onChange={e => cfg('create_order', e.target.checked)}
                    className="rounded border-gray-300 text-sky-600 focus:ring-sky-200"
                  />
                  <label htmlFor={`create_order_${step.id}`} className="text-xs text-gray-700 cursor-pointer select-none">
                    Create order after search
                  </label>
                  <span className="text-[10px] text-gray-400">(stores results as a draft <code className="font-mono bg-sky-100 px-0.5 rounded">UserOrders</code> record; id available as <code className="font-mono bg-sky-100 px-0.5 rounded">output.search_order_id</code>)</span>
                </div>
                {/* Parallel queries */}
                <div className="border-t border-sky-200 pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-[10px] font-semibold text-sky-700 uppercase tracking-wide flex items-center gap-1">
                      ⚡ Parallel queries
                    </label>
                    <button
                      type="button"
                      className="text-[10px] px-2 py-0.5 rounded-md bg-sky-100 text-sky-700 hover:bg-sky-200 transition-colors font-medium"
                      onClick={() => {
                        const current: any[] = step.config.queries ?? [];
                        cfg('queries', [...current, { vin: '', query: '', sourcer: '' }]);
                      }}
                    >
                      + Add query
                    </button>
                  </div>
                  <p className="text-[10px] text-gray-400 mb-2">
                    Add multiple queries to run them all concurrently. When set, takes precedence over the single VIN/query above.
                    Results are merged under <code className="font-mono bg-sky-100 px-0.5 rounded">output.results</code> and also available per-query in <code className="font-mono bg-sky-100 px-0.5 rounded">output.results_by_query</code>.
                  </p>
                  {(step.config.queries ?? []).map((q: any, qi: number) => (
                    <div key={qi} className="rounded-lg border border-sky-200 bg-white p-2 mb-2 space-y-1.5">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-semibold text-sky-600">Query {qi + 1}</span>
                        <button
                          type="button"
                          className="text-[10px] text-red-400 hover:text-red-600"
                          onClick={() => {
                            const updated = (step.config.queries ?? []).filter((_: any, i: number) => i !== qi);
                            cfg('queries', updated);
                          }}
                        >
                          Remove
                        </button>
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">VIN</label>
                        <VariablePicker
                          value={q.vin ?? ''}
                          contextVars={contextVars}
                          placeholder="e.g. entity.vin or static VIN (leave blank to use global)"
                          onChange={v => {
                            const updated = [...(step.config.queries ?? [])];
                            updated[qi] = { ...updated[qi], vin: v };
                            cfg('queries', updated);
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">Query</label>
                        <VariablePicker
                          value={q.query ?? ''}
                          contextVars={contextVars}
                          placeholder='e.g. "brake pads" or event_payload.query'
                          onChange={v => {
                            const updated = [...(step.config.queries ?? [])];
                            updated[qi] = { ...updated[qi], query: v };
                            cfg('queries', updated);
                          }}
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] text-gray-500 mb-0.5">Sourcer (optional)</label>
                        <VariablePicker
                          value={q.sourcer ?? ''}
                          contextVars={contextVars}
                          placeholder='e.g. "acme-supplier"'
                          onChange={v => {
                            const updated = [...(step.config.queries ?? [])];
                            updated[qi] = { ...updated[qi], sourcer: v };
                            cfg('queries', updated);
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="rounded-lg border border-sky-100 bg-sky-50/60 px-3 py-2">
                  <p className="text-[10px] text-sky-700">
                    💡 Results are available downstream as{' '}
                    <code className="font-mono bg-sky-100 px-0.5 rounded">node_results.{step.id}.output.results</code>{' '}
                    (array of parts) and{' '}
                    <code className="font-mono bg-sky-100 px-0.5 rounded">node_results.{step.id}.output.not_found_parts</code>.
                  </p>
                </div>
              </div>
            )}

            {/* On error */}
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">If this step fails…</label>
              <select className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none"
                value={step.on_error ?? 'abort'}
                onChange={e => onUpdate({ ...step, on_error: e.target.value })}>
                <option value="abort">Stop the whole workflow</option>
                <option value="continue">Skip this step and continue</option>
                <option value="set_status">Set order status</option>
              </select>
              {step.on_error === 'set_status' && (
                <select className="mt-1 w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white"
                  value={step.error_status ?? ''}
                  onChange={e => onUpdate({ ...step, error_status: e.target.value })}>
                  <option value="">Select status…</option>
                  {states.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Loop body: nested steps rendered below the foreach card ── */}
      {step.type === 'foreach_array' && (
        <div className="ml-6 mt-0 border-l-2 border-orange-200 pl-4 space-y-2 pb-2">
          {/* Body header */}
          <div className="flex items-center gap-1.5 pt-2 pb-0.5">
            <div className="w-3 h-3 rounded-full bg-orange-200 flex-shrink-0" />
            <p className="text-[10px] font-bold uppercase tracking-widest text-orange-500">
              Loop body — runs for each {step.config.item_var || 'item'}
            </p>
          </div>

          {/* foreach_item vars banner */}
          {(() => {
            const loopVars = buildForeachItemVars(step);
            return loopVars.length > 0 ? (
              <div className="rounded-lg border border-orange-200 bg-orange-50/60 px-2 py-1.5 flex flex-wrap gap-1">
                {loopVars.slice(0, 10).map(v => (
                  <span key={v.path} title={v.description}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-orange-200 bg-white text-[9px] font-mono text-orange-700">
                    {v.path.split('.').slice(1).join('.')}
                  </span>
                ))}
                <span className="text-[9px] text-orange-400 self-center">← available in loop</span>
              </div>
            ) : null;
          })()}

          {/* Nested step cards */}
          {(step.children ?? []).map((child, ci) => {
            const loopVars = buildForeachItemVars(step);
            const childCtxVars = [...allVars, ...loopVars,
              ...buildNodeResultVars(child.id,
                [...(step.children ?? [])],
                (step.children ?? []).slice(0, ci).map((_, j) => ({
                  id: `le_${j}`, source: (step.children ?? [])[j].id,
                  target: (step.children ?? [])[j + 1]?.id ?? child.id,
                })).filter(e => e.target)
              )
            ];
            return (
              <StepCard
                key={child.id}
                step={child}
                index={ci}
                totalSteps={step.children?.length ?? 0}
                states={states}
                contextVars={childCtxVars}
                foreachItemVars={loopVars}
                sources={[]}
                isExpanded={loopExpandedIdx === ci}
                onToggle={() => setLoopExpandedIdx(loopExpandedIdx === ci ? null : ci)}
                onUpdate={updated => {
                  const newChildren = (step.children ?? []).map((c, i) => i === ci ? updated : c);
                  onUpdate({ ...step, children: newChildren });
                }}
                onDelete={() => {
                  const newChildren = (step.children ?? []).filter((_, i) => i !== ci);
                  onUpdate({ ...step, children: newChildren });
                }}
                onMoveUp={() => {
                  if (ci === 0) return;
                  const nc = [...(step.children ?? [])];
                  [nc[ci], nc[ci - 1]] = [nc[ci - 1], nc[ci]];
                  onUpdate({ ...step, children: nc });
                  setLoopExpandedIdx(ci - 1);
                }}
                onMoveDown={() => {
                  const nc = [...(step.children ?? [])];
                  if (ci >= nc.length - 1) return;
                  [nc[ci], nc[ci + 1]] = [nc[ci + 1], nc[ci]];
                  onUpdate({ ...step, children: nc });
                  setLoopExpandedIdx(ci + 1);
                }}
                counter={counter}
                entityType={entityType}
                allVars={allVars}
                orgId={orgId}
                sharedOrgIds={sharedOrgIds}
              />
            );
          })}

          {/* Add step inside loop */}
          <button type="button" onClick={() => setShowLoopPicker(true)}
            className="w-full flex items-center justify-center gap-1.5 py-2 border-2 border-dashed border-orange-200 rounded-xl text-xs text-orange-500 hover:border-orange-400 hover:text-orange-700 hover:bg-orange-50/50 transition-colors">
            <Plus className="w-3.5 h-3.5" /> Add step inside loop
          </button>

          {/* Loop body end marker */}
          <div className="flex items-center gap-1.5 pb-0.5">
            <div className="w-3 h-3 rounded-full bg-orange-200 flex-shrink-0" />
            <p className="text-[10px] text-orange-400">End of loop</p>
          </div>
        </div>
      )}

      {showLoopPicker && (
        <ActionPicker
          groups={LOOP_ACTION_GROUPS}
          title={`Add step inside loop`}
          subtitle={`This step will run for each ${step.config.item_var || 'item'}`}
          onPick={action => {
            if (!counter) return;
            const id = `node_${++counter.current}`;
            const loopVars = buildForeachItemVars(step);
            const defaultCfg = action.defaultConfig(entityType, [...allVars, ...loopVars]);
            const newChild: StepNode = {
              id, label: action.label, type: action.nodeType,
              config: { ...defaultCfg, _action_id: action.id },
              on_error: 'abort',
              _action_id: action.id,
            };
            const newChildren = [...(step.children ?? []), newChild];
            onUpdate({ ...step, children: newChildren });
            setLoopExpandedIdx(newChildren.length - 1);
          }}
          onClose={() => setShowLoopPicker(false)}
          orgId={orgId}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Convert simplified steps → DAG nodes + edges
// foreach_array children are connected FROM the foreach node (not linear chain)
// ---------------------------------------------------------------------------

function stepsToDAG(steps: StepNode[]): { nodes: DAGNode[]; edges: DAGEdge[] } {
  const nodes: DAGNode[] = [];
  const edges: DAGEdge[] = [];

  function flattenStep(s: StepNode, xBase: number, yBase: number) {
    nodes.push({
      id: s.id,
      label: s.label,
      type: s.type,
      config: s.config,
      on_error: s.on_error ?? 'abort',
      error_status: s.error_status,
      position: s.position ?? { x: xBase, y: yBase },
    });
    // For foreach_array, flatten children and connect them FROM the foreach node
    if (s.type === 'foreach_array' && s.children?.length) {
      s.children.forEach((child, ci) => {
        flattenStep(child, xBase + 260, yBase + ci * 140);
        if (ci === 0) {
          // First child connects from foreach node
          edges.push({ id: `e_${s.id}_${child.id}`, source: s.id, target: child.id });
        } else {
          // Chain children linearly
          const prev = s.children![ci - 1];
          if (prev.type === 'condition') {
            edges.push({
              id: `e_${prev.id}_${child.id}`,
              source: prev.id, target: child.id,
              label: prev.config.true_label || 'Yes',
              condition: { field: `node_results.${prev.id}.output.result`, operator: 'eq', value: 'true' },
            });
          } else {
            edges.push({ id: `e_${prev.id}_${child.id}`, source: prev.id, target: child.id });
          }
        }
      });
    }
  }

  steps.forEach((s, i) => flattenStep(s, 120, 80 + i * 160));

  // Wire top-level steps linearly (skip foreach → its first child is already wired inside)
  for (let i = 0; i < steps.length - 1; i++) {
    const src = steps[i];
    const tgt = steps[i + 1];
    if (src.type === 'condition') {
      edges.push({
        id: `e_${src.id}_${tgt.id}`,
        source: src.id, target: tgt.id,
        label: src.config.true_label || 'Yes',
        condition: { field: `node_results.${src.id}.output.result`, operator: 'eq', value: 'true' },
      });
    } else {
      edges.push({ id: `e_${src.id}_${tgt.id}`, source: src.id, target: tgt.id });
    }
  }

  return { nodes, edges };
}

function dagToSteps(nodes: DAGNode[], edges: DAGEdge[]): StepNode[] {
  // Find foreach nodes and their body (BFS from foreach)
  const foreachIds = new Set(nodes.filter(n => n.type === 'foreach_array').map(n => n.id));
  const bodyParent: Record<string, string> = {};  // childId → foreachId

  for (const fn of Array.from(foreachIds)) {
    const visited = new Set<string>();
    const q = [fn];
    while (q.length) {
      const cur = q.shift()!;
      edges.filter(e => e.source === cur && e.target !== fn).forEach(e => {
        if (!visited.has(e.target) && !foreachIds.has(e.target)) {
          visited.add(e.target);
          bodyParent[e.target] = fn;
          q.push(e.target);
        }
      });
    }
  }

  const bodyIds = new Set(Object.keys(bodyParent));
  const topLevel = nodes.filter(n => !bodyIds.has(n.id));

  // Build foreach children maps
  const childrenMap: Record<string, StepNode[]> = {};
  for (const n of nodes) {
    if (bodyIds.has(n.id)) {
      const parent = bodyParent[n.id];
      childrenMap[parent] = childrenMap[parent] ?? [];
      childrenMap[parent].push({ ...n, _action_id: n.config._action_id });
    }
  }

  return topLevel.map(n => ({
    ...n,
    _action_id: n.config._action_id,
    children: childrenMap[n.id],
  }));
}

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// SimplifiedWorkflowForm — the main simplified builder
// ---------------------------------------------------------------------------

interface SimplifiedWorkflowFormProps {
  initial?: DAGDefinition | null;
  states: WorkflowState[];
  eventTypes: EventTypeInfo[];
  onSave: (data: any) => Promise<void>;
  onCancel: () => void;
  orgId?: number;
  /** Shared (no-owner) definitions — scopes version-history calls. */
  isShared?: boolean;
}

const SimplifiedWorkflowForm: React.FC<SimplifiedWorkflowFormProps> = ({
  initial, states, eventTypes, onSave, onCancel, orgId, isShared,
}) => {
  const [name, setName]         = useState(initial?.name ?? '');
  const [desc, setDesc]         = useState(initial?.description ?? '');
  const [triggerType, setTrig]  = useState(initial?.trigger?.type ?? '');
  const [cronConfig, setCronConfig] = useState<CronConfig>({
    schedule:    initial?.trigger?.schedule    ?? '',
    timezone:    initial?.trigger?.timezone    ?? 'UTC',
    entity_type: initial?.trigger?.entity_type ?? '',
    entity_id:   initial?.trigger?.entity_id   ?? '',
    payload_raw: initial?.trigger?.payload
      ? JSON.stringify(initial.trigger.payload, null, 2)
      : '',
  });
  // Event triggers: filters that narrow which events start this workflow
  const [triggerFilters, setTriggerFilters] = useState<TriggerFilterRow[]>(
    () => filtersToRows(initial?.trigger?.filters),
  );
  // Subflow trigger: inputs this workflow expects from the calling parent
  const [subflowInputs, setSubflowInputs] = useState<SubflowExpectedInput[]>(
    (initial?.trigger?.expected_inputs ?? []).map(i => ({ key: i.key, description: i.description ?? '' })),
  );
  // DAG canvas: nodes + edges
  const [dagNodes, setDagNodes] = useState<DAGNode[]>(initial?.nodes ?? []);
  const [dagEdges, setDagEdges] = useState<DAGEdge[]>(initial?.edges ?? []);

  // Secrets — Google Secret Manager references exposed as secrets.<name>
  const [secrets, setSecrets] = useState<SecretRef[]>(initial?.secrets ?? []);

  // Multi-org sharing
  const [sharedOrgIds, setSharedOrgIds] = useState<number[]>(initial?.organization_ids ?? []);
  // Shared workflows: Organization row shown = run only for sharedOrgIds; hidden =
  // all orgs (trigger.all_organizations). Older shared workflows saved without the
  // flag open with the row shown, so editing one never silently widens it.
  const [orgRowOpen, setOrgRowOpen] = useState(
    () => (initial?.organization_ids?.length ?? 0) > 0 || (!!initial && !initial.trigger?.all_organizations),
  );
  const [allOrgs, setAllOrgs]           = useState<{ id: number; name: string }[]>([]);

  useEffect(() => {
    adminService.getOrganizations()
      .then(r => setAllOrgs(r.data.organizations ?? []))
      .catch(() => {/* silently ignore — field just won't show options */});
  }, []);

  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  // Findings from the backend workflow checks, shown before saving
  const [checkWarnings, setCheckWarnings] = useState<DAGCheckWarning[] | null>(null);
  // Version history
  const [historyOpen, setHistoryOpen]       = useState(false);
  const [versions, setVersions]             = useState<DAGVersionSummary[] | null>(null);
  const [historyError, setHistoryError]     = useState('');
  const [loadingVersion, setLoadingVersion] = useState<number | null>(null);
  const [loadedVersion, setLoadedVersion]   = useState<number | null>(null);
  // Test run (nothing is written)
  const [testOrderRef, setTestOrderRef] = useState('');
  const [testOrder, setTestOrder]       = useState<DryRunOrder | null>(null);
  const [testItemId, setTestItemId]     = useState('');
  const [testBusy, setTestBusy]         = useState<'' | 'find' | 'run'>('');
  const [testError, setTestError]       = useState('');
  const [testResult, setTestResult]     = useState<DryRunResult | null>(null);
  const [openStep, setOpenStep]         = useState<string | null>(null);
  const counter = useRef((() => {
    const allNodes = initial?.nodes ?? [];
    return allNodes.reduce((max, n) => {
      const m = n.id.match(/^node_(\d+)$/);
      return m ? Math.max(max, parseInt(m[1], 10)) : max;
    }, 0) + 1;
  })());

  const selEventType = eventTypes.find(et => et.type === triggerType);
  const { eventVars, entityType, entityVars, extraVars: triggerExtraVars } = triggerType
    ? buildContextVars(triggerType, eventTypes)
    : { eventVars: [], entityType: 'order', entityVars: [], extraVars: [] };

  // Secret variables exposed as secrets.<name>
  const secretVars: ContextVar[] = secrets
    .filter(s => s.name.trim())
    .map(s => ({ path: `secrets.${s.name}`, type: 'string', description: `Secret — ${s.secret || 'Secret Manager'}` }));

  // Subflow trigger: declared expected inputs become event_payload.<key> vars
  const subflowInputVars: ContextVar[] = triggerType === 'subflow'
    ? subflowInputs
        .filter(i => i.key.trim())
        .map(i => ({
          path: `event_payload.${i.key.trim()}`,
          type: 'any',
          description: i.description || 'Input passed by the parent workflow',
        }))
    : [];

  // All context vars available for the full workflow
  const allVars: ContextVar[] = [...eventVars, ...subflowInputVars, ...entityVars, ...triggerExtraVars, ...secretVars];

  /** Strip frontend-only fields before sending nodes to the backend. The
   *  canvas position is kept (top-level `position`) so the layout survives a
   *  reload; older backends simply ignore it. */
  const sanitizeNodes = (nodes: DAGNode[]): DAGNode[] =>
    nodes.map(({ id, label, type, config, on_error, error_status, position }) => {
      const pos = position ?? (config as any)?.position;   // legacy: config.position
      return {
        id, label, type,
        config: (({ position: _p, _action_id: _a, _status_value: _s, _output_schema: _o, ...rest }) => rest)(config as any),
        on_error,
        error_status,
        ...(pos && Number.isFinite(pos.x) && Number.isFinite(pos.y)
          ? { position: { x: Math.round(pos.x), y: Math.round(pos.y) } } : {}),
      };
    });

  const findTestOrder = async () => {
    if (!testOrderRef.trim()) return;
    setTestBusy('find'); setTestError(''); setTestOrder(null); setTestItemId('');
    try {
      const r = await workflowService.findDryRunOrder(testOrderRef.trim());
      setTestOrder(r.data);
      const items = r.data.items ?? [];
      if (items.length) setTestItemId(items[items.length - 1].item_id ?? '');
    } catch (e: any) {
      setTestError(e?.response?.status === 404 ? 'Order not found.' : 'Couldn\'t look up that order.');
    } finally { setTestBusy(''); }
  };

  const runTest = async () => {
    if (!testOrder) return;
    setTestBusy('run'); setTestError(''); setOpenStep(null);
    try {
      const triggerForTest: Record<string, any> = { type: triggerType };
      const r = await workflowService.dryRunDAG({
        definition_id: initial?.id || undefined,
        trigger: triggerForTest,
        nodes: sanitizeNodes(dagNodes),
        edges: dagEdges,
        secrets: secrets.filter(x => x.name.trim() && x.secret.trim()),
        order_id: testOrder.order_id,
        item_id: testItemId || undefined,
      }, orgId, isShared);
      setTestResult(r.data);
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setTestError(typeof detail === 'string' ? detail : 'The test run couldn\'t start (the server may need updating).');
    } finally { setTestBusy(''); }
  };

  // Real outputs + per-step outcome of the last test run (memoized: the
  // canvas redraws when these change identity)
  const { sampleOutputs, runStates } = useMemo(() => {
    const samples: Record<string, any> = {};
    const states: Record<string, string> = {};
    if (testResult) {
      Object.entries(testResult.node_results ?? {}).forEach(([id, r]: [string, any]) => {
        // Loop iterations are stored as "<id>[<n>]": the first one stands in for the step
        const m = id.match(/^(.*)\[(\d+)\]$/);
        if (r && r.output !== undefined) {
          if (!m) samples[id] = r.output;
          else if (m[2] === '0' && samples[m[1]] === undefined) samples[m[1]] = r.output;
        }
      });
      (testResult.steps ?? []).forEach(st => { states[st.id] = st.state; });
    }
    return { sampleOutputs: samples, runStates: states };
  }, [testResult]);

  const openHistory = async () => {
    if (!initial?.id) return;
    setHistoryOpen(true); setVersions(null); setHistoryError('');
    try {
      const r = await workflowService.listDAGVersions(initial.id, orgId, isShared);
      setVersions(Array.isArray(r.data) ? r.data : []);
    } catch {
      setVersions([]);
      setHistoryError('Version history isn\'t available yet (the server may need updating).');
    }
  };

  /** Load a saved version into the editor. Nothing changes until the user saves. */
  const openVersion = async (version: number) => {
    if (!initial?.id) return;
    setLoadingVersion(version);
    try {
      const r = await workflowService.getDAGVersion(initial.id, version, orgId, isShared);
      const snap = (r.data as DAGVersion).snapshot ?? {};
      const t: any = snap.trigger ?? {};
      setName(snap.name ?? name);
      setDesc(snap.description ?? '');
      setTrig(t.type ?? '');
      setCronConfig({
        schedule: t.schedule ?? '', timezone: t.timezone ?? 'UTC',
        entity_type: t.entity_type ?? '', entity_id: t.entity_id ?? '',
        payload_raw: t.payload ? JSON.stringify(t.payload, null, 2) : '',
      });
      setSubflowInputs((t.expected_inputs ?? []).map((i: any) => ({ key: i.key, description: i.description ?? '' })));
      setTriggerFilters(filtersToRows(t.filters));
      const ns = snap.nodes ?? [];
      setDagNodes(ns);
      setDagEdges(snap.edges ?? []);
      setSecrets(snap.secrets ?? []);
      setSharedOrgIds(snap.organization_ids ?? []);
      setOrgRowOpen((snap.organization_ids?.length ?? 0) > 0 || !t.all_organizations);
      // Keep new step ids above every id in the loaded version
      const maxId = ns.reduce((m, n) => { const x = n.id.match(/^node_(\d+)$/); return x ? Math.max(m, +x[1]) : m; }, 0);
      counter.current = Math.max(counter.current, maxId + 1);
      setLoadedVersion(version);
      setHistoryOpen(false);
    } catch {
      setHistoryError(`Couldn't load version ${version}.`);
    } finally {
      setLoadingVersion(null);
    }
  };

  const handleSave = async (ignoreWarnings = false) => {
    if (!name.trim()) { setError('Please give this workflow a name.'); return; }
    if (!triggerType) { setError('Please select a trigger event.'); return; }
    if (triggerType === 'cron' && !cronConfig.schedule.trim()) {
      setError('Please provide a cron schedule expression.'); return;
    }
    if (dagNodes.length === 0) { setError('Add at least one node.'); return; }
    // Filters only apply to event triggers (cron and subflow runs aren't
    // dispatched through the event bus).
    let filters: Record<string, any> = {};
    if (triggerType !== 'cron' && triggerType !== 'subflow') {
      try { filters = rowsToFilters(triggerFilters); }
      catch (e) { setError((e as Error).message); return; }
    }
    setSaving(true); setError('');

    // Workflow checks: warnings never block, but are shown once so the user
    // can fix them or save anyway. Older backends return no `warnings`, and a
    // failed check call never prevents saving.
    if (!ignoreWarnings) {
      try {
        const r = await workflowService.validateDAG(sanitizeNodes(dagNodes), dagEdges);
        const found = r.data?.warnings ?? [];
        if (found.length > 0) { setCheckWarnings(found); setSaving(false); return; }
      } catch { /* checks unavailable — save as before */ }
    }
    setCheckWarnings(null);

    // Build trigger object — include cron fields when applicable
    const triggerObj: Record<string, any> = { type: triggerType };
    if (Object.keys(filters).length > 0) triggerObj.filters = filters;
    if (isShared && !orgRowOpen) triggerObj.all_organizations = true;
    if (triggerType === 'cron') {
      triggerObj.schedule = cronConfig.schedule.trim();
      triggerObj.timezone = cronConfig.timezone || 'UTC';
      if (cronConfig.entity_type) triggerObj.entity_type = cronConfig.entity_type;
      if (cronConfig.entity_id)   triggerObj.entity_id   = cronConfig.entity_id;
      if (cronConfig.payload_raw.trim() && cronConfig.payload_raw.trim() !== '{}') {
        try { triggerObj.payload = JSON.parse(cronConfig.payload_raw); } catch { /* ignore */ }
      }
    }
    if (triggerType === 'subflow') {
      const declared = subflowInputs
        .map(i => ({ key: i.key.trim(), description: i.description.trim() }))
        .filter(i => i.key);
      if (declared.length) triggerObj.expected_inputs = declared;
    }

    try {
      await onSave({ name: name.trim(), description: desc || undefined,
        trigger: triggerObj, nodes: sanitizeNodes(dagNodes), edges: dagEdges,
        // [] must be sent: the PATCH treats a missing field as "keep", so a cleared list never cleared.
        organization_ids: isShared ? (orgRowOpen ? sharedOrgIds : []) : undefined,
        secrets: secrets.filter(s => s.name.trim() && s.secret.trim()),
      });
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to save. Please try again.');
      setSaving(false);
    }
  };

  const addDagNode = (action: ExplicitAction, position: { x: number; y: number }) => {
    const id = `node_${++counter.current}`;
    const defaultCfg = action.defaultConfig(entityType, [...eventVars, ...entityVars]);
    const newNode: DAGNode & { position: any } = {
      id, label: action.label, type: action.nodeType,
      config: { ...defaultCfg, _action_id: action.id },
      on_error: 'abort', position,
    };
    setDagNodes(ns => [...ns, newNode]);
  };

  const dagContextVars: ContextVar[] = [...allVars, ...buildNodeResultVars('', dagNodes, dagEdges, sampleOutputs)];

  return (
    <div className="flex flex-col gap-4 min-h-full pb-0 max-w-4xl mx-auto w-full">

      {/* ── Name + Trigger ── */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-gray-900 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">1</div>
          <p className="text-sm font-semibold text-gray-900">Name & Trigger</p>
        </div>

        {error && (
          <div className="flex items-center gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" /> {error}
          </div>
        )}
        {loadedVersion !== null && (
          <div className="flex items-center gap-2 text-xs text-sky-800 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2">
            <Clock className="w-3.5 h-3.5 flex-shrink-0" />
            Showing saved version {loadedVersion}. Save to make it the current workflow (as a new version), or Cancel to leave everything as it was.
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Workflow name *</label>
            <input className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
              placeholder="e.g. Notify on order complete" value={name} onChange={e => setName(e.target.value)} />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Description</label>
            <input className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
              placeholder="Optional note…" value={desc} onChange={e => setDesc(e.target.value)} />
          </div>
        </div>

        <div>
          <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
            <Zap className="w-3 h-3 inline mr-1 text-amber-500" />When does this run? *
          </label>
          <select className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-200"
            value={triggerType} onChange={e => { setTrig(e.target.value); setTriggerFilters([]); }}>
            <option value="">— select an event —</option>
            {eventTypes.map(et => (
              <option key={et.type} value={et.type}>{et.label}</option>
            ))}
          </select>
          {selEventType?.description && (
            <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
              <Info className="w-3 h-3" /> {selEventType.description}
            </p>
          )}

          {triggerType === 'cron' && (
            <CronConfigPanel value={cronConfig} onChange={setCronConfig} />
          )}

          {triggerType === 'subflow' && (
            <SubflowTriggerPanel value={subflowInputs} onChange={setSubflowInputs} />
          )}

          {triggerType && (() => {
            const hasFilters = triggerType !== 'cron' && triggerType !== 'subflow'
              && ((selEventType?.filter_fields?.length ?? 0) > 0 || triggerFilters.length > 0);
            const showOrgs = !!isShared && allOrgs.length > 0;
            return (hasFilters || showOrgs) && (
              <TriggerFiltersPanel
                value={triggerFilters}
                onChange={setTriggerFilters}
                fields={hasFilters ? selEventType?.filter_fields ?? [] : []}
                states={states}
                orgScope={showOrgs ? { orgs: allOrgs, value: sharedOrgIds, onChange: setSharedOrgIds, open: orgRowOpen, setOpen: setOrgRowOpen } : undefined}
              />
            );
          })()}
        </div>

        {triggerType && triggerType !== 'cron' && <InlineContextPanel triggerType={triggerType} eventTypes={eventTypes} />}
      </div>

      {/* ── Secrets (Google Secret Manager) ── */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-3">
        <div className="flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-amber-500" />
          <p className="text-sm font-semibold text-gray-900">Secrets &amp; Credentials</p>
          <span className="ml-auto text-[10px] text-gray-400">optional</span>
        </div>
        <SecretsConfigPanel value={secrets} onChange={setSecrets} orgId={orgId} />
      </div>

      {/* ── Step 2: Actions — DAG canvas ── */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="flex items-center border-b border-gray-200 px-5 py-3 gap-2">
          <div className="w-6 h-6 rounded-full bg-gray-900 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">2</div>
          <p className="text-sm font-semibold text-gray-900">Actions</p>
          <span className="ml-auto text-[10px] text-gray-400">
            {dagNodes.length} nodes · {dagEdges.length} edges
          </span>
        </div>

        {triggerType ? (
          <ReactFlowProvider>
            <DAGCanvasEditor
              nodes={dagNodes} edges={dagEdges}
              states={states} contextVars={dagContextVars}
              onChange={(ns, es) => { setDagNodes(ns); setDagEdges(es); }}
              onAddNode={addDagNode}
              orgId={orgId}
              sharedOrgIds={!orgId ? sharedOrgIds : undefined}
              workflowName={name}
              secrets={secrets}
              nextNodeId={() => `node_${++counter.current}`}
              onAddSecrets={refs => setSecrets(cur => [...cur, ...refs])}
              sampleOutputs={sampleOutputs}
              runStates={testResult ? runStates : undefined}
              isShared={isShared}
            />
          </ReactFlowProvider>
        ) : (
          <div className="p-6 text-center text-xs text-gray-400">
            Select a trigger event above before building the DAG.
          </div>
        )}
      </div>

      {/* ── Test run — runs the current draft against a real order; nothing is written ── */}
      {triggerType && triggerType !== 'cron' && dagNodes.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-3">
          <div className="flex items-center gap-2">
            <Play className="w-4 h-4 text-emerald-600" />
            <p className="text-sm font-semibold text-gray-900">Test run</p>
            <span className="text-[10px] text-gray-400">
              Runs this draft (unsaved changes included) against a real order. Write steps and notifications don't run; lookups and custom code do.
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input className="w-56 border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-emerald-200"
              placeholder="Order id or reference (CH-1234567)" value={testOrderRef}
              onChange={e => setTestOrderRef(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') findTestOrder(); }} />
            <button type="button" onClick={findTestOrder} disabled={!testOrderRef.trim() || testBusy !== ''}
              className="flex items-center gap-1 px-3 py-1.5 text-xs border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50">
              {testBusy === 'find' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />} Find order
            </button>
            {testOrder && (
              <>
                <select className="flex-1 min-w-[12rem] border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white"
                  value={testItemId} onChange={e => setTestItemId(e.target.value)}>
                  <option value="">— whole order (no specific item) —</option>
                  {testOrder.items.map(it => (
                    <option key={`${it.index}-${it.item_id}`} value={it.item_id ?? ''}>
                      #{it.index + 1} {it.part_number || ''} {it.name ? `· ${it.name}` : ''}
                    </option>
                  ))}
                </select>
                <button type="button" onClick={runTest} disabled={testBusy !== ''}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50">
                  {testBusy === 'run' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />} Run test
                </button>
              </>
            )}
          </div>
          {testOrder && (
            <p className="text-[10px] text-gray-400">
              Order {testOrder.external_order_id || testOrder.order_id}{testOrder.vin ? ` · VIN ${testOrder.vin}` : ''} · {testOrder.items.length} item{testOrder.items.length === 1 ? '' : 's'}
            </p>
          )}
          {testError && <p className="text-xs text-red-600">{testError}</p>}
          {testResult && (
            <div className="space-y-2">
              <div className={`flex items-center gap-2 text-xs rounded-lg px-3 py-2 border ${
                testResult.status === 'completed' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-700'}`}>
                {testResult.status === 'completed' ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                {testResult.status === 'completed' ? 'Finished' : testResult.status === 'timeout' ? 'Timed out' : 'Failed'}
                {testResult.error ? `: ${testResult.error}` : ''}
                <span className="ml-auto text-[10px] opacity-70">
                  {testResult.steps.filter(x => x.state === 'ran').length} ran ·{' '}
                  {testResult.steps.filter(x => x.state === 'simulated').length} not written ·{' '}
                  {testResult.steps.filter(x => x.state === 'skipped').length} skipped
                </span>
                <button type="button" onClick={() => setTestResult(null)} className="p-0.5 opacity-60 hover:opacity-100" title="Clear result">
                  <X className="w-3 h-3" />
                </button>
              </div>
              <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg">
                {testResult.steps.map(st => {
                  const r = testResult.node_results?.[st.id];
                  const open = openStep === st.id;
                  return (
                    <div key={st.id}>
                      <button type="button" onClick={() => setOpenStep(open ? null : st.id)}
                        disabled={st.state === 'skipped'}
                        className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-gray-50 disabled:hover:bg-transparent">
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          st.state === 'ran' ? 'bg-emerald-500' : st.state === 'simulated' ? 'bg-sky-500'
                          : st.state === 'error' ? 'bg-red-500' : 'bg-gray-300'}`} />
                        <span className={`flex-1 truncate ${st.state === 'skipped' ? 'text-gray-400' : 'text-gray-800'}`}>
                          {st.label} <span className="text-gray-400 font-mono">({st.id})</span>
                        </span>
                        <span className="text-[10px] text-gray-400">
                          {st.state === 'simulated' ? 'not written' : st.state}{st.duration_ms !== undefined ? ` · ${st.duration_ms} ms` : ''}
                        </span>
                      </button>
                      {open && (
                        <div className="px-3 pb-2">
                          {st.error && <p className="text-[11px] text-red-600 mb-1">{st.error}</p>}
                          <pre className="text-[10px] font-mono bg-gray-50 border border-gray-100 rounded p-2 max-h-64 overflow-auto whitespace-pre-wrap">
                            {JSON.stringify(st.state === 'simulated' ? r?.output?.would : r?.output, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] text-gray-400">
                The fields each step returned are now offered in every step's data picker (marked "test run").
              </p>
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-3 flex justify-end gap-2 rounded-b-2xl shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
        {initial?.id ? (
          <button onClick={openHistory} title="Saved versions of this workflow"
            className="mr-auto flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-200 rounded-xl hover:bg-gray-50">
            <Clock className="w-4 h-4" /> History
          </button>
        ) : null}
        <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-200 rounded-xl hover:bg-gray-50">
          Cancel
        </button>
        <button onClick={() => handleSave()} disabled={saving}
          className="flex items-center gap-1.5 px-5 py-2 text-sm font-medium text-white bg-gray-900 rounded-xl hover:bg-gray-800 disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {initial?.id ? 'Update workflow' : 'Create workflow'}
        </button>
      </div>

      {historyOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={() => setHistoryOpen(false)}>
          <div className="bg-white rounded-2xl border border-gray-200 shadow-2xl w-full max-w-xl mx-4 overflow-hidden flex flex-col max-h-[85vh]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <p className="text-sm font-semibold text-gray-900 flex items-center gap-2"><Clock className="w-4 h-4" /> Version history</p>
              <button onClick={() => setHistoryOpen(false)} className="p-1 text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
            </div>
            <div className="overflow-y-auto px-5 py-3 space-y-1.5">
              {historyError && <p className="text-xs text-red-600">{historyError}</p>}
              {versions === null && <p className="text-xs text-gray-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</p>}
              {versions !== null && versions.length === 0 && !historyError && (
                <p className="text-xs text-gray-400">No saved versions yet. A version is kept every time you save a change.</p>
              )}
              {versions?.map((v, i) => (
                <div key={v.version} className="flex items-center gap-3 border border-gray-100 rounded-lg px-3 py-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-900">
                      Version {v.version}{i === 0 ? <span className="ml-1.5 text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5">current</span> : null}
                      {v.note ? <span className="ml-1.5 text-gray-500 font-normal">· {v.note}</span> : null}
                    </p>
                    <p className="text-[10px] text-gray-400 truncate">
                      {v.created_at ? new Date(v.created_at).toLocaleString() : ''}{v.created_by ? ` · ${v.created_by}` : ''} · {v.steps} steps, {v.arrows} arrows
                    </p>
                  </div>
                  {i > 0 && (
                    <button onClick={() => openVersion(v.version)} disabled={loadingVersion !== null}
                      className="flex items-center gap-1 px-2.5 py-1 text-xs border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                      {loadingVersion === v.version ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />} Open in editor
                    </button>
                  )}
                </div>
              ))}
            </div>
            <p className="px-5 py-2.5 border-t border-gray-100 text-[10px] text-gray-400">
              Opening a version only loads it into the editor. Nothing changes until you save, and saving keeps the old versions.
            </p>
          </div>
        </div>
      )}

      {checkWarnings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={() => setCheckWarnings(null)}>
          <div className="bg-white rounded-2xl border border-gray-200 shadow-2xl w-full max-w-2xl mx-4 overflow-hidden flex flex-col max-h-[85vh]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
              <AlertCircle className="w-4 h-4 text-amber-500" />
              <p className="text-sm font-semibold text-gray-900">
                {checkWarnings.length === 1 ? '1 thing to check' : `${checkWarnings.length} things to check`} before saving
              </p>
            </div>
            <div className="overflow-y-auto px-5 py-4 space-y-2">
              <p className="text-xs text-gray-500">
                The workflow is valid and can be saved, but these usually mean it works by accident and can break when steps change.
              </p>
              {checkWarnings.map((w, i) => (
                <div key={i} className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  {w.message}
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-100">
              <button onClick={() => setCheckWarnings(null)}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-200 rounded-xl hover:bg-gray-50">
                Keep editing
              </button>
              <button onClick={() => handleSave(true)} disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-xl hover:bg-gray-800 disabled:opacity-50">
                Save anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Node config modal (reuses StepCard fields in a modal wrapper)
interface NodeConfigModalProps {
  node: StepNode;
  states: WorkflowState[];
  contextVars: ContextVar[];
  onSave: (n: StepNode) => void;
  onClose: (current?: StepNode) => void;
  orgId?: number;
  sharedOrgIds?: number[];
  /** Download this step (with any unsaved edits) as a file for another workflow. */
  onExport?: (n: StepNode) => void;
  /** Shown under "Available data" (e.g. when no earlier steps are connected). */
  upstreamHint?: string;
}

const NodeConfigModal: React.FC<NodeConfigModalProps> = ({ node, states, contextVars, onSave, onClose, orgId, sharedOrgIds, onExport, upstreamHint }) => {
  const [local, setLocal] = useState<StepNode>(node);
  const [stepSources, setStepSources] = useState<any[]>([]);
  const [loadingSrc, setLoadingSrc] = useState(false);
  const [sourcesSharedMode, setSourcesSharedMode] = useState(false);

  const cfg = (k: string, v: any) => setLocal(l => ({ ...l, config: { ...l.config, [k]: v } }));
  const cfgMany = (patch: Record<string, any>) => setLocal(l => ({ ...l, config: { ...l.config, ...patch } }));

  const loadIntSources = useCallback(async (intType: string) => {
    setLoadingSrc(true);
    try {
      const result = await integrationService.getSourcesForWorkflow(intType, orgId, sharedOrgIds);
      setStepSources(result.sources);
      setSourcesSharedMode(result.isSharedMode);
    }
    catch { setStepSources([]); }
    finally { setLoadingSrc(false); }
  }, [orgId, sharedOrgIds]);

  useEffect(() => {
    if ((local.type === 'run_integration' || local.type === 'refresh_integration_source') && local.config.integration_type) {
      loadIntSources(local.config.integration_type);
    } else if (local.type === 'enrich_order') {
      loadIntSources('enrichment');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (local.config._action_id === 'update_order_status' && local.config._status_value !== undefined) {
      const fields = [{ key: 'status', value: local.config._status_value }];
      if (JSON.stringify(local.config.fields) !== JSON.stringify(fields)) {
        setLocal(l => ({ ...l, config: { ...l.config, fields } }));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local.config._status_value]);

  const entityFields: Array<{key: string; value: string | null}> = local.config.fields ?? [];
  const updateField = (i: number, patch: Partial<{key: string; value: string | null}>) => {
    cfg('fields', entityFields.map((f, idx) => idx === i ? { ...f, ...patch } : f));
  };

  const selectedSource = stepSources.find((s: any) => String(s.id) === String(local.config.source_id));
  const intActions: any[] = selectedSource?.actions || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={() => onClose(local)}>
      <div
        className={`bg-white rounded-2xl border border-gray-200 shadow-2xl w-full mx-4 overflow-hidden flex flex-col max-h-[92vh] ${
          local.type === 'run_python' ? 'max-w-[min(96rem,95vw)] h-[92vh]' : 'max-w-3xl'
        }`}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            {(() => { const a = EXPLICIT_ACTIONS.find(a => a.id === local.config?._action_id || a.nodeType === local.type); const Icon = a?.icon ?? Circle; return <Icon className="w-4 h-4" />; })()}
            <p className="text-sm font-semibold text-gray-900">{local.label || 'Configure step'}</p>
          </div>
          <div className="flex items-center gap-1">
            {onExport && (
              <button
                type="button"
                onClick={() => onExport(local)}
                title="Download this step as a file you can import into another workflow"
                className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
              >
                <Download className="w-3.5 h-3.5" /> Export step
              </button>
            )}
            <button onClick={() => onClose(local)} className="p-1 text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-3">
          {/* Label */}
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Step label</label>
            <input className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-gray-200"
              value={local.label} onChange={e => setLocal(l => ({ ...l, label: e.target.value }))} />
          </div>

          {/* Context panel */}
          <InlineContextPanel triggerType="" eventTypes={[]} nodeResultVars={contextVars.filter(v => v.path.startsWith('node_results.'))} defaultOpen={false} />
          {upstreamHint && (
            <p className="text-[10px] text-gray-400 flex items-center gap-1 -mt-1">
              <Info className="w-3 h-3" /> {upstreamHint}
            </p>
          )}

          {/* update_order_status */}
          {local.config._action_id === 'update_order_status' && (
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">New Status <span className="text-red-400">*</span></label>
              <select className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-teal-200"
                value={local.config._status_value ?? ''} onChange={e => cfg('_status_value', e.target.value)}>
                <option value="">— select a status —</option>
                {states.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
          )}

          {/* find_orders_by_status (query_orders) */}
          {local.config._action_id === 'find_orders_by_status' && (
            <>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Order Status <span className="text-red-400">*</span></label>
                <select className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-sky-200"
                  value={local.config.status ?? ''} onChange={e => cfg('status', e.target.value)}>
                  <option value="">— select a status —</option>
                  {states.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Source (optional)</label>
                <input className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-sky-200"
                  placeholder="e.g. insurance_portal" value={local.config.source ?? ''} onChange={e => cfg('source', e.target.value)} />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Max orders</label>
                <input type="number" min={1} max={1000} className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-sky-200"
                  value={local.config.limit ?? 200} onChange={e => cfg('limit', Number(e.target.value))} />
              </div>
            </>
          )}

          {/* update_order_field / update_part_field / update_part_price_from_integration / update_triggered_item_fields / write_result_to_part */}
          {(local.config._action_id === 'update_order_field' || local.config._action_id === 'update_part_field' || local.config._action_id === 'update_part_price_from_integration' || local.config._action_id === 'update_triggered_item_fields' || local.config._action_id === 'write_result_to_part' || (local.type === 'entity_action' && (local.config.operation === 'update' || !local.config.operation) && local.config._action_id !== 'update_order_status')) && (() => {
            const PART_FIELD_OPTIONS = [
              { value: 'unit_price',               label: 'Unit Price' },
              { value: 'currency',                 label: 'Currency' },
              { value: 'supplier_name',            label: 'Supplier Name' },
              { value: 'supplier_part_number',     label: 'Supplier Part #' },
              { value: 'availability',             label: 'Availability' },
              { value: 'description',              label: 'Description' },
              { value: 'internal_catalog_number',  label: 'Internal Catalog #' },
              { value: 'stock_qty',                label: 'Stock Qty' },
              { value: 'lead_time_days',           label: 'Lead Time (days)' },
              { value: 'unit',                     label: 'Unit' },
              { value: 'min_order_qty',            label: 'Min Order Qty' },
              { value: 'oe_number',                label: 'OE Number' },
            ];
            const isWriteResult = local.config._action_id === 'write_result_to_part';
            const lookupNodePrefixes = isWriteResult
              ? Array.from(new Set(
                  contextVars
                    .filter(v => v.path.startsWith('node_results.') && v.path.includes('.output.price'))
                    .map(v => v.path.split('.output.')[0])
                ))
              : [];
            return (
              <div>
                {/* Quick-add panel — write_result_to_part only */}
                {isWriteResult && lookupNodePrefixes.length > 0 && (
                  <div className="rounded-xl border border-green-200 bg-green-50/60 px-3 py-2.5 mb-3 space-y-2">
                    <p className="text-[9px] font-bold uppercase tracking-widest text-green-600 flex items-center gap-1">⚡ Quick-add from lookup result</p>
                    {lookupNodePrefixes.map(prefix => (
                      <div key={prefix}>
                        <p className="text-[10px] font-semibold text-green-700 mb-1.5">
                          <span className="bg-green-100 border border-green-200 rounded px-1.5 py-0.5 font-mono text-[9px]">{prefix.replace('node_results.', '')}</span>
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {PART_FIELD_OPTIONS.map(f => {
                            const varPath = `${prefix}.output.${f.value}`;
                            const alreadyMapped = entityFields.some(ef => ef.key === f.value);
                            return (
                              <button key={f.value} type="button" disabled={alreadyMapped}
                                onClick={() => cfg('fields', [...entityFields, { key: f.value, value: varPath }])}
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-mono transition-colors ${
                                  alreadyMapped ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-default' : 'border-green-300 bg-white text-green-700 hover:bg-green-100 cursor-pointer'
                                }`}>
                                {alreadyMapped ? <span className="text-green-500">✓</span> : <Plus className="w-2 h-2" />} {f.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                    <p className="text-[9px] text-green-400">Click a field to add it as a mapping row.</p>
                  </div>
                )}
                {isWriteResult && lookupNodePrefixes.length === 0 && (
                  <div className="rounded-lg border border-amber-100 bg-amber-50/50 px-3 py-2 mb-2">
                    <p className="text-[10px] text-amber-600">💡 Add a <strong>Look Up Supplier Pricing</strong> node before this one for quick-add shortcuts, or add rows manually below.</p>
                  </div>
                )}
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">
                    {isWriteResult ? 'Field mappings → cart item' :
                     local.config._action_id === 'update_part_field' ? 'Part fields to update' :
                     local.config._action_id === 'update_part_price_from_integration' ? 'Price fields to map' :
                     local.config._action_id === 'update_triggered_item_fields' ? 'Fields to write to added item' :
                     'Fields to update'}
                  </label>
                  <button type="button" onClick={() => cfg('fields', [...entityFields, { key: isWriteResult ? PART_FIELD_OPTIONS[0].value : '', value: '' }])}
                    className="text-[10px] flex items-center gap-0.5 text-teal-700 border border-teal-200 rounded px-1.5 py-0.5 bg-white hover:bg-teal-50">
                    <Plus className="w-2.5 h-2.5" /> Add row
                  </button>
                </div>
                {entityFields.length === 0 ? (
                  <div className="border border-dashed border-gray-200 rounded-lg p-3 text-center text-[10px] text-gray-400">
                    {isWriteResult ? 'Use quick-add above or click "Add row" to manually map a field.' : 'Click "Add row" to add field mappings.'}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {isWriteResult && (
                      <div className="flex items-center gap-1.5 text-[9px] text-gray-400 px-0.5">
                        <span className="w-36 flex-shrink-0">Field on cart item</span>
                        <span className="flex-1">Value (from lookup / context)</span>
                      </div>
                    )}
                    {entityFields.map((f, i) => (
                      <div key={i} className="flex items-start gap-1.5">
                        {isWriteResult ? (
                          <div className="w-36 flex-shrink-0">
                            {PART_FIELD_OPTIONS.some(o => o.value === f.key) ? (
                              <select
                                className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white font-mono focus:outline-none focus:ring-2 focus:ring-teal-200"
                                value={f.key}
                                onChange={e => updateField(i, { key: e.target.value === '__custom__' ? '' : e.target.value })}>
                                {PART_FIELD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                <option value="__custom__">✏️ Custom field…</option>
                              </select>
                            ) : (
                              <div className="flex gap-0.5">
                                <input className="flex-1 min-w-0 border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-teal-200"
                                  placeholder="field name" value={f.key} onChange={e => updateField(i, { key: e.target.value })} />
                                <button type="button" title="Pick a standard field"
                                  onClick={() => updateField(i, { key: PART_FIELD_OPTIONS[0].value })}
                                  className="flex-shrink-0 px-1 py-1 text-gray-400 hover:text-teal-600 border border-gray-200 rounded-lg text-[10px]">↑</button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <input className="w-28 border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-mono focus:outline-none flex-shrink-0"
                            placeholder="field name" value={f.key} onChange={e => updateField(i, { key: e.target.value })} />
                        )}
                        <div className="flex-1">
                          <VariablePicker value={f.value} contextVars={contextVars} placeholder="value or variable" onChange={v => updateField(i, { value: v })} />
                        </div>
                        <button type="button" onClick={() => cfg('fields', entityFields.filter((_, idx) => idx !== i))}
                          className="p-1 text-gray-300 hover:text-red-400 flex-shrink-0 mt-1"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {/* send_notification */}
          {local.type === 'send_notification' && (
            <>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                  {local.config.channel === 'whatsapp' ? 'Phone number' : 'Recipient email'} *
                </label>
                <VariablePicker value={local.config.recipient ?? ''} contextVars={contextVars} placeholder="e.g. entity.buyer" onChange={v => cfg('recipient', v)} />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Message *</label>
                <textarea rows={3} className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs resize-none focus:outline-none focus:ring-1 focus:ring-gray-300"
                  value={local.config.message ?? ''} onChange={e => cfg('message', e.target.value)} />
              </div>
            </>
          )}

          {/* switch */}
          {local.type === 'switch' && (() => {
            const cases: string[] = Array.isArray(local.config.cases) ? local.config.cases : [];
            const setCases = (next: string[]) => cfg('cases', next);
            return (
              <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-3 space-y-2.5">
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Value to check</label>
                  <VariablePicker allowNull={false} value={local.config.field ?? ''} contextVars={contextVars}
                    placeholder="e.g. entity.vin_data.datasetName" onChange={v => cfg('field', v ?? '')} />
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Cases</label>
                    <button type="button" onClick={() => setCases([...cases, ''])}
                      className="text-[10px] px-2 py-0.5 rounded-md bg-amber-100 text-amber-700 hover:bg-amber-200 font-medium">
                      + Add case
                    </button>
                  </div>
                  {cases.map((c, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <input className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-mono bg-white focus:outline-none focus:ring-2 focus:ring-amber-200"
                        placeholder={i === 0 ? 'e.g. MG' : 'value'} value={c}
                        onChange={e => setCases(cases.map((x, j) => (j === i ? e.target.value : x)))} />
                      <button type="button" className="p-1.5 text-gray-400 hover:text-red-500"
                        onClick={() => setCases(cases.filter((_, j) => j !== i))}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                  <label className="flex items-center gap-2 text-[11px] text-gray-600 select-none cursor-pointer">
                    <input type="checkbox" className="rounded" checked={!!local.config.case_sensitive}
                      onChange={e => cfg('case_sensitive', e.target.checked)} />
                    Match letter case exactly
                  </label>
                </div>
                <p className="text-[10px] text-gray-400">
                  The first matching case wins. Then draw an arrow from this step for each case and pick the case
                  in the arrow's settings; add an <strong>Otherwise</strong> arrow for everything else.
                </p>
              </div>
            );
          })()}

          {/* condition (check_condition) */}
          {local.type === 'condition' && (
            <>
              <ConditionRowsEditor config={local.config} contextVars={contextVars} cfgMany={cfgMany} />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">True path label</label>
                  <input className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-amber-200"
                    value={local.config.true_label ?? 'Yes'} onChange={e => cfg('true_label', e.target.value)} />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">False path label</label>
                  <input className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-amber-200"
                    value={local.config.false_label ?? 'No'} onChange={e => cfg('false_label', e.target.value)} />
                </div>
              </div>
              <div className="rounded-lg border border-amber-100 bg-amber-50/50 px-3 py-2">
                <p className="text-[10px] text-amber-600">
                  💡 Draw <strong>two outgoing edges</strong> from this node — one for YES (condition: <code className="font-mono bg-amber-100 px-0.5 rounded">node_results.{'{'}{local.id}{'}'}.output.result = true</code>) and one for NO.
                </p>
              </div>
            </>
          )}


          {/* call_workflow (subflow) */}
          {local.type === 'call_workflow' && (
            <CallWorkflowConfig config={local.config} contextVars={contextVars} cfg={cfg} />
          )}

          {/* set_output (subflow return value) */}
          {local.type === 'set_output' && (
            <SetOutputConfig config={local.config} contextVars={contextVars} cfg={cfg} />
          )}

          {/* search_parts */}
          {local.type === 'search_parts' && (
            <div className="rounded-xl border border-sky-200 bg-sky-50/50 p-3 space-y-3">
              <p className="text-[10px] font-semibold text-sky-700 uppercase tracking-wide flex items-center gap-1">
                <Search className="w-3 h-3" /> Parts search configuration
              </p>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">VIN</label>
                <VariablePicker value={local.config.vin ?? ''} contextVars={contextVars}
                  placeholder="e.g. entity.vin or 1HGCM82633A004352" onChange={v => cfg('vin', v)} />
                <p className="text-[10px] text-gray-400 mt-0.5">17-character VIN — dot-path or static value.</p>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Search query</label>
                <VariablePicker value={local.config.query ?? ''} contextVars={contextVars}
                  placeholder='e.g. event_payload.query or "brake pads"' onChange={v => cfg('query', v)} />
                <p className="text-[10px] text-gray-400 mt-0.5">Free-text parts query — dot-path or static text.</p>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Language</label>
                <input className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-sky-200 bg-white"
                  placeholder="en" value={local.config.language ?? 'en'} onChange={e => cfg('language', e.target.value)} />
                <p className="text-[10px] text-gray-400 mt-0.5">Response language code (en, es, he, …).</p>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Sourcer</label>
                <VariablePicker value={local.config.sourcer ?? ''} contextVars={contextVars}
                  placeholder='e.g. event_payload.sourcer or "acme-supplier"' onChange={v => cfg('sourcer', v)} />
                <p className="text-[10px] text-gray-400 mt-0.5">Optional. Labels the search as a guest request attributed to this sourcer name.</p>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Organization ID</label>
                <VariablePicker value={local.config.organization_id ?? ''} contextVars={contextVars}
                  placeholder='e.g. entity.organization_id or a static UUID' onChange={v => cfg('organization_id', v)} />
                <p className="text-[10px] text-gray-400 mt-0.5">Optional. Override the organization used for the parts search.</p>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Notes</label>
                <VariablePicker value={local.config.notes ?? ''} contextVars={contextVars}
                  placeholder='e.g. "Automated search from workflow" or a dot-path' onChange={v => cfg('notes', v)} />
                <p className="text-[10px] text-gray-400 mt-0.5">Optional. Free-text note attached to this search (visible in search history).</p>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Timeout (seconds)</label>
                <input
                  type="number"
                  min={10}
                  max={600}
                  step={10}
                  className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-sky-200 bg-white"
                  placeholder="120"
                  value={local.config.timeout ?? 120}
                  onChange={e => cfg('timeout', e.target.value === '' ? null : Number(e.target.value))}
                />
                <p className="text-[10px] text-gray-400 mt-0.5">Max seconds to wait for the agent (10–600). Leave blank to use the default (120 s).</p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id={`create_order_modal_${local.id}`}
                  checked={!!local.config.create_order}
                  onChange={e => cfg('create_order', e.target.checked)}
                  className="rounded border-gray-300 text-sky-600 focus:ring-sky-200"
                />
                <label htmlFor={`create_order_modal_${local.id}`} className="text-xs text-gray-700 cursor-pointer select-none">
                  Create order after search
                </label>
                <span className="text-[10px] text-gray-400">(stores results as a draft <code className="font-mono bg-sky-100 px-0.5 rounded">UserOrders</code> record; id available as <code className="font-mono bg-sky-100 px-0.5 rounded">output.search_order_id</code>)</span>
              </div>
              {/* Parallel queries */}
              <div className="border-t border-sky-200 pt-3">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[10px] font-semibold text-sky-700 uppercase tracking-wide flex items-center gap-1">
                    ⚡ Parallel queries
                  </label>
                  <button
                    type="button"
                    className="text-[10px] px-2 py-0.5 rounded-md bg-sky-100 text-sky-700 hover:bg-sky-200 transition-colors font-medium"
                    onClick={() => {
                      const current: any[] = local.config.queries ?? [];
                      cfg('queries', [...current, { vin: '', query: '', sourcer: '' }]);
                    }}
                  >
                    + Add query
                  </button>
                </div>
                <p className="text-[10px] text-gray-400 mb-2">
                  Add multiple queries to run them all concurrently. When set, takes precedence over the single VIN/query above.
                  Results are merged under <code className="font-mono bg-sky-100 px-0.5 rounded">output.results</code> and also available per-query in <code className="font-mono bg-sky-100 px-0.5 rounded">output.results_by_query</code>.
                </p>
                {(local.config.queries ?? []).map((q: any, qi: number) => (
                  <div key={qi} className="rounded-lg border border-sky-200 bg-white p-2 mb-2 space-y-1.5">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-semibold text-sky-600">Query {qi + 1}</span>
                      <button
                        type="button"
                        className="text-[10px] text-red-400 hover:text-red-600"
                        onClick={() => {
                          const updated = (local.config.queries ?? []).filter((_: any, i: number) => i !== qi);
                          cfg('queries', updated);
                        }}
                      >
                        Remove
                      </button>
                    </div>
                    <div>
                      <label className="block text-[10px] text-gray-500 mb-0.5">VIN</label>
                      <VariablePicker
                        value={q.vin ?? ''}
                        contextVars={contextVars}
                        placeholder="e.g. entity.vin or static VIN (leave blank to use global)"
                        onChange={v => {
                          const updated = [...(local.config.queries ?? [])];
                          updated[qi] = { ...updated[qi], vin: v };
                          cfg('queries', updated);
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-gray-500 mb-0.5">Query</label>
                      <VariablePicker
                        value={q.query ?? ''}
                        contextVars={contextVars}
                        placeholder='e.g. "brake pads" or event_payload.query'
                        onChange={v => {
                          const updated = [...(local.config.queries ?? [])];
                          updated[qi] = { ...updated[qi], query: v };
                          cfg('queries', updated);
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-gray-500 mb-0.5">Sourcer (optional)</label>
                      <VariablePicker
                        value={q.sourcer ?? ''}
                        contextVars={contextVars}
                        placeholder='e.g. "acme-supplier"'
                        onChange={v => {
                          const updated = [...(local.config.queries ?? [])];
                          updated[qi] = { ...updated[qi], sourcer: v };
                          cfg('queries', updated);
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="rounded-lg border border-sky-100 bg-sky-50/60 px-3 py-2">
                <p className="text-[10px] text-sky-700">
                  💡 Results available as <code className="font-mono bg-sky-100 px-0.5 rounded">node_results.{local.id}.output.results</code> and <code className="font-mono bg-sky-100 px-0.5 rounded">node_results.{local.id}.output.not_found_parts</code>.
                </p>
              </div>
            </div>
          )}

          {/* run_integration */}
          {local.type === 'run_integration' && (
            <>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Integration type</label>
                <select className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white"
                  value={local.config.integration_type || 'inventory'}
                  onChange={e => { const t = e.target.value; setLocal(l => ({ ...l, config: { ...l.config, integration_type: t, source_id: '', action_id: '', input_mapping: {} } })); loadIntSources(t); }}>
                  {['inventory', 'pricing', 'supplier', 'crm', 'shipping', 'quotes', 'dms'].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Source</label>
                <select className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white" value={local.config.source_id ?? ''} disabled={loadingSrc}
                  onChange={e => setLocal(l => ({ ...l, config: { ...l.config, source_id: e.target.value, action_id: '', input_mapping: {} } }))}>
                  <option value="">{loadingSrc ? 'Loading…' : 'Select source…'}</option>
                  {stepSources.map((s: any) => {
                    const isShared = Array.isArray(s.shared_organization_ids) && s.shared_organization_ids.length > 0;
                    const prefix = sourcesSharedMode ? (isShared ? '🌐 ' : '🏢 ') : '';
                    return <option key={s.id} value={String(s.id)}>{prefix}{s.name}</option>;
                  })}
                </select>
                {sourcesSharedMode && stepSources.length === 0 && !loadingSrc && (sharedOrgIds?.length ?? 0) > 0 && (
                  <p className="text-[10px] text-amber-600 mt-0.5">No integrations found for the selected organisations.</p>
                )}
              </div>
              {local.config.source_id && (
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Action</label>
                  <select className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white" value={local.config.action_id ?? ''}
                    onChange={e => {
                      const picked = intActions.find((a: any) => String(a.id) === e.target.value);
                      const outputSchema = picked?.outputSchema?.properties ?? picked?.outputSchema ?? null;
                      setLocal(l => ({ ...l, config: { ...l.config, action_id: e.target.value, input_mapping: {}, _output_schema: outputSchema } }));
                    }}>
                    <option value="">Select action…</option>
                    {intActions.map((a: any) => <option key={a.id} value={String(a.id)}>{a.name}</option>)}
                  </select>
                </div>
              )}
              {local.config.action_id && (() => {
                const selAction = intActions.find((a: any) => String(a.id) === String(local.config.action_id));
                if (!selAction) return null;
                const paramNames: string[] = selAction.inputSchema?.properties
                  ? Object.keys(selAction.inputSchema.properties)
                  : (selAction.inputParameters ?? []).map((p: any) => p.name);
                const mapping: Record<string, string | null> = local.config.input_mapping ?? {};
                const outProps: Record<string, { type?: string; description?: string }> =
                  selAction.outputSchema?.properties ?? selAction.outputSchema ?? {};
                const outEntries = Object.entries(outProps);
                return (
                  <>
                    {paramNames.length > 0 && (
                      <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-3 space-y-2.5">
                        <p className="text-[10px] font-semibold text-indigo-700 uppercase tracking-wide">Map action inputs</p>
                        {paramNames.map((param: string) => {
                          const propDef = selAction.inputSchema?.properties?.[param];
                          const required = selAction.inputSchema?.required?.includes(param);
                          return (
                            <div key={param}>
                              <label className="block text-[10px] font-medium text-gray-600 mb-0.5">
                                <code className="font-mono">{param}</code>
                                {required && <span className="ml-1 text-red-400 text-[9px]">required</span>}
                                {propDef?.description && <span className="ml-1 text-gray-400 italic font-normal">{propDef.description}</span>}
                              </label>
                              <VariablePicker value={param in mapping ? mapping[param] : ''} contextVars={contextVars}
                                placeholder={`value for ${param}`}
                                onChange={v => {
                                  const newMapping = { ...mapping, [param]: v };
                                  setLocal(l => ({ ...l, config: { ...l.config, input_mapping: newMapping } }));
                                }} />
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {outEntries.length > 0 && (
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3 space-y-1.5">
                        <p className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wide flex items-center gap-1">
                          <ArrowRight className="w-3 h-3" /> Output fields available to next steps
                        </p>
                        <p className="text-[9px] text-emerald-600 mb-1">
                          Reference as <code className="font-mono bg-emerald-100 px-0.5 rounded">node_results.{local.id}.output.<em>field</em></code>
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {outEntries.map(([k, v]) => (
                            <span key={k} title={(v as any)?.description ?? ''}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-emerald-200 bg-white text-[10px] font-mono text-emerald-700">
                              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                (v as any)?.type === 'integer' || (v as any)?.type === 'number' ? 'bg-blue-400' :
                                (v as any)?.type === 'boolean' ? 'bg-yellow-400' :
                                (v as any)?.type === 'array'   ? 'bg-rose-400' :
                                (v as any)?.type === 'object'  ? 'bg-violet-400' : 'bg-emerald-400'
                              }`} />
                              {k}
                              {(v as any)?.type && <span className="text-[9px] text-emerald-400 ml-0.5">{(v as any).type}</span>}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </>
          )}

          {/* refresh_integration_source */}
          {local.type === 'refresh_integration_source' && (
            <>
              <div className="rounded-lg border border-cyan-200 bg-cyan-50/60 px-3 py-2 text-[11px] text-cyan-800">
                Re-loads the data behind a CSV integration source from a fresh file in GCS. Point <span className="font-semibold">GCS file</span> at an upstream step that wrote the CSV (e.g. <code className="font-mono">node_results.&lt;node&gt;.output.gcs_path</code>).
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Integration type</label>
                <select className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white"
                  value={local.config.integration_type || 'pricing'}
                  onChange={e => { const t = e.target.value; setLocal(l => ({ ...l, config: { ...l.config, integration_type: t, source_id: '' } })); loadIntSources(t); }}>
                  {['pricing', 'supplier', 'inventory', 'quotes', 'crm', 'shipping', 'dms'].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Source to refresh <span className="text-red-400">*</span></label>
                <select className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white" value={local.config.source_id ?? ''} disabled={loadingSrc}
                  onChange={e => cfg('source_id', e.target.value)}>
                  <option value="">{loadingSrc ? 'Loading…' : 'Select source…'}</option>
                  {stepSources.map((s: any) => {
                    const isShared = Array.isArray(s.shared_organization_ids) && s.shared_organization_ids.length > 0;
                    const prefix = sourcesSharedMode ? (isShared ? '🌐 ' : '🏢 ') : '';
                    return <option key={s.id} value={String(s.id)}>{prefix}{s.name}</option>;
                  })}
                </select>
                {sourcesSharedMode && stepSources.length === 0 && !loadingSrc && (sharedOrgIds?.length ?? 0) > 0 && (
                  <p className="text-[10px] text-amber-600 mt-0.5">No integrations found for the selected organisations.</p>
                )}
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">GCS file <span className="text-red-400">*</span></label>
                <VariablePicker value={local.config.gcs_path ?? ''} contextVars={contextVars}
                  placeholder="node_results.<node>.output.gcs_path or gs://bucket/file.csv"
                  onChange={v => cfg('gcs_path', v)} />
                <p className="text-[10px] text-gray-400 mt-0.5">Must resolve to a <code className="font-mono">gs://</code> CSV URI.</p>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-cyan-200 bg-cyan-50/60 px-3 py-2">
                <div>
                  <p className="text-[10px] font-semibold text-cyan-800">Mark source active after refresh</p>
                  <p className="text-[10px] text-cyan-500 mt-0.5">Set the source status to “active” once the dataset is loaded.</p>
                </div>
                <button type="button"
                  onClick={() => cfg('set_active', !(local.config.set_active ?? true))}
                  className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 transition-colors duration-200 focus:outline-none ${(local.config.set_active ?? true) ? 'bg-cyan-500 border-cyan-500' : 'bg-gray-200 border-gray-200'}`}>
                  <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-200 ${(local.config.set_active ?? true) ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
            </>
          )}

          {/* enrich_order */}
            {local.type === 'enrich_order' && (() => {
              const activeCount = stepSources.filter((s: any) => s.status === 'active').length;
              return (
                <>
                  <div className="rounded-lg border border-teal-200 bg-teal-50/60 px-3 py-2 text-[11px] text-teal-800">
                    Looks up this order's vehicle in your reference documents (uploaded client documents,
                    managed in Admin → Enrichment) and writes the matching data — local model name,
                    maintenance schedule, part prices, warranty terms — into the order's extra data.
                    The chat then answers from it and the header shows the local model name.
                    Results are cached per vehicle model, so repeat models resolve instantly.
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-white px-3 py-2">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Data searched</p>
                    {loadingSrc ? (
                      <p className="text-[11px] text-gray-400 mt-0.5">Loading available documents…</p>
                    ) : activeCount === 0 ? (
                      <p className="text-[11px] text-amber-600 mt-0.5">
                        No active documents available yet — upload a client document under Admin → Enrichment first.
                      </p>
                    ) : (
                      <p className="text-[11px] text-gray-600 mt-0.5">
                        All active documents are searched automatically —{' '}
                        <span className="font-semibold text-teal-700">{activeCount}</span> available.
                      </p>
                    )}
                  </div>
                </>
              );
            })()}

          {/* foreach_array */}
          {local.type === 'foreach_array' && (
            <>
              {local.config._action_id === 'for_each_array' && (
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Array to loop over *</label>
                  <VariablePicker value={local.config.array_source ?? ''} contextVars={contextVars} placeholder="e.g. entity.parts_in_order" onChange={v => cfg('array_source', v)} />
                </div>
              )}
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">Loop variable name</label>
                <input className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-mono"
                  placeholder="item" value={local.config.item_var ?? 'item'} onChange={e => cfg('item_var', e.target.value)} />
              </div>
              <div className="flex items-center justify-between rounded-lg border border-orange-200 bg-orange-50/60 px-3 py-2">
                <div>
                  <p className="text-[10px] font-semibold text-orange-800">Run iterations in parallel</p>
                  <p className="text-[10px] text-orange-500 mt-0.5">All items are processed concurrently. Use when order doesn't matter.</p>
                </div>
                <button type="button"
                  onClick={() => cfg('parallel', !(local.config.parallel ?? false))}
                  className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 transition-colors duration-200 focus:outline-none ${local.config.parallel ? 'bg-orange-500 border-orange-500' : 'bg-gray-200 border-gray-200'}`}>
                  <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-200 ${local.config.parallel ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>
            </>
          )}

          {/* run_python */}
          {local.type === 'run_python' && (
            <RunPythonNodeEditor
              config={local.config}
              nodeId={local.id}
              contextVars={contextVars}
              cfg={cfg}
              VariablePicker={VariablePicker}
            />
          )}

          {/* On error */}
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">If this step fails…</label>
            <select className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white"
              value={local.on_error ?? 'abort'} onChange={e => setLocal(l => ({ ...l, on_error: e.target.value }))}>
              <option value="abort">Stop the whole workflow</option>
              <option value="continue">Skip and continue</option>
              <option value="set_status">Set order status</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-100 flex-shrink-0">
          <button onClick={() => onClose(local)} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50">Cancel</button>
          <button onClick={() => { onSave(local); onClose(); }}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-xl hover:bg-gray-800">
            <Check className="w-4 h-4" /> Apply
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// DAG Canvas Editor — node types, edge modal, node config modal
// ---------------------------------------------------------------------------

interface DAGEditorNodeData extends Record<string, unknown> {
  nodeType: string;
  label: string;
  config: Record<string, any>;
  /** Outcome in the last test run: ran | simulated | skipped | error */
  runState?: string;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}
type RFEditorNode = Node<DAGEditorNodeData>;

const DAGEditorNodeComponent = ({ id, data, selected }: NodeProps & { data: DAGEditorNodeData }) => {
  const colorClass = NODE_COLORS[data.nodeType] ?? 'bg-gray-50 border-gray-300 text-gray-800';
  const Icon = NODE_ICON_MAP[data.nodeType] ?? Circle;
  const runRing = data.runState === 'ran' ? 'ring-2 ring-emerald-400'
    : data.runState === 'simulated' ? 'ring-2 ring-sky-400'
    : data.runState === 'error' ? 'ring-2 ring-red-500'
    : data.runState === 'skipped' ? 'opacity-40' : '';
  return (
    <div
      className={`relative rounded-xl border-2 px-3 py-2.5 min-w-[160px] max-w-[220px] shadow-sm group ${colorClass} ${selected ? 'ring-2 ring-offset-1 ring-gray-900' : runRing}`}
      onDoubleClick={() => data.onEdit(id)}
    >
      {data.runState && data.runState !== 'skipped' && (
        <span className={`absolute -top-2 -right-2 text-[9px] font-semibold px-1.5 rounded-full border bg-white ${
          data.runState === 'ran' ? 'text-emerald-700 border-emerald-300'
          : data.runState === 'simulated' ? 'text-sky-700 border-sky-300' : 'text-red-700 border-red-300'}`}>
          {data.runState === 'ran' ? 'ran' : data.runState === 'simulated' ? 'not written' : 'error'}
        </span>
      )}
      <Handle type="target" position={Position.Top} className="!w-2 !h-2 !bg-gray-400" />
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 flex-shrink-0" />
        <span className="text-xs font-semibold truncate flex-1">{data.label || data.nodeType}</span>
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <button type="button" className="p-0.5 hover:opacity-70" onClick={e => { e.stopPropagation(); data.onEdit(id); }} title="Configure">
            <Settings className="w-3 h-3" />
          </button>
          <button type="button" className="p-0.5 text-red-400 hover:text-red-600" onClick={e => { e.stopPropagation(); data.onDelete(id); }} title="Delete">
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
      {data.nodeType === 'entity_action' && (data.config as any)?._status_value && (
        <p className="text-[10px] opacity-60 mt-0.5 truncate">→ {(data.config as any)._status_value}</p>
      )}
      {data.nodeType === 'switch' && (data.config as any)?.field && (
        <p className="text-[10px] opacity-60 mt-0.5 truncate">
          {(data.config as any).field?.split('.').pop()}: {((data.config as any).cases ?? []).filter((c: string) => c).join(' / ') || 'no cases'}
        </p>
      )}
      {data.nodeType === 'condition' && (data.config as any)?.field && (
        <p className="text-[10px] opacity-60 mt-0.5 truncate">
          {(data.config as any).field?.split('.').pop()} {(data.config as any).operator ?? 'eq'}{(data.config as any).value ? ` ${(data.config as any).value}` : ''}
        </p>
      )}
      <Handle type="source" position={Position.Bottom} className="!w-2 !h-2 !bg-gray-400" />
    </div>
  );
};

const rfEditorNodeTypes = { dagEditorNode: DAGEditorNodeComponent };

interface EdgeConfigModalProps {
  edge: DAGEdge;
  /** The step the arrow starts from — enables one-click Yes/No or switch-case picks. */
  sourceNode?: DAGNode;
  contextVars: ContextVar[];
  onSave: (e: DAGEdge) => void;
  onDelete: () => void;
  onClose: () => void;
}

const EdgeConfigModal: React.FC<EdgeConfigModalProps> = ({ edge, sourceNode, contextVars, onSave, onDelete, onClose }) => {
  const [label, setLabel]             = useState(edge.label ?? '');
  const [hasCondition, setHasCond]    = useState(!!edge.condition);
  const [field, setField]             = useState(edge.condition?.field ?? '');
  const [operator, setOperator]       = useState(edge.condition?.operator ?? 'eq');
  const [value, setValue]             = useState(String(edge.condition?.value ?? ''));

  // One-click branches for yes/no checks and switch cases
  const quickPicks: Array<{ label: string; field: string; operator: string; value: string }> = [];
  if (sourceNode?.type === 'condition') {
    const res = `node_results.${sourceNode.id}.output.result`;
    quickPicks.push({ label: sourceNode.config?.true_label || 'Yes', field: res, operator: 'truthy', value: '' });
    quickPicks.push({ label: sourceNode.config?.false_label || 'No', field: res, operator: 'falsy', value: '' });
  } else if (sourceNode?.type === 'switch') {
    ((sourceNode.config?.cases ?? []) as string[]).filter(c => String(c ?? '').trim()).forEach(c =>
      quickPicks.push({ label: c, field: `node_results.${sourceNode.id}.output.case`, operator: 'eq', value: c }));
    quickPicks.push({ label: 'Otherwise', field: `node_results.${sourceNode.id}.output.otherwise`, operator: 'truthy', value: '' });
  }
  const isPicked = (q: typeof quickPicks[number]) =>
    hasCondition && field === q.field && operator === q.operator && (VALUE_FREE_OPS.has(q.operator) || value === q.value);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-gray-200 shadow-2xl w-full max-w-sm mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <p className="text-sm font-semibold text-gray-900">Configure connection</p>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          {quickPicks.length > 0 && (
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
                {sourceNode?.type === 'switch' ? 'This arrow is for' : 'Follow this arrow when the check is'}
              </label>
              <div className="flex flex-wrap gap-1.5">
                {quickPicks.map(q => (
                  <button key={`${q.operator}:${q.value}:${q.label}`} type="button"
                    onClick={() => { setHasCond(true); setField(q.field); setOperator(q.operator); setValue(q.value); setLabel(q.label); }}
                    className={`px-2.5 py-1 text-xs rounded-lg border ${isPicked(q)
                      ? 'bg-amber-100 border-amber-300 text-amber-900 font-medium'
                      : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
                    {q.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Label (optional)</label>
            <input
              className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-gray-200"
              value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Yes / No / On success"
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" checked={hasCondition} onChange={e => setHasCond(e.target.checked)} className="rounded" />
            <span className="text-xs font-semibold text-gray-700">Only follow this path when a condition is met</span>
          </label>
          {hasCondition && (
            <div className="rounded-xl border border-amber-100 bg-amber-50/50 p-3 space-y-2.5">
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Check value</label>
                <VariablePicker allowNull={false} value={field} contextVars={contextVars} placeholder="e.g. entity.status" onChange={v => setField(v ?? '')} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Condition</label>
                  <select className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none"
                    value={operator} onChange={e => setOperator(e.target.value)}>
                    {EDGE_OPERATORS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                {!VALUE_FREE_OPS.has(operator) && (
                  <div>
                    <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Compare to</label>
                    <VariablePicker value={value} contextVars={contextVars} placeholder="value" onChange={v => setValue(v ?? '')} />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50">
          <button type="button" onClick={onDelete} className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700">
            <Trash2 className="w-3 h-3" /> Remove connection
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-xs text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-100">Cancel</button>
            <button
              onClick={() => onSave({ ...edge, label: label || undefined, condition: hasCondition && field ? { field, operator, value: VALUE_FREE_OPS.has(operator) ? undefined : value } : undefined })}
              className="px-3 py-1.5 text-xs text-white bg-gray-900 rounded-lg hover:bg-gray-800"
            >Save</button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface DAGCanvasEditorProps {
  nodes: DAGNode[];
  edges: DAGEdge[];
  states: WorkflowState[];
  contextVars: ContextVar[];
  onChange: (nodes: DAGNode[], edges: DAGEdge[]) => void;
  onAddNode: (action: ExplicitAction, position: { x: number; y: number }) => void;
  orgId?: number;
  sharedOrgIds?: number[];
  // Step export / import
  workflowName?: string;
  secrets?: SecretRef[];
  /** Next free step id in this workflow's scheme (node_N). Import is hidden without it. */
  nextNodeId?: () => string;
  onAddSecrets?: (refs: SecretRef[]) => void;
  /** From the last test run: real outputs (for the data picker) and per-step outcome. */
  sampleOutputs?: Record<string, any>;
  runStates?: Record<string, string>;
  /** Shared (no-owner) definitions — scopes step-library calls. */
  isShared?: boolean;
}

const DAGCanvasEditor: React.FC<DAGCanvasEditorProps> = ({
  nodes, edges, states, contextVars, onChange, onAddNode, orgId, sharedOrgIds,
  workflowName, secrets, nextNodeId, onAddSecrets, sampleOutputs, runStates, isShared,
}) => {
  const [editingNodeId, setEditingNodeId]   = useState<string | null>(null);
  const importInputRef = useRef<HTMLInputElement>(null);
  const [importNote, setImportNote] = useState<{ kind: 'ok' | 'warn' | 'error'; text: string; details?: string[] } | null>(null);
  const [editingEdge, setEditingEdge]       = useState<DAGEdge | null>(null);
  const [showNodePicker, setShowNodePicker] = useState(false);
  const [pendingPos, setPendingPos]         = useState<{ x: number; y: number }>({ x: 200, y: 200 });

  /** Compute positions via topological sort so nodes flow top-to-bottom in execution order */
  const computeAutoLayout = useCallback((ns: DAGNode[], es: DAGEdge[]): Record<string, { x: number; y: number }> => {
    // Build adjacency & in-degree
    const inDegree: Record<string, number> = {};
    const children: Record<string, string[]> = {};
    ns.forEach(n => { inDegree[n.id] = 0; children[n.id] = []; });
    es.forEach(e => {
      if (inDegree[e.target] !== undefined) inDegree[e.target]++;
      if (children[e.source]) children[e.source].push(e.target);
    });

    // Kahn's BFS topological sort → assign layer (rank)
    const rank: Record<string, number> = {};
    const queue = ns.filter(n => inDegree[n.id] === 0).map(n => n.id);
    queue.forEach(id => { rank[id] = 0; });

    const processed = new Set<string>();
    while (queue.length) {
      const cur = queue.shift()!;
      if (processed.has(cur)) continue;
      processed.add(cur);
      (children[cur] ?? []).forEach(child => {
        rank[child] = Math.max(rank[child] ?? 0, (rank[cur] ?? 0) + 1);
        inDegree[child]--;
        if (inDegree[child] <= 0) queue.push(child);
      });
    }

    // Group nodes by rank, assign x within rank
    const byRank: Record<number, string[]> = {};
    ns.forEach(n => {
      const r = rank[n.id] ?? 0;
      (byRank[r] ??= []).push(n.id);
    });

    const X_SPACING = 280;
    const Y_SPACING = 150;
    const positions: Record<string, { x: number; y: number }> = {};

    Object.entries(byRank).forEach(([rankStr, ids]) => {
      const r = Number(rankStr);
      const totalWidth = (ids.length - 1) * X_SPACING;
      ids.forEach((id, col) => {
        positions[id] = { x: col * X_SPACING - totalWidth / 2 + 400, y: 80 + r * Y_SPACING };
      });
    });

    return positions;
  }, []);

  const nodeEditHandlers = useCallback((id: string) => setEditingNodeId(id), []);
  const nodeDeleteHandlers = useCallback((id: string) => {
    onChange(nodes.filter(n => n.id !== id), edges.filter(e => e.source !== id && e.target !== id));
  }, [nodes, edges, onChange]);

  const initialLayout = computeAutoLayout(nodes, edges);
  const rfNodes: RFEditorNode[] = nodes.map((n) => ({
    id: n.id,
    type: 'dagEditorNode',
    position: (n as any).position ?? (n.config as any)?.position ?? initialLayout[n.id] ?? { x: 400, y: 80 },
    data: {
      nodeType: n.type,
      label: n.label || n.type,
      config: n.config,
      runState: runStates?.[n.id],
      onEdit: nodeEditHandlers,
      onDelete: nodeDeleteHandlers,
    },
  }));

  const rfEdges: Edge[] = edges.map(e => ({
    id: e.id,
    source: e.source,
    target: e.target,
    label: e.label || (e.condition ? `${e.condition.field?.split('.').pop()} ${e.condition.operator}${e.condition.value !== undefined ? ' ' + e.condition.value : ''}` : undefined),
    markerEnd: { type: MarkerType.ArrowClosed },
    style: { stroke: e.condition ? '#f59e0b' : '#9ca3af', strokeWidth: 1.5 },
    labelStyle: { fontSize: 10, fill: '#6b7280' },
    labelBgStyle: { fill: '#f9fafb', fillOpacity: 0.9 },
    animated: !!e.condition,
  }));

  const [rn, setRn, onRnC] = useNodesState<RFEditorNode>(rfNodes);
  const [re, setRe, onReC] = useEdgesState(rfEdges);

  // Sync external nodes/edges → RF state
  useEffect(() => {
    const layout = computeAutoLayout(nodes, edges);
    setRn(prevRn => {
      // Build a map of existing RF positions so dragged positions are preserved
      const existingPositions: Record<string, { x: number; y: number }> = {};
      prevRn.forEach(rn => { existingPositions[rn.id] = rn.position; });

      return nodes.map((n) => ({
        id: n.id,
        type: 'dagEditorNode',
        // Priority: 1) position saved on the DAG node (from drag), 2) existing RF position, 3) auto-layout, 4) fallback
        position: (n as any).position ?? (n.config as any)?.position ?? existingPositions[n.id] ?? layout[n.id] ?? { x: 400, y: 80 },
        data: {
          nodeType: n.type,
          label: n.label || n.type,
          config: n.config,
          runState: runStates?.[n.id],
          onEdit: nodeEditHandlers,
          onDelete: nodeDeleteHandlers,
        },
      }));
    });
    setRe(edges.map(e => ({
      id: e.id,
      source: e.source,
      target: e.target,
      label: e.label || (e.condition ? `${e.condition.field?.split('.').pop()} ${e.condition.operator}${e.condition.value !== undefined ? ' ' + e.condition.value : ''}` : undefined),
      markerEnd: { type: MarkerType.ArrowClosed },
      style: { stroke: e.condition ? '#f59e0b' : '#9ca3af', strokeWidth: 1.5 },
      labelStyle: { fontSize: 10, fill: '#6b7280' },
      labelBgStyle: { fill: '#f9fafb', fillOpacity: 0.9 },
      animated: !!e.condition,
    })));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, edges, runStates]);

  // Pass through React Flow internal changes (selection, viewport, etc.) without touching parent state
  const onNodesChange = useCallback((changes: any) => {
    onRnC(changes);
  }, [onRnC]);

  // Persist drag positions back to parent — fires only after an actual user
  // drag completes. Every step gets its on-screen position (auto-laid-out ones
  // included) so the saved layout is exactly what the user sees.
  const onNodeDragStop = useCallback((_: any, node: any, dragged?: any[]) => {
    const pos: Record<string, { x: number; y: number }> = {};
    rn.forEach(r => { pos[r.id] = r.position; });
    (dragged ?? [node]).forEach((d: any) => { pos[d.id] = d.position; });
    onChange(
      nodes.map(n => (pos[n.id] ? { ...n, position: pos[n.id] } : n)),
      edges,
    );
  }, [nodes, edges, onChange, rn]);

  // Create a new edge when user draws a connection
  const onConnect = useCallback((conn: Connection) => {
    if (!conn.source || !conn.target) return;
    const newEdge: DAGEdge = {
      id: `e_${conn.source}_${conn.target}_${Date.now()}`,
      source: conn.source,
      target: conn.target,
    };
    onChange(nodes, [...edges, newEdge]);
    // Open edge config immediately
    setEditingEdge(newEdge);
  }, [nodes, edges, onChange]);

  // Click edge label → edit
  const onEdgeClick = useCallback((_: any, edge: Edge) => {
    const dagEdge = edges.find(e => e.id === edge.id);
    if (dagEdge) setEditingEdge(dagEdge);
  }, [edges]);

  const editingNode = editingNodeId ? nodes.find(n => n.id === editingNodeId) as StepNode | undefined : undefined;

  const selectedIds = rn.filter(n => n.selected).map(n => n.id);

  // Data pickers only offer outputs of steps that are connected BEFORE the
  // step being edited (or, for an arrow, before/at its source step). Picking a
  // step that isn't upstream only works by accident of step order.
  const baseVars = contextVars.filter(v => !v.path.startsWith('node_results.'));
  const editingNodeVars = editingNode
    ? [...baseVars, ...buildNodeResultVars(editingNode.id, nodes, edges, sampleOutputs)]
    : contextVars;
  const editingNodeHasInputs = !!editingNode && edges.some(e => e.target === editingNode.id);
  const editingEdgeVars = editingEdge
    ? [
        ...baseVars,
        ...buildNodeResultVars(editingEdge.source, nodes, edges, sampleOutputs),
        ...buildNodeResultVars('', nodes.filter(n => n.id === editingEdge.source), [], sampleOutputs),
      ]
    : contextVars;

  /** Download the given steps; `override` carries unsaved edits from the step editor. */
  const exportSteps = (ids: string[], override?: DAGNode) => {
    const list = override ? nodes.map(n => (n.id === override.id ? override : n)) : nodes;
    const positions = Object.fromEntries(rn.map(n => [n.id, n.position]));
    const data = buildStepsExport(ids, list, edges, secrets ?? [], positions, workflowName);
    const base = ids.length === 1
      ? (list.find(n => n.id === ids[0])?.label || ids[0])
      : `${workflowName || 'workflow'}-${ids.length}-steps`;
    downloadJson(`${slugify(base)}.steps.json`, data);
  };

  /** Add steps (from a file, the clipboard or the library): fresh ids, references between them rewritten, placed below the canvas. */
  const addSteps = (data: StepsExport, verb = 'Imported') => {
    try {
      if (!nextNodeId) throw new Error('adding steps is not available here');
      const maxY = rn.length ? Math.max(...rn.map(n => n.position.y)) : -150;
      const minX = rn.length ? Math.min(...rn.map(n => n.position.x)) : 200;
      const res = remapImportedSteps(data, nodes, secrets ?? [], nextNodeId, { x: minX, y: maxY + 200 });
      onChange([...nodes, ...res.nodes], [...edges, ...res.edges]);
      if (res.secretsToAdd.length) onAddSecrets?.(res.secretsToAdd);

      const details: string[] = [];
      for (const d of res.dangling) {
        const clash = d.refs.some(r => nodes.some(n => n.id === r));
        details.push(
          `"${d.label}" (${d.nodeId}) still reads ${d.refs.map(r => `node_results.${r}`).join(', ')}, `
          + `which wasn't part of the import. Re-map its inputs`
          + (clash ? ': this workflow has its own step with that id, so it would read the wrong data.' : '.'),
        );
      }
      if (res.secretsToAdd.length) {
        details.push(`Added secret reference${res.secretsToAdd.length > 1 ? 's' : ''}: ${res.secretsToAdd.map(s => s.name).join(', ')}.`);
      }
      for (const name of res.secretConflicts) {
        details.push(`Secret "${name}" already exists here with a different Secret Manager id. Kept this workflow's.`);
      }
      if (res.nodes.some(n => n.config?.source_id)) {
        details.push('Integration steps keep their source. If this workflow runs for another organization, re-pick it.');
      }
      const n = res.nodes.length;
      setImportNote({
        kind: res.dangling.length || res.secretConflicts.length ? 'warn' : 'ok',
        text: `${verb} ${n} step${n > 1 ? 's' : ''}${data.source_workflow ? ` from "${data.source_workflow}"` : ''} as `
          + `${Object.values(res.idMap).join(', ')}. Connect ${n > 1 ? 'them' : 'it'} with an arrow, then save the workflow.`,
        details,
      });
    } catch (e) {
      setImportNote({ kind: 'error', text: `Couldn't add the steps: ${(e as Error).message}` });
    }
  };

  const importSteps = async (file: File) => {
    try { addSteps(parseStepsFile(await file.text())); }
    catch (e) { setImportNote({ kind: 'error', text: `Import failed: ${(e as Error).message}` }); }
  };

  // ── Copy / paste (⌘C / ⌘V, Ctrl on Windows) ─────────────────────────────
  // Uses the system clipboard so steps can be pasted into another workflow
  // or browser tab; falls back to an in-page clipboard.
  const canvasRef = useRef<HTMLDivElement>(null);
  const canvasActive = useRef(false);
  const copySelected = async () => {
    if (!selectedIds.length) return;
    const positions = Object.fromEntries(rn.map(n => [n.id, n.position]));
    const text = JSON.stringify(buildStepsExport(selectedIds, nodes, edges, secrets ?? [], positions, workflowName));
    stepsClipboard = text;
    try { await navigator.clipboard.writeText(text); } catch { /* in-page clipboard still has it */ }
    setImportNote({ kind: 'ok', text: `Copied ${selectedIds.length} step${selectedIds.length > 1 ? 's' : ''}. Paste with ⌘V / Ctrl+V here or in another workflow.` });
  };
  const pasteSteps = async () => {
    // System clipboard first; the in-page copy only when it can't be read
    // (so pasting after copying other text doesn't resurrect old steps).
    let text: string | null = null;
    try { text = await navigator.clipboard.readText(); } catch { text = stepsClipboard; }
    if (!text) return;
    try { addSteps(parseStepsFile(text), 'Pasted'); } catch { /* not steps — ignore */ }
  };
  // Re-subscribed every render on purpose, so the handlers always see current state.
  useEffect(() => {
    const onDown = (e: MouseEvent) => { canvasActive.current = !!canvasRef.current?.contains(e.target as Element); };
    const onKey = (e: KeyboardEvent) => {
      const modalOpen = !!editingNodeId || !!editingEdge || showNodePicker || libraryOpen || !!saveToLib;
      if (!canvasActive.current || modalOpen || !(e.metaKey || e.ctrlKey)) return;
      const el = document.activeElement as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable)) return;
      const k = e.key.toLowerCase();
      if (k === 'c' && selectedIds.length) { e.preventDefault(); copySelected(); }
      else if (k === 'v' && nextNodeId) { e.preventDefault(); pasteSteps(); }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  });

  // ── Step library ─────────────────────────────────────────────────────────
  const [libraryOpen, setLibraryOpen]   = useState(false);
  const [libraryItems, setLibraryItems] = useState<StepLibraryItem[] | null>(null);
  const [libraryError, setLibraryError] = useState('');
  const [libraryBusy, setLibraryBusy]   = useState<number | 'save' | null>(null);
  const [saveToLib, setSaveToLib]       = useState<{ name: string; description: string } | null>(null);
  const openLibrary = async () => {
    setLibraryOpen(true); setLibraryItems(null); setLibraryError('');
    try { const r = await workflowService.listStepLibrary(orgId, isShared); setLibraryItems(r.data ?? []); }
    catch { setLibraryItems([]); setLibraryError('The step library isn\'t available yet (the server may need updating).'); }
  };
  const addFromLibrary = async (item: StepLibraryItem) => {
    setLibraryBusy(item.id);
    try {
      const r = await workflowService.getStepLibraryItem(item.id, orgId, isShared);
      addSteps(parseStepsFile(JSON.stringify(r.data.steps ?? {})), `Added "${item.name}":`);
      setLibraryOpen(false);
    } catch (e: any) {
      setLibraryError(`Couldn't add "${item.name}": ${e?.message ?? 'error'}`);
    } finally { setLibraryBusy(null); }
  };
  const deleteFromLibrary = async (item: StepLibraryItem) => {
    if (!window.confirm(`Remove "${item.name}" from the library? Workflows that already use these steps are not affected.`)) return;
    setLibraryBusy(item.id);
    try {
      await workflowService.deleteStepLibraryItem(item.id, orgId, isShared);
      setLibraryItems(cur => (cur ?? []).filter(x => x.id !== item.id));
    } catch { setLibraryError(`Couldn't remove "${item.name}".`); }
    finally { setLibraryBusy(null); }
  };
  const saveSelectionToLibrary = async () => {
    if (!saveToLib?.name.trim() || !selectedIds.length) return;
    setLibraryBusy('save');
    try {
      const positions = Object.fromEntries(rn.map(n => [n.id, n.position]));
      const steps = buildStepsExport(selectedIds, nodes, edges, secrets ?? [], positions, workflowName);
      await workflowService.saveStepLibraryItem(
        { name: saveToLib.name.trim(), description: saveToLib.description.trim() || undefined, steps: steps as any },
        orgId, isShared,
      );
      setImportNote({ kind: 'ok', text: `Saved ${selectedIds.length} step${selectedIds.length > 1 ? 's' : ''} to the library as "${saveToLib.name.trim()}".` });
      setSaveToLib(null);
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setImportNote({ kind: 'error', text: `Couldn't save to the library: ${typeof detail === 'string' ? detail : 'the server may need updating'}` });
      setSaveToLib(null);
    } finally { setLibraryBusy(null); }
  };

  return (
    <div ref={canvasRef} className="flex flex-col" style={{ height: 560 }}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-gray-100 bg-white flex-shrink-0">
        <button
          type="button"
          onClick={() => { setPendingPos({ x: 200, y: 200 }); setShowNodePicker(true); }}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-800"
        >
          <Plus className="w-3.5 h-3.5" /> Add node
        </button>
        {nextNodeId && (
          <button
            type="button"
            onClick={() => importInputRef.current?.click()}
            title="Add steps exported from another workflow"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            <Upload className="w-3.5 h-3.5" /> Import steps
          </button>
        )}
        {nextNodeId && (
          <button
            type="button"
            onClick={openLibrary}
            title="Add steps your organization saved for reuse"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            <FolderOpen className="w-3.5 h-3.5" /> Library
          </button>
        )}
        {selectedIds.length > 0 && (
          <button
            type="button"
            onClick={() => setSaveToLib({ name: '', description: '' })}
            title="Save the selected steps to your organization's library"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            <Save className="w-3.5 h-3.5" /> Save to library
          </button>
        )}
        {selectedIds.length > 0 && (
          <button
            type="button"
            onClick={() => exportSteps(selectedIds)}
            title="Download the selected steps and the arrows between them"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            <Download className="w-3.5 h-3.5" /> Export selected ({selectedIds.length})
          </button>
        )}
        <input
          ref={importInputRef}
          type="file"
          accept=".json,application/json"
          className="hidden"
          onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) importSteps(f); }}
        />
        <span className="text-[10px] text-gray-400">Draw an arrow between nodes to connect them. Click a connection to add a condition. Shift-drag to select several steps; ⌘C / ⌘V to copy and paste them.</span>
      </div>
      {importNote && (
        <div className={`flex items-start gap-2 px-3 py-2 border-b text-xs flex-shrink-0 ${
          importNote.kind === 'error' ? 'bg-red-50 border-red-200 text-red-700'
          : importNote.kind === 'warn' ? 'bg-amber-50 border-amber-200 text-amber-800'
          : 'bg-emerald-50 border-emerald-200 text-emerald-800'}`}>
          {importNote.kind === 'ok'
            ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            : <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />}
          <div className="flex-1 space-y-0.5">
            <p>{importNote.text}</p>
            {importNote.details?.map((d, i) => <p key={i} className="opacity-80">• {d}</p>)}
          </div>
          <button type="button" onClick={() => setImportNote(null)} className="p-0.5 opacity-60 hover:opacity-100">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      <div style={{ flex: 1, minHeight: 0, width: '100%' }}>
        <ReactFlow
          nodes={rn}
          edges={re}
          nodeTypes={rfEditorNodeTypes}
          onNodesChange={onNodesChange}
          onNodeDragStop={onNodeDragStop}
          onEdgesChange={onReC}
          onConnect={onConnect}
          onEdgeClick={onEdgeClick}
          fitView
          fitViewOptions={{ padding: 0.3 }}
          deleteKeyCode="Delete"
          style={{ width: '100%', height: '100%' }}
        >
          <Background gap={16} size={1} color="#e5e7eb" />
          <Controls />
          <MiniMap nodeStrokeWidth={2} />
        </ReactFlow>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 px-3 py-1.5 border-t border-gray-100 bg-gray-50 text-[10px] text-gray-400 flex-shrink-0">
        <span>⬛ Gray arrow = always run</span>
        <span>🟡 Amber arrow = conditional</span>
        <span>Double-click node = configure</span>
        <span>Delete key = remove selected</span>
      </div>

      {libraryOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={() => setLibraryOpen(false)}>
          <div className="bg-white rounded-2xl border border-gray-200 shadow-2xl w-full max-w-xl mx-4 overflow-hidden flex flex-col max-h-[80vh]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <p className="text-sm font-semibold text-gray-900 flex items-center gap-2"><FolderOpen className="w-4 h-4" /> Step library</p>
              <button onClick={() => setLibraryOpen(false)} className="p-1 text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
            </div>
            <div className="overflow-y-auto px-5 py-3 space-y-1.5">
              {libraryError && <p className="text-xs text-red-600">{libraryError}</p>}
              {libraryItems === null && <p className="text-xs text-gray-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</p>}
              {libraryItems?.length === 0 && !libraryError && (
                <p className="text-xs text-gray-400">Nothing saved yet. Select steps on the canvas and click <strong>Save to library</strong>.</p>
              )}
              {libraryItems?.map(item => (
                <div key={item.id} className="flex items-start gap-3 border border-gray-100 rounded-lg px-3 py-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-900">{item.name}</p>
                    {item.description && <p className="text-[11px] text-gray-500">{item.description}</p>}
                    <p className="text-[10px] text-gray-400 truncate">
                      {item.step_count} step{item.step_count === 1 ? '' : 's'} · {item.step_types.join(', ')}
                      {item.created_by ? ` · ${item.created_by}` : ''}
                    </p>
                  </div>
                  <button onClick={() => addFromLibrary(item)} disabled={libraryBusy !== null}
                    className="flex items-center gap-1 px-2.5 py-1 text-xs border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                    {libraryBusy === item.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Add
                  </button>
                  <button onClick={() => deleteFromLibrary(item)} disabled={libraryBusy !== null} title="Remove from library"
                    className="p-1.5 text-gray-300 hover:text-red-500 disabled:opacity-50">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {saveToLib && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={() => setSaveToLib(null)}>
          <div className="bg-white rounded-2xl border border-gray-200 shadow-2xl w-full max-w-sm mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-100">
              <p className="text-sm font-semibold text-gray-900">Save {selectedIds.length} step{selectedIds.length > 1 ? 's' : ''} to the library</p>
            </div>
            <div className="px-5 py-4 space-y-2.5">
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Name *</label>
                <input autoFocus className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-gray-200"
                  placeholder="e.g. MG importer stock + price" value={saveToLib.name}
                  onChange={e => setSaveToLib(v => v && { ...v, name: e.target.value })}
                  onKeyDown={e => { if (e.key === 'Enter') saveSelectionToLibrary(); }} />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Description</label>
                <textarea rows={2} className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-gray-200"
                  placeholder="What it does and what it expects (optional)" value={saveToLib.description}
                  onChange={e => setSaveToLib(v => v && { ...v, description: e.target.value })} />
              </div>
              <p className="text-[10px] text-gray-400">Everyone in your organization can add these steps to their workflows. Changing them later in a workflow doesn't change the library copy.</p>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-100 bg-gray-50">
              <button onClick={() => setSaveToLib(null)} className="px-3 py-1.5 text-xs text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-100">Cancel</button>
              <button onClick={saveSelectionToLibrary} disabled={!saveToLib.name.trim() || libraryBusy === 'save'}
                className="px-3 py-1.5 text-xs text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50">
                {libraryBusy === 'save' ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showNodePicker && (
        <ActionPicker
          onPick={action => { onAddNode(action, pendingPos); setShowNodePicker(false); }}
          onClose={() => setShowNodePicker(false)}
          orgId={orgId}
        />
      )}

      {editingNode && (
        <NodeConfigModal
          node={editingNode}
          states={states}
          contextVars={editingNodeVars}
          upstreamHint={editingNodeHasInputs ? undefined
            : 'Connect an arrow into this step to use outputs of earlier steps.'}
          orgId={orgId}
          sharedOrgIds={sharedOrgIds}
          onExport={n => exportSteps([n.id], n)}
          onSave={updated => {
            onChange(nodes.map(n => n.id === updated.id ? { ...updated } : n), edges);
            setEditingNodeId(null);
          }}
          onClose={(updated) => {
            // Auto-apply unsaved edits when the panel is dismissed (e.g. clicking outside)
            if (updated) {
              onChange(nodes.map(n => n.id === updated.id ? { ...updated } : n), edges);
            }
            setEditingNodeId(null);
          }}
        />
      )}

      {editingEdge && (
        <EdgeConfigModal
          key={editingEdge.id}
          edge={editingEdge}
          sourceNode={nodes.find(n => n.id === editingEdge.source)}
          contextVars={editingEdgeVars}
          onSave={updated => {
            onChange(nodes, edges.map(e => e.id === updated.id ? updated : e));
            setEditingEdge(null);
          }}
          onDelete={() => {
            onChange(nodes, edges.filter(e => e.id !== editingEdge.id));
            setEditingEdge(null);
          }}
          onClose={() => setEditingEdge(null)}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// DAGCanvasReadOnly — lightweight read-only React Flow preview
// ---------------------------------------------------------------------------

interface DAGNodeData extends Record<string, unknown> {
  nodeType: string;
  label: string;
  executionStatus?: string;
}
type RFDAGNode = Node<DAGNodeData>;

const WorkflowNodeComponent = ({ data, selected }: NodeProps & { data: DAGNodeData }) => {
  const colorClass = NODE_COLORS[data.nodeType] ?? 'bg-gray-50 border-gray-300 text-gray-800';
  const Icon = NODE_ICON_MAP[data.nodeType] ?? Circle;
  return (
    <div className={`rounded-xl border-2 px-4 py-3 min-w-[160px] max-w-[220px] shadow-sm cursor-pointer ${colorClass} ${selected ? 'ring-2 ring-offset-1 ring-gray-900' : ''}`}>
      <Handle type="target" position={Position.Top} className="!w-2 !h-2 !bg-gray-400" />
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 flex-shrink-0" />
        <span className="text-xs font-semibold truncate">{data.label || data.nodeType}</span>
      </div>
      {data.executionStatus && (
        <span className="mt-1 block text-xs opacity-60 capitalize">{data.executionStatus}</span>
      )}
      <Handle type="source" position={Position.Bottom} className="!w-2 !h-2 !bg-gray-400" />
    </div>
  );
};
const rfNodeTypes = { dagNode: WorkflowNodeComponent };

const DAGCanvasReadOnly: React.FC<{ nodes: DAGNode[]; edges: DAGEdge[] }> = ({ nodes, edges }) => {
  const rfNodes: RFDAGNode[] = nodes.map((n, i) => ({
    id: n.id, type: 'dagNode',
    position: n.config?.position as any ?? { x: 120, y: 80 + i * 140 },
    data: { nodeType: n.type, label: n.label || n.type },
  }));
  const rfEdges: Edge[] = edges.map(e => ({
    id: e.id, source: e.source, target: e.target,
    label: e.label || (e.condition ? `${e.condition.field} ${e.condition.operator}` : undefined),
    markerEnd: { type: MarkerType.ArrowClosed },
  }));

  const [rn,, onRnC] = useNodesState<RFDAGNode>(rfNodes);
  const [re,, onReC] = useEdgesState(rfEdges);

  return (
    <ReactFlow nodes={rn} edges={re} nodeTypes={rfNodeTypes}
      onNodesChange={onRnC} onEdgesChange={onReC}
      fitView fitViewOptions={{ padding: 0.3 }} nodesDraggable={false} nodesConnectable={false}>
      <Background gap={16} size={1} color="#e5e7eb" />
      <Controls showInteractive={false} />
    </ReactFlow>
  );
};

// ---------------------------------------------------------------------------
// Execution Panel
// ---------------------------------------------------------------------------

const ExecutionPanel: React.FC<{ executions: DAGExecution[]; loading: boolean; onRefresh: () => void; onClose: () => void }> = ({ executions, loading, onRefresh, onClose }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-gray-900">Execution History</span>
        {executions.length > 0 && <span className="text-xs text-gray-400 font-normal">({executions.length})</span>}
      </div>
      <div className="flex items-center gap-1">
        <button onClick={onRefresh} disabled={loading} className="p-1 text-gray-400 hover:text-gray-600 rounded disabled:opacity-40">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
        <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded"><X className="w-4 h-4" /></button>
      </div>
    </div>
    {loading ? (
      <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
    ) : executions.length === 0 ? (
      <div className="text-center py-6">
        <Play className="w-6 h-6 mx-auto mb-2 text-gray-300" />
        <p className="text-sm text-gray-400">No executions yet.</p>
      </div>
    ) : (
      <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
        {executions.map(ex => (
          <div key={ex.id} className="border border-gray-200 rounded-lg p-3 space-y-1.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge label={ex.status} color={execStatusColor(ex.status)} />
                <span className="text-xs text-gray-500">{ex.event_type}</span>
              </div>
              <span className="text-xs text-gray-400">{new Date(ex.started_at).toLocaleString()}</span>
            </div>
            {ex.entity_id && (
              <div className="text-xs text-gray-500">Entity: <span className="font-mono">{ex.entity_type}/{ex.entity_id}</span></div>
            )}
            {ex.error_message && <div className="text-xs text-red-600 bg-red-50 rounded px-2 py-1">{ex.error_message}</div>}
            {Object.entries(ex.node_results ?? {}).length > 0 && (
              <div className="space-y-1 pt-1 border-t border-gray-100 mt-1.5">
                {Object.entries(ex.node_results).map(([nid, r]: [string, any]) => (
                  <div key={nid} className="flex items-center gap-2 text-xs text-gray-600">
                    <Badge label={r.status ?? '?'} color={execStatusColor(r.status)} />
                    <span className="font-mono text-gray-400 truncate">{nid}</span>
                    {r.error && <span className="text-red-500 truncate max-w-xs">{r.error}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// State Machine Tab (unchanged)
// ---------------------------------------------------------------------------

interface StateMachineTabProps {
  config: StateConfigResponse;
  onConfigChange: (c: StateConfigResponse) => void;
  orgId?: number;
}

const StateMachineTab: React.FC<StateMachineTabProps> = ({ config, onConfigChange, orgId }) => {
  const { t } = useTranslation();
  const [saving, setSaving]     = useState(false);
  const [resetting, setReset]   = useState(false);
  const [toast, setToast]       = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const [showAddS, setShowAddS] = useState(false);
  const [newState, setNewState] = useState<Omit<WorkflowState, 'id'>>({ key: '', label: '', is_initial: false, is_terminal: false });
  const [showAddT, setShowAddT] = useState(false);
  const [newTrans, setNewTrans] = useState<Omit<WorkflowTransition, 'id'>>({ from_state: '', to_state: '', label: '', allowed_actors: ['user'] });

  // When orgId is undefined we are in "shared/default" mode
  const isShared = orgId === undefined;

  const flash = (msg: string, type: 'success' | 'error') => {
    setToast({ msg, type }); setTimeout(() => setToast(null), 3000);
  };

  const handleSave = async () => {
    setSaving(true);
    try { const r = await workflowService.saveStateConfig({ states: config.states, transitions: config.transitions }, orgId, isShared); onConfigChange(r.data); flash('Saved', 'success'); }
    catch { flash('Save failed', 'error'); }
    finally { setSaving(false); }
  };
  const handleReset = async () => {
    if (!window.confirm('Reset to default?')) return;
    setReset(true);
    try { const r = await workflowService.resetStateConfig(orgId, isShared); onConfigChange(r.data); flash('Reset', 'success'); }
    catch { flash('Reset failed', 'error'); }
    finally { setReset(false); }
  };
  const handleAddState = async () => {
    try { const r = await workflowService.addState(newState, orgId, isShared); onConfigChange(r.data); setShowAddS(false); setNewState({ key: '', label: '', is_initial: false, is_terminal: false }); flash('State added', 'success'); }
    catch { flash('Failed', 'error'); }
  };
  const handleDelState = async (key: string) => {
    if (!window.confirm(`Delete state "${key}"?`)) return;
    try { const r = await workflowService.deleteState(key, orgId, isShared); onConfigChange(r.data); flash('Deleted', 'success'); }
    catch { flash('Failed', 'error'); }
  };
  const handleAddTrans = async () => {
    try { const r = await workflowService.addTransition(newTrans, orgId, isShared); onConfigChange(r.data); setShowAddT(false); setNewTrans({ from_state: '', to_state: '', label: '', allowed_actors: ['user'] }); flash('Transition added', 'success'); }
    catch { flash('Failed', 'error'); }
  };
  const handleDelTrans = async (id: string) => {
    if (!window.confirm('Delete transition?')) return;
    try { const r = await workflowService.deleteTransition(id, orgId, isShared); onConfigChange(r.data); flash('Deleted', 'success'); }
    catch { flash('Failed', 'error'); }
  };

  return (
    <div className="space-y-6">
      {toast && (
        <div className={`fixed top-4 right-4 z-[100] flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium
          ${toast.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
          {toast.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {toast.msg}
        </div>
      )}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{t('workflows.stateMachine.title', 'Order State Machine')}</h2>
          <p className="text-sm text-gray-500 mt-0.5">{t('workflows.stateMachine.subtitle', 'Define valid statuses and allowed transitions.')}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleReset} disabled={resetting}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
            {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Reset
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* States */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">States</h3>
            <button onClick={() => setShowAddS(p => !p)} className="flex items-center gap-1 text-xs text-gray-700 hover:text-gray-900 font-medium">
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
          </div>
          {showAddS && (
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <input className="border border-gray-300 rounded px-2 py-1.5 text-xs" placeholder="key"
                  value={newState.key} onChange={e => setNewState(p => ({ ...p, key: e.target.value }))} />
                <input className="border border-gray-300 rounded px-2 py-1.5 text-xs" placeholder="label"
                  value={newState.label} onChange={e => setNewState(p => ({ ...p, label: e.target.value }))} />
              </div>
              <div className="flex gap-4 text-xs text-gray-600">
                <label className="flex items-center gap-1 cursor-pointer">
                  <input type="checkbox" checked={newState.is_initial} onChange={e => setNewState(p => ({ ...p, is_initial: e.target.checked }))} /> Initial
                </label>
                <label className="flex items-center gap-1 cursor-pointer">
                  <input type="checkbox" checked={newState.is_terminal} onChange={e => setNewState(p => ({ ...p, is_terminal: e.target.checked }))} /> Terminal
                </label>
              </div>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setShowAddS(false)} className="text-xs text-gray-500 px-2 py-1">Cancel</button>
                <button onClick={handleAddState} className="text-xs text-white bg-gray-800 px-3 py-1 rounded hover:bg-gray-700">Add</button>
              </div>
            </div>
          )}
          <div className="space-y-1">
            {config.states.map(s => (
              <div key={s.key} className="group flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-gray-50">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono text-gray-600">{s.key}</span>
                  <span className="text-xs text-gray-800">{s.label}</span>
                  {s.is_initial && <Badge label="initial" color="green" />}
                  {s.is_terminal && <Badge label="terminal" color="red" />}
                </div>
                <button onClick={() => handleDelState(s.key)} className="p-1 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 rounded">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
        {/* Transitions */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Transitions</h3>
            <button onClick={() => setShowAddT(p => !p)} className="flex items-center gap-1 text-xs text-gray-700 hover:text-gray-900 font-medium">
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
          </div>
          {showAddT && (
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <select className="border border-gray-300 rounded px-2 py-1.5 text-xs bg-white"
                  value={newTrans.from_state} onChange={e => setNewTrans(p => ({ ...p, from_state: e.target.value }))}>
                  <option value="">From…</option>
                  {config.states.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
                <select className="border border-gray-300 rounded px-2 py-1.5 text-xs bg-white"
                  value={newTrans.to_state} onChange={e => setNewTrans(p => ({ ...p, to_state: e.target.value }))}>
                  <option value="">To…</option>
                  {config.states.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </div>
              <input className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs"
                placeholder="Label" value={newTrans.label} onChange={e => setNewTrans(p => ({ ...p, label: e.target.value }))} />
              <div className="flex flex-wrap gap-2 text-xs">
                {ACTOR_OPTIONS.map(a => (
                  <label key={a} className="flex items-center gap-1 cursor-pointer text-gray-600">
                    <input type="checkbox" checked={newTrans.allowed_actors.includes(a)}
                      onChange={e => setNewTrans(p => ({
                        ...p,
                        allowed_actors: e.target.checked ? [...p.allowed_actors, a] : p.allowed_actors.filter(x => x !== a),
                      }))} />
                    {a}
                  </label>
                ))}
              </div>
              <div className="flex gap-2 justify-end">
                <button onClick={() => setShowAddT(false)} className="text-xs text-gray-500 px-2 py-1">Cancel</button>
                <button onClick={handleAddTrans} className="text-xs text-white bg-gray-800 px-3 py-1 rounded hover:bg-gray-700">Add</button>
              </div>
            </div>
          )}
          <div className="space-y-1">
            {config.transitions.map(tr => (
              <div key={tr.id} className="group flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-gray-50">
                <div className="flex items-center gap-2 text-xs">
                  <Badge label={tr.from_state} color="gray" />
                  <ArrowRight className="w-3 h-3 text-gray-400" />
                  <Badge label={tr.to_state} color="blue" />
                  {tr.label && <span className="text-gray-500">{tr.label}</span>}
                  <span className="text-gray-400">[{tr.allowed_actors.join(', ')}]</span>
                </div>
                <button onClick={() => handleDelTrans(tr.id)} className="p-1 text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 rounded">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Automations Tab
// ---------------------------------------------------------------------------
// CopyToOrgModal
// ---------------------------------------------------------------------------

interface CopyToOrgModalProps {
  def: DAGDefinition;
  currentOrgId?: number;
  onClose: () => void;
  onCopied: (msg: string) => void;
}

const CopyToOrgModal: React.FC<CopyToOrgModalProps> = ({ def, currentOrgId, onClose, onCopied }) => {
  const [orgs, setOrgs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [targetOrgId, setTargetOrgId] = useState<number | ''>('');
  const [copying, setCopying] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    adminService.getOrganizations()
      .then(r => setOrgs((r.data.organizations ?? []).filter((o: any) => o.id !== currentOrgId)))
      .catch(() => setError('Failed to load organizations.'))
      .finally(() => setLoading(false));
  }, [currentOrgId]);

  const handleCopy = async () => {
    if (!targetOrgId) return;
    setCopying(true); setError('');
    try {
      await workflowService.createDAGDefinition({
        name: def.name,
        description: def.description,
        trigger: def.trigger,
        nodes: def.nodes,
        edges: def.edges,
        is_active: false,
      }, targetOrgId as number);
      const orgName = orgs.find(o => o.id === targetOrgId)?.name ?? `#${targetOrgId}`;
      onCopied(`Copied "${def.name}" to ${orgName}`);
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Failed to copy workflow.');
    } finally {
      setCopying(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-gray-200 shadow-2xl w-full max-w-sm mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Copy className="w-4 h-4 text-gray-500" />
            <p className="text-sm font-semibold text-gray-900">Copy to another organisation</p>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <p className="text-xs text-gray-500">
            Copying <strong>{def.name}</strong> — the workflow will be created as <em>inactive</em> in the target org.
          </p>
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
          ) : (
            <div>
              <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Target organisation</label>
              <select
                className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-teal-200"
                value={targetOrgId}
                onChange={e => setTargetOrgId(e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">— select —</option>
                {orgs.map((o: any) => (
                  <option key={o.id} value={o.id}>{o.name} (#{o.id})</option>
                ))}
              </select>
            </div>
          )}
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50">Cancel</button>
          <button
            onClick={handleCopy}
            disabled={!targetOrgId || copying || loading}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-xl hover:bg-gray-800 disabled:opacity-50"
          >
            {copying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
            Copy workflow
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------

interface AutomationsTabProps {
  states: WorkflowState[];
  orgId?: number;
  isShared?: boolean;
}

const AutomationsTab: React.FC<AutomationsTabProps> = ({ states, orgId, isShared }) => {
  const [definitions, setDefs]     = useState<DAGDefinition[]>([]);
  const [loading, setLoading]       = useState(true);
  const [eventTypes, setEventTypes] = useState<EventTypeInfo[]>([]);
  const [showForm, setShowForm]     = useState(false);
  const [editing, setEditing]       = useState<DAGDefinition | null>(null);
  const [expandedId, setExpanded]   = useState<number | null>(null);
  const [executions, setExecs]      = useState<Record<number, DAGExecution[]>>({});
  const [loadingExec, setLdExec]    = useState<Record<number, boolean>>({});
  const [firing, setFiring]         = useState<Record<number, boolean>>({});
  const [toast, setToast]           = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const [copyingDef, setCopyingDef] = useState<DAGDefinition | null>(null);
  const [importDraft, setImportDraft] = useState<DAGDefinition | null>(null);
  const importFileRef = useRef<HTMLInputElement>(null);

  const flash = (msg: string, type: 'success' | 'error') => {
    setToast({ msg, type }); setTimeout(() => setToast(null), 3000);
  };

  // ── Import / export ───────────────────────────────────────────────────
  // Export: portable JSON — the workflow's structure only. Org scoping and
  // DB ids are stripped; secrets are exported as REFERENCES (variable name →
  // Secret Manager id), never values.
  const handleExport = (def: DAGDefinition) => {
    const payload = {
      format: 'catax-workflow',
      version: 1,
      exported_at: new Date().toISOString(),
      workflow: {
        name: def.name,
        description: def.description ?? null,
        trigger: def.trigger,
        nodes: def.nodes ?? [],
        edges: def.edges ?? [],
        secrets: def.secrets ?? [],
      },
    };
    const slug = (def.name || 'workflow').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${slug || 'workflow'}.workflow.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Import: parse + validate the file, then open the builder prefilled so the
  // user reviews (name, secrets, trigger) before anything is saved.
  const handleImportFile = async (file: File) => {
    try {
      const parsed = JSON.parse(await file.text());
      // Accept both the export envelope and a raw definition JSON.
      const wf = parsed?.format === 'catax-workflow' ? parsed.workflow : parsed;
      if (!wf || typeof wf !== 'object') throw new Error('not a workflow file');
      if (!wf.trigger?.type) throw new Error('missing trigger');
      if (!Array.isArray(wf.nodes) || wf.nodes.length === 0) throw new Error('missing nodes');
      const bad = wf.nodes.find((n: any) => !n?.id || !n?.type);
      if (bad) throw new Error('a node is missing id/type');
      setImportDraft({
        id: 0,
        name: wf.name || 'Imported workflow',
        description: wf.description ?? '',
        is_active: true,
        trigger: wf.trigger,
        nodes: wf.nodes,
        edges: Array.isArray(wf.edges) ? wf.edges : [],
        secrets: Array.isArray(wf.secrets) ? wf.secrets : [],
      } as unknown as DAGDefinition);
      setEditing(null);
      setShowForm(false);
    } catch (e) {
      flash(`Import failed: ${(e as Error).message}`, 'error');
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, e] = await Promise.all([workflowService.listDAGDefinitions(orgId, isShared), workflowService.getEventTypes()]);
      setDefs(d.data); setEventTypes(e.data);
    } catch { flash('Failed to load', 'error'); }
    finally { setLoading(false); }
  }, [orgId, isShared]);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (def: DAGDefinition) => {
    try { const r = await workflowService.updateDAGDefinition(def.id, { is_active: !def.is_active }, orgId, isShared); setDefs(p => p.map(d => d.id === def.id ? r.data : d)); }
    catch { flash('Failed', 'error'); }
  };
  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this workflow?')) return;
    try { await workflowService.deleteDAGDefinition(id, orgId, isShared); setDefs(p => p.filter(d => d.id !== id)); flash('Deleted', 'success'); }
    catch { flash('Failed', 'error'); }
  };
  const handleSave = async (data: any) => {
    if (editing) {
      const r = await workflowService.updateDAGDefinition(editing.id, data, orgId, isShared);
      setDefs(p => p.map(d => d.id === editing.id ? r.data : d));
      flash('Updated', 'success');
    } else {
      // New workflows start switched off so they can't fire before they're
      // reviewed. (The API default is unchanged for other callers.)
      const r = await workflowService.createDAGDefinition({ ...data, is_active: false }, orgId, isShared);
      setDefs(p => [r.data, ...p]);
      flash('Created (switched off). Turn it on when it\'s ready.', 'success');
    }
    setShowForm(false); setEditing(null); setImportDraft(null);
  };
  const handleExpand = async (id: number) => {
    if (expandedId === id) { setExpanded(null); return; }
    setExpanded(id);
    setLdExec(p => ({ ...p, [id]: true }));
    try { const r = await workflowService.getDAGExecutions(id, 20, orgId, isShared); setExecs(p => ({ ...p, [id]: r.data })); }
    catch { setExecs(p => ({ ...p, [id]: [] })); }
    finally { setLdExec(p => ({ ...p, [id]: false })); }
  };
  const refreshExecutions = async (id: number) => {
    setLdExec(p => ({ ...p, [id]: true }));
    try { const r = await workflowService.getDAGExecutions(id, 20, orgId, isShared); setExecs(p => ({ ...p, [id]: r.data })); }
    catch { setExecs(p => ({ ...p, [id]: [] })); }
    finally { setLdExec(p => ({ ...p, [id]: false })); }
  };
  const handleFire = async (id: number) => {
    setFiring(p => ({ ...p, [id]: true }));
    try {
      await workflowService.manuallyTriggerDAG(id, {}, orgId, isShared);
      flash('Workflow fired', 'success');
      setExpanded(id);
      const r = await workflowService.getDAGExecutions(id, 20, orgId, isShared);
      setExecs(p => ({ ...p, [id]: r.data }));
    } catch { flash('Failed to fire', 'error'); }
    finally { setFiring(p => ({ ...p, [id]: false })); }
  };

  const evtLabel = (type: string) => eventTypes.find(et => et.type === type)?.label ?? type.replace(/_/g, ' ');

  if (showForm || editing || importDraft) {
    return (
      <SimplifiedWorkflowForm
        initial={editing ?? importDraft}
        states={states}
        eventTypes={eventTypes}
        onSave={handleSave}
        onCancel={() => { setShowForm(false); setEditing(null); setImportDraft(null); }}
        orgId={orgId}
        isShared={isShared}
      />
    );
  }

  return (
    <div className="space-y-4">
      {toast && (
        <div className={`fixed top-4 right-4 z-[100] flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium
          ${toast.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
          {toast.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {toast.msg}
        </div>
      )}

      {copyingDef && (
        <CopyToOrgModal
          def={copyingDef}
          currentOrgId={orgId}
          onClose={() => setCopyingDef(null)}
          onCopied={msg => flash(msg, 'success')}
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Automations</h2>
          <p className="text-xs text-gray-400 mt-0.5">Event-driven workflows — run automatically when something happens.</p>
          {orgId && (
            <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-[11px] text-blue-700 font-medium">
              Scoped to organisation #{orgId}
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <button onClick={load} title="Refresh" className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          <input
            ref={importFileRef}
            type="file"
            accept=".json,application/json"
            className="hidden"
            onChange={e => {
              const f = e.target.files?.[0];
              if (f) handleImportFile(f);
              e.target.value = '';   // allow re-importing the same file
            }}
          />
          <button onClick={() => importFileRef.current?.click()}
            title="Import a workflow from a .workflow.json export"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">
            <Upload className="w-3.5 h-3.5" /> Import
          </button>
          <button onClick={() => { setEditing(null); setShowForm(true); }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800">
            <Plus className="w-3.5 h-3.5" /> New workflow
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : definitions.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-200">
          <GitMerge className="w-10 h-10 mx-auto mb-3 text-gray-200" />
          <p className="text-sm font-medium text-gray-500">No automations yet</p>
          <p className="text-xs text-gray-400 mt-0.5 mb-4">Workflows run automatically when events occur in your system.</p>
          <button onClick={() => setShowForm(true)} className="text-sm font-medium text-white bg-gray-900 px-4 py-2 rounded-lg hover:bg-gray-800">
            Create your first workflow
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {definitions.map(def => {
            const stepsCount = (def.nodes ?? []).length;
            const actionSummary = (def.nodes ?? []).slice(0, 3).map(n => {
              const actionDef = EXPLICIT_ACTIONS.find(a => a.id === n.config?._action_id || a.nodeType === n.type);
              return actionDef?.label ?? n.label ?? n.type;
            });
            // In org-specific mode, shared definitions (no owner org) are read-only
            const isSharedDef = !isShared && !!orgId && def.organization_id == null;

            return (
              <div key={def.id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-sm transition-shadow">
                <div className="flex items-start justify-between px-5 py-4 gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <button onClick={() => !isSharedDef && handleToggle(def)} disabled={isSharedDef} className={`flex-shrink-0 mt-0.5 ${isSharedDef ? 'opacity-40 cursor-not-allowed' : ''}`} title={isSharedDef ? 'Shared — read only' : def.is_active ? 'Deactivate' : 'Activate'}>
                      {def.is_active
                        ? <ToggleRight className="w-5 h-5 text-gray-900" />
                        : <ToggleLeft className="w-5 h-5 text-gray-400" />}
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900 text-sm">{def.name}</span>
                        {!def.is_active && <Badge label="inactive" color="gray" />}
                        {isSharedDef && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-[11px] text-amber-700 font-medium">
                            🌐 Shared — read only
                          </span>
                        )}
                        {def.description && <span className="text-xs text-gray-400 truncate">{def.description}</span>}
                      </div>
                      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap text-xs">
                        <Zap className="w-3 h-3 text-amber-500 flex-shrink-0" />
                        <span className="text-gray-500 font-medium">{evtLabel(def.trigger?.type)}</span>
                        {Object.entries(def.trigger?.filters ?? {}).map(([k, v]) => (
                          <span key={k} className="text-[10px] font-mono bg-amber-50 border border-amber-200 text-amber-700 rounded-full px-2 py-0.5">
                            {formatTriggerFilter(k, v)}
                          </span>
                        ))}
                      </div>
                      {actionSummary.length > 0 && (
                        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                          {actionSummary.map((a, i) => (
                            <React.Fragment key={i}>
                              {i > 0 && <ArrowRight className="w-2.5 h-2.5 text-gray-300 flex-shrink-0" />}
                              <span className="text-[10px] bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">{a}</span>
                            </React.Fragment>
                          ))}
                          {stepsCount > 3 && <span className="text-[10px] text-gray-400">+{stepsCount - 3} more</span>}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => handleFire(def.id)} disabled={firing[def.id]}
                      title="Test run" className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg disabled:opacity-50 transition-colors">
                      {firing[def.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                    </button>
                    <button onClick={() => handleExport(def)}
                      title="Export as JSON"
                      className="p-1.5 text-gray-400 hover:text-sky-600 hover:bg-sky-50 rounded-lg transition-colors">
                      <Download className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleExpand(def.id)}
                      className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
                      <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${expandedId === def.id ? 'rotate-180' : ''}`} />
                    </button>
                    {!isSharedDef && (
                      <>
                        <button onClick={() => { setEditing(def); setShowForm(false); }}
                          className="p-1.5 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors">
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button onClick={() => setCopyingDef(def)}
                          title="Copy to another organisation"
                          className="p-1.5 text-gray-400 hover:text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors">
                          <Copy className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(def.id)}
                          className="p-1.5 text-red-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {expandedId === def.id && (
                  <div className="border-t border-gray-100 bg-gray-50 px-5 py-4">
                    <ExecutionPanel
                      executions={executions[def.id] ?? []}
                      loading={!!loadingExec[def.id]}
                      onRefresh={() => refreshExecutions(def.id)}
                      onClose={() => setExpanded(null)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

type Tab = 'automations' | 'state-machine';

const WorkflowsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('automations');
  const [stateConfig, setStateConfig] = useState<StateConfigResponse | null>(null);
  const [loadingCfg, setLoadingCfg] = useState(true);

  useEffect(() => {
    workflowService.getStateConfig()
      .then(r => setStateConfig(r.data))
      .catch(() => {})
      .finally(() => setLoadingCfg(false));
  }, []);

  return (
    <AppLayout>
      <div className="flex items-center justify-between flex-shrink-0 h-14 px-6 bg-white border-b border-gray-200">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(-1)}
            className="p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <GitMerge className="w-4 h-4 text-gray-500" />
          <h1 className="text-lg font-semibold text-gray-900">{t('workflows.title', 'Workflows')}</h1>
        </div>
        <div className="flex gap-0.5 p-0.5 bg-gray-100 rounded-lg">
          {([
            { id: 'automations',   label: t('workflows.tabs.automations', 'Automations'),   icon: Zap },
            { id: 'state-machine', label: t('workflows.tabs.stateMachine', 'State Machine'), icon: Settings },
          ] as { id: Tab; label: string; icon: React.ElementType }[]).map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors
                ${activeTab === tab.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      <div className="w-full px-4 sm:px-6 py-4 space-y-4 bg-main-bg flex-1 overflow-y-auto">
        {loadingCfg ? (
          <div className="flex justify-center py-24"><Loader2 className="w-5 h-5 animate-spin mr-2 text-gray-400" /></div>
        ) : (
          <>
            {activeTab === 'automations' && stateConfig && <AutomationsTab states={stateConfig.states} />}            {activeTab === 'state-machine' && stateConfig && <StateMachineTab config={stateConfig} onConfigChange={setStateConfig} />}
          </>
        )}
      </div>
    </AppLayout>
  );
};

export { AutomationsTab, StateMachineTab };
export default WorkflowsPage;
